/**
 * FairPlayPlayers.jsx · the FairPlay (Video.js) players, moved as is (punchlist Stage 1).
 *
 * Each player below was moved word for word from its page file, together with every helper it uses
 * and every comment. Nothing is combined: each player keeps its own copies. Names that would clash
 * inside this one file carry a Desktop, Tablet or Phone suffix; nothing else changed.
 *
 *   Desktop ← VideoViewPageFairPlay.jsx (VideoPlayer)
 *   Tablet  ← VideoViewPageFairPlayTablet.jsx (MobilePlayer)
 *   Phone   ← VideoViewPageFairPlayMobile.jsx (MobilePlayer)
 *
 * The chooser at the bottom picks the player for the device.
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import EndOfLessonCard from './EndOfLessonCard';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-contrib-eme';
import { useAuth } from './AuthContext';
import { useHeartbeat } from './useHeartbeat';
import { isLocked, isLightTheme, tierRank } from './config';
// Link refresh, step 2: the same token arithmetic as the page's hook, so the player and the page agree.
import { cdnTokenSecondsLeft, videoNeedSeconds, CDN_TOKEN_MIN_SECONDS } from './useCDNToken';
import { LinkOverlay } from './LinkOverlay';

// "Tap for sound" shows big and centred for this long after a lesson has to start muted, then shrinks to the
// small corner pill until the sound is back on. Change the number here.
const SOUND_PROMPT_BIG_MS = 4000;

// ── AIRPLAY SWITCH ───────────────────────────────────────────────────────────
// OFF for now: AirPlay of our FairPlay lessons fails on the TV's own key request
// (it surfaces as "You don't have access to this video"), so it is hidden on
// every Apple device until that is fixed. false = our AirPlay button is gone AND
// each <video> tells Safari not to offer AirPlay for it (Apple's own full-screen
// player on iPhone, Control Centre). Set to true to bring it all back.
const AIRPLAY_ENABLED = false;
const applyAirplayPolicy = (videoEl) => {
  if (AIRPLAY_ENABLED || !videoEl) return;
  videoEl.setAttribute('x-webkit-airplay', 'deny');      // WebKit: no AirPlay for this element
  videoEl.setAttribute('disableremoteplayback', '');     // the standard equivalent
  try { videoEl.disableRemotePlayback = true; } catch (e) { /* older Safari: the attributes above are enough */ }
};

// ── LINK REFRESH, STEP 2 (Apple) ─────────────────────────────────────────────
// Safari's player only takes a new CDN link by reloading its source. When the link this player loaded has run
// short (less than a play from the current spot needs) and the page holds a fresher one (useCDNToken.js), the
// player reloads its source at the same spot IN THE BACKGROUND, while paused. This overlay shows "Loading…"
// until the new source has its metadata, then "Continue from 12:34"; that tap calls play() inside the gesture,
// so the video starts with sound. The overlay never asks for a fresh link: that is the page's job.
// A lesson that was never played reloads quietly with no overlay (the play button stays).
// ════════════════ Desktop player · moved as is from VideoViewPageFairPlay.jsx (VideoPlayer) ════════════════

const PLAYBACK_SPEEDSDesktop = [0.5, 0.75, 1, 1.25, 1.5, 2];

// ============================================================================
// FAIRPLAY VIA NATIVE HLS  (Video.js + videojs-contrib-eme)
// ============================================================================
// FairPlay on Safari runs through NATIVE HLS (src=), where Safari's AVFoundation
// demuxes the separate audio + video tracks and fires a webkitneedkey PER TRACK.
// videojs-contrib-eme's legacy path (initLegacyFairplay) creates a key session
// per event and calls getLicense per track — which is exactly what Shaka's single
// MSE session could not do (shaka-player#9268: "the same assets play correctly in
// Video.js and other HTML5 players"). That per-track keying is the whole fix.
//
// THE ONE TRAP: firstWebkitneedkeyTimeout (default 1000ms) debounces the first
// webkitneedkey to swallow Safari's startup rendition-switch duplicates — but it
// is track-blind and can collapse the audio+video events into one, re-creating the
// silent-audio bug. We set it to 1 (NOT 0 — see the note at the src() call) so
// BOTH tracks get keyed.

// SPC (Uint8Array/ArrayBuffer) -> standard base64 for the DRMtoday license POST.
function toBase64Desktop(input) {
  const bytes = input instanceof Uint8Array ? input
    : input instanceof ArrayBuffer ? new Uint8Array(input)
    : new Uint8Array(input && input.buffer ? input.buffer : input);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary);
}

// base64 CKC -> ArrayBuffer for the CDM.
function fromBase64Desktop(b64) {
  const binary = window.atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

// Replacement for shaka.util.StringUtils.fromBytesAutoDetect (utf-8, then raw).
function fromBytesAutoDetectDesktop(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  try { return new TextDecoder('utf-8').decode(u8); }
  catch (e) { let s = ''; for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]); return s; }
}

// Deterministic per-lesson gradient. The API no longer stores gradient colors;
// instead we derive a stable pair from the videoId so every lesson keeps a
// consistent look across sessions without any stored data. Hues are spread
// across the wheel; saturation/lightness kept muted so it sits behind the
// player without clashing with the gold/cream theme. Returns [from, to] hex.
function gradientFor(id) {
  const s = String(id || '');
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0; // djb2
  const hue1 = h % 360;
  const hue2 = (hue1 + 40) % 360;               // nearby hue for a smooth blend
  const hsl = (deg, sat, light) => {
    // minimal HSL→hex (no deps)
    const a = sat * Math.min(light, 1 - light);
    const f = (n) => {
      const k = (n + deg / 30) % 12;
      const c = light - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
      return Math.round(255 * c).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };
  return [hsl(hue1, 0.45, 0.45), hsl(hue2, 0.5, 0.35)];
}

// ============================================================================
// TIER-SPECIFIC STYLE HELPERS
// ============================================================================
const getSkipButtonStylesDesktop = (currentTier, isHovered, isMobile) => {
  const base = { position:'relative', borderRadius:isMobile?'16px':'20px', cursor:'pointer', display:'flex', alignItems:'center', gap:isMobile?'5px':'8px', padding:isMobile?'7px 12px':'12px 20px', transition:'all 0.4s cubic-bezier(0.4,0,0.2,1)', overflow:'hidden', border:'none', fontFamily:'"DM Sans",sans-serif', WebkitTapHighlightColor:'transparent' };
  if (tierRank(currentTier) === 0) return { ...base, background:isHovered?'linear-gradient(135deg,rgba(26,26,26,0.5),rgba(42,42,42,0.45))':'linear-gradient(135deg,rgba(26,26,26,0.4),rgba(42,42,42,0.35))', border:`1px solid ${isHovered?'rgba(255,255,255,0.3)':'rgba(255,255,255,0.2)'}`, boxShadow:isHovered?'0 4px 16px rgba(0,0,0,0.4),inset 0 1px 0 rgba(255,255,255,0.2)':'0 2px 8px rgba(0,0,0,0.3),inset 0 1px 0 rgba(255,255,255,0.15)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
  if (tierRank(currentTier) === 1) return { ...base, background:isHovered?'linear-gradient(135deg,rgba(201,169,98,0.35),rgba(201,169,98,0.25))':'linear-gradient(135deg,rgba(201,169,98,0.25),rgba(201,169,98,0.15))', border:`1px solid ${isHovered?'rgba(201,169,98,0.6)':'rgba(201,169,98,0.4)'}`, boxShadow:isHovered?'0 6px 24px rgba(0,0,0,0.4),0 0 36px rgba(201,169,98,0.4),inset 0 1px 0 rgba(201,169,98,0.5)':'0 3px 12px rgba(0,0,0,0.3),0 0 20px rgba(201,169,98,0.18),inset 0 1px 0 rgba(201,169,98,0.3)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
  return { ...base, background:isHovered?'linear-gradient(135deg,rgba(232,213,163,0.4),rgba(232,213,163,0.3))':'linear-gradient(135deg,rgba(232,213,163,0.3),rgba(232,213,163,0.2))', border:`1px solid ${isHovered?'rgba(232,213,163,0.7)':'rgba(232,213,163,0.5)'}`, boxShadow:isHovered?'0 8px 32px rgba(0,0,0,0.5),0 0 44px rgba(232,213,163,0.45),inset 0 1px 0 rgba(232,213,163,0.6)':'0 4px 16px rgba(0,0,0,0.4),0 0 24px rgba(232,213,163,0.24),inset 0 1px 0 rgba(232,213,163,0.4)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
};
const getSkipIconColorDesktop = (t,h) => tierRank(t)===0?'#FFF':tierRank(t)===1?(h?'#E8D5A3':'#C9A962'):(h?'#FFF8E7':'#E8D5A3');
const getSkipNumberColorDesktop = (t) => tierRank(t)===0?'#FFF':tierRank(t)===1?'#C9A962':'#E8D5A3';
const getProgressStylesDesktop = (t) => {
  if (tierRank(t)===0) return { played:{background:'#8B8B7A',boxShadow:'0 0 8px rgba(139,139,122,0.4)'}, thumb:{background:'#8B8B7A',boxShadow:'0 0 8px rgba(139,139,122,0.5)'} };
  if (tierRank(t)===1) return { played:{background:'linear-gradient(90deg,#C9A962,#E8D5A3)',boxShadow:'0 0 12px rgba(201,169,98,0.5)'}, thumb:{background:'linear-gradient(135deg,#E8D5A3,#C9A962)',boxShadow:'0 0 12px rgba(201,169,98,0.6)'} };
  return { played:{background:'linear-gradient(90deg,#E8D5A3,#FFF8E7)',boxShadow:'0 0 16px rgba(232,213,163,0.6)'}, thumb:{background:'linear-gradient(135deg,#FFF8E7,#E8D5A3)',boxShadow:'0 0 16px rgba(232,213,163,0.7)'} };
};
const getVolumeStyles = (t) => {
  if (tierRank(t)===0) return {background:'#8B8B7A'};
  if (tierRank(t)===1) return {background:'linear-gradient(90deg,#C9A962,#E8D5A3)',boxShadow:'0 0 8px rgba(201,169,98,0.4)'};
  return {background:'linear-gradient(90deg,#E8D5A3,#FFF8E7)',boxShadow:'0 0 10px rgba(232,213,163,0.5)'};
};

// ============================================================================
// SKIP BUTTON - Pill-shaped with tier styling
// ============================================================================
function SkipButtonDesktop({ direction, onClick, currentTier, isMobile, seconds=15 }) {
  const [isHovered, setIsHovered] = useState(false);
  const styles = getSkipButtonStylesDesktop(currentTier, isHovered, isMobile);
  const iconColor = getSkipIconColorDesktop(currentTier, isHovered);
  const numberColor = getSkipNumberColorDesktop(currentTier);
  const iconSize = isMobile?13:18, numberSize = isMobile?12:16, unitSize = isMobile?9:12;
  const iconFilter = tierRank(currentTier)===0?'drop-shadow(0 1px 3px rgba(0,0,0,0.5))':tierRank(currentTier)===1?(isHovered?'drop-shadow(0 0 8px rgba(201,169,98,0.5))':'drop-shadow(0 0 4px rgba(201,169,98,0.3))'):(isHovered?'drop-shadow(0 0 12px rgba(232,213,163,0.6))':'drop-shadow(0 0 6px rgba(232,213,163,0.4))');
  return (
    <button onClick={onClick} onMouseEnter={()=>setIsHovered(true)} onMouseLeave={()=>setIsHovered(false)} style={styles}>
      {direction==='back' && <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{filter:iconFilter,transition:'all 0.3s ease'}}><path d="M19 12H5M12 5l-7 7 7 7"/></svg>}
      <span style={{display:'flex',alignItems:'baseline',gap:'2px',color:'#F5F5F0',textShadow:isLightTheme(currentTier)?'0 1px 3px rgba(0,0,0,0.5)':'0 1px 2px rgba(0,0,0,0.3)'}}>
        <span style={{fontSize:`${numberSize}px`,fontWeight:'700',color:numberColor,letterSpacing:'-0.02em'}}>{seconds}</span>
        <span style={{fontSize:`${unitSize}px`,fontWeight:'500',opacity:0.8}}>s</span>
      </span>
      {direction==='forward' && <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{filter:iconFilter,transition:'all 0.3s ease'}}><path d="M5 12h14M12 5l7 7-7 7"/></svg>}
    </button>
  );
}

// ============================================================================
// VIDEO PLAYER COMPONENT
// ============================================================================
// ┌───────────────────────────────────────────────────────────────────────────┐
// │ TEST HOOK — pin playback to ONE rung of the ladder.                       │
// │   ?forceVariant=v0   on the PAGE url → play v0/video.m3u8 directly        │
// │   ?forceVariant=v3                   → play v3/video.m3u8 directly        │
// │   (absent)                           → normal, master.m3u8               │
// └───────────────────────────────────────────────────────────────────────────┘
// In native HLS Safari picks the variant, so pinning a rung means handing the
// player a MEDIA playlist instead of the MASTER. The CDN token is prefix-scoped
// to videos/<slug>, so it already authorizes v0/video.m3u8 and its segments.
// ── MUXED FAIRPLAY PACKAGE ───────────────────────────────────────────────────
// Safari gets the single-track SAMPLE-AES TS package instead of the demuxed cbcs
// fMP4 one:
//     videos/<slug>/main-video/master.m3u8         (Widevine — Chrome keeps this)
//     videos/<slug>/main-video-single/prog_index.m3u8   (FairPlay — muxed)
//
// Same lesson prefix, so the SAME ?t= token authorizes it: no API, DB or Worker
// change. The Worker is already path-agnostic (.ts → video/mp2t; the EXT-X-MEDIA
// DEFAULT=YES fix only matches TYPE=AUDIO lines, which a muxed package has none of).
//
// Chrome is unaffected — this file is only loaded when player === 'videojs'.
//
// GATING: 'all' once the whole catalogue is packaged; 'list' while rolling out
// (only slugs in MUXED_SLUGS are swapped); 'off' to fall back to the demuxed
// cbcs fMP4 package everywhere. An unpackaged lesson under 'all' will 404 on the
// manifest request — check the network tab for the rewritten URL.
const MUXED_MODEDesktop = 'all';          // 'all' | 'list' | 'off'
const MUXED_SLUGSDesktop = [];            // used only when MUXED_MODE === 'list'

// The package layout written by mux-fairplay-all.sh:
//     videos/<slug>/main-video-fairplay/master.m3u8      <- multi-rung
//     videos/<slug>/main-video-fairplay/v0/prog_index.m3u8
// If the multi-rung master ever misbehaves, set MUXED_INDEX to
// 'v0/prog_index.m3u8' to pin the single proven rendition — no repackaging.
const MUXED_SUBDIRDesktop = 'main-video-fairplay';
const MUXED_INDEXDesktop = 'master.m3u8';

function toMuxedPackageDesktop(url) {
  if (!url || MUXED_MODEDesktop === 'off') return url;
  const m = String(url).match(/\/videos\/([^/]+)\/main-video\/master\.m3u8/);
  if (!m) return url;                                     // answer-key etc. untouched
  if (MUXED_MODEDesktop === 'list' && MUXED_SLUGSDesktop.indexOf(m[1]) < 0) return url;
  const out = String(url).replace('/main-video/master.m3u8',
                                  '/' + MUXED_SUBDIRDesktop + '/' + MUXED_INDEXDesktop);
  return out;
}

function applyVariantOverride(url) {
  if (!url) return url;
  // ── ?testSrc= — load an ARBITRARY manifest, bypassing the CDN entirely. ──────
  // Diagnostic only. Purpose: prove whether this player can drive a multi-variant
  // master playlist WITH a separate audio rendition group at all, using a known-good
  // UNENCRYPTED stream. If a reference stream also freezes, the fault is in the
  // player/contrib-eme setup, not in DRM or our packaging — and vice versa.
  //   ?testSrc=apple   -> Apple's public bipbop advanced fMP4 example (multi-variant + audio group)
  //   ?testSrc=<url>   -> any https .m3u8 you paste (URL-encode it)
  const TEST_SRCS = {
    apple: 'https://devstreaming-cdn.apple.com/videos/streaming/examples/img_bipbop_adv_example_fmp4/master.m3u8',
  };
  try {
    const t = new URLSearchParams(window.location.search).get('testSrc');
    if (t) {
      const chosen = TEST_SRCS[t] || (/^https:\/\/[^\s]+\.m3u8(\?|$)/i.test(t) ? t : null);
      if (chosen) {
        return chosen;
      }
    }
  } catch (e) { /* no search string — ignore */ }

  let variant = null;
  try { variant = new URLSearchParams(window.location.search).get('forceVariant'); } catch { return url; }
  // Two forms:
  //   ?forceVariant=v0           -> <base>/v0/video.m3u8   (bare video variant, no audio)
  //   ?forceVariant=audio/audio  -> <base>/audio/audio.m3u8 (any child playlist path)
  // The second form exists to pin the AUDIO rendition alone, which the original
  // video-only form could not reach (the filename was hardcoded to video.m3u8).
  if (!variant || !/^[a-z0-9_\-/]+$/i.test(variant) || variant.includes('..')) return url;
  const child = variant.includes('/') ? variant + '.m3u8' : variant + '/video.m3u8';
  const out = url.replace(/\/master\.m3u8(\?|$)/, '/' + child + '$1');
  return out;
}

// ── skd content-id extraction ────────────────────────────────────────────────
// videojs-contrib-eme hands getContentId the initData as a String, but it can
// arrive either clean ("skd://drmtoday?...") or in the legacy length-prefixed
// UTF-16LE shape. Handle both and always return the skd URI.
function parseSkdInitDataDesktop(initData) {
  if (typeof initData === 'string') {
    const idx = initData.indexOf('skd:');
    if (idx >= 0) return { uri: initData.slice(idx).replace(/\u0000/g, ''), shape: 'string' };
    return { uri: initData, shape: 'string_raw' };
  }
  const u8 = initData instanceof Uint8Array ? initData : new Uint8Array(initData);
  if (u8.byteLength >= 6) {
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    const byteLen = dv.getUint32(0, true);
    if (byteLen + 4 === u8.byteLength && byteLen % 2 === 0) {
      const uri = new TextDecoder('utf-16le').decode(new Uint8Array(u8.buffer, u8.byteOffset + 4, byteLen));
      if (uri.startsWith('skd:')) return { uri, shape: 'legacy_len_utf16le' };
    }
  }
  return { uri: fromBytesAutoDetectDesktop(u8), shape: 'plain' };
}

function VideoPlayer({ upNext = null, upNextLocked = false, onUpNext, onUnlock, endCard = null, video, colors, currentTier, isPlaying, setIsPlaying, currentTime, setCurrentTime, videoUrl: rawVideoUrl, videoId, assetId, resumeFrom = 0, isMobile, isTablet, refreshLink }) {
  // Muxed package swap first, then ?forceVariant (which is a no-op without the param).
  // Order matters: forceVariant rewrites /master.m3u8, which the muxed URL no longer has,
  // so on a muxed lesson forceVariant is inert — pin variants on the fMP4 package instead.
  const videoUrl = useMemo(() => applyVariantOverride(toMuxedPackageDesktop(rawVideoUrl)), [rawVideoUrl]);

  const containerRef = useRef(null);   // .video-player-container (fullscreen target)
  const mediaHostRef = useRef(null);   // div Video.js mounts into (React owns this, NOT the <video>)
  const mediaRef = useRef(null);       // the raw <video> element (for webkitEnterFullscreen etc.)
  const vjsRef = useRef(null);         // the Video.js player instance
  const progressRef = useRef(null);
  const volumeRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const { token, profile, api } = useAuth();
  // Live JWT for the license POST — read from a ref so a refresh never rebuilds the player.
  const authTokenRef = useRef(token);
  authTokenRef.current = token;

  const API_CONTENT_BASE = process.env.REACT_APP_CONTENT_API_URL || process.env.REACT_APP_API_URL || '';
  const DRMTODAY_MERCHANT = process.env.REACT_APP_DRMTODAY_MERCHANT || 'f100090a-e116-42ba-b03a-1d157c6be3f3';

  const latestRef = useRef({ pos: 0, dur: 0 });
  const resumedRef = useRef(false);
  const resumeFromRef = useRef(resumeFrom);
  // Link refresh, step 2: the link this player's source was last set from, how to build a source for a link
  // (set by the load effect, which owns the FairPlay callbacks), the latest link prop for the player's own
  // event handlers, the reload function for the error handler, and the overlay state.
  const loadedUrlRef = useRef(null);
  const sourceForRef = useRef(null);
  const videoUrlRef = useRef(null);
  videoUrlRef.current = videoUrl;
  const rebuildFromFreshLinkRef = useRef(null);
  const [linkOverlay, setLinkOverlay] = useState(null);   // null | 'loading' | 'resuming' | 'ready' ('resuming' = the Resume tap's own reload)
  const resumingRef = useRef(false);   // the Resume tap's reload is in progress: its own play() must not close the overlay

  // Heartbeats (the 10-second send while playing, and the final send on unmount) live in useHeartbeat.
  const heartbeatOnTime = useHeartbeat(videoId);

  const [showControls, setShowControls] = useState(true);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Saved speed (1.22): start from the profile's preferredPlaybackSpeed when it is one of our speeds, else 1×.
  const [playbackSpeed, setPlaybackSpeed] = useState(() => { const s = Number(profile && profile.preferredPlaybackSpeed); return PLAYBACK_SPEEDSDesktop.includes(s) ? s : 1; });
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isLoading, setIsLoading] = useState(!!videoUrl);
  const [error, setError] = useState(null);

  // Debounced overlay — a transient error during DRM startup shouldn't flash the overlay.
  const [showError, setShowError] = useState(false);
  useEffect(() => {
    if (!error) { setShowError(false); return; }
    const t = setTimeout(() => setShowError(true), 600);
    return () => clearTimeout(t);
  }, [error]);

  const isHls = videoUrl && /\.m3u8($|\?)/.test(videoUrl);
  // Path-keyed so a token re-mint of the same video never tears down a live player.
  const videoUrlKey = videoUrl ? String(videoUrl).split('?')[0] : null;

  const totalSeconds = duration || (34 * 60 + 12);
  // Desktop timeline (1.26): hover shows the time under the pointer; press and drag moves the thumb
  // and seeks once, on release. A plain click still goes through handleProgressClick.
  const [hoverTime, setHoverTime] = useState(null);
  const [dragTime, setDragTime] = useState(null);
  const dragRef = useRef({ active: false, moved: false, x: 0, justEnded: false });
  // While dragging the timeline (1.26), the played bar and thumb follow the pointer.
  const progressPercent = ((dragTime ?? currentTime) / totalSeconds) * 100;
  const bufferedPercent = duration > 0 ? (buffered / duration) * 100 : 60;
  const skipAmount = isMobile ? 10 : 15;
  const pStyles = getProgressStylesDesktop(currentTier);
  const vStyles = getVolumeStyles(currentTier);

  // ── Video.js + FairPlay (native HLS) setup ────────────────────────────────
  useEffect(() => {
    if (!videoUrl || !mediaHostRef.current) return;
    let cancelled = false;

    // Create the media element IMPERATIVELY and append it, so Video.js can own and
    // dispose it without fighting React's reconciler (React only owns the host div).
    const videoEl = document.createElement('video');
    videoEl.className = 'video-js vjs-tech';
    videoEl.setAttribute('playsinline', '');
    applyAirplayPolicy(videoEl);
    videoEl.playsInline = true;
    videoEl.preload = 'metadata';
    videoEl.style.width = '100%';
    videoEl.style.height = '100%';
    videoEl.style.objectFit = 'contain';
    mediaHostRef.current.appendChild(videoEl);
    mediaRef.current = videoEl;

    const player = videojs(videoEl, {
      controls: false, autoplay: false, preload: 'metadata',
      playsinline: true, fill: true, fluid: false,
      bigPlayButton: false, loadingSpinner: false, errorDisplay: false, controlBar: false,
      // overrideNative:false → Safari plays HLS natively → webkitneedkey per track (the FairPlay path).
      html5: { vhs: { overrideNative: false }, nativeAudioTracks: true, nativeVideoTracks: true },
    });
    vjsRef.current = player;

    try {
      const rootEl = player.el();
      rootEl.style.position = 'absolute'; rootEl.style.inset = '0';
      rootEl.style.width = '100%'; rootEl.style.height = '100%';
    } catch (e) { /* non-fatal */ }

    // ── player state wiring (replaces the old <video> JSX handlers) ──────────
    player.on('loadedmetadata', () => {
      if (cancelled) return;
      const d = player.duration() || 0;
      setDuration(d); setIsLoading(false); setError(null);
      if (!resumedRef.current) {
        const r = resumeFromRef.current;
        if (r > 0 && r < d) { player.currentTime(r); setCurrentTime(r); }
        resumedRef.current = true;
      }
    });
    player.on('timeupdate', () => {
      const t = player.currentTime();
      setCurrentTime(t);
      const d = player.duration() || 0;
      latestRef.current = { pos: t, dur: d };
      heartbeatOnTime(t, d, player.playbackRate(), !player.paused() && !player.seeking());
    });
    player.on('seeking', () => heartbeatOnTime.seeking());
    player.on('progress', () => { try { setBuffered(player.bufferedEnd()); } catch (e) {} });
    player.on('waiting', () => setIsLoading(true));
    player.on('canplay', () => { setIsLoading(false); setError(null); });
    player.on('playing', () => { setIsLoading(false); setError(null); });
    player.on('ended', () => setIsPlaying(false));
    // play/pause events only REPORT what the video is doing: they copy its real state, never the event's name.
    // A late event (a 'pause' that arrives while the video plays, or a 'play' while it is paused) then changes
    // nothing, so it can't make the play effect send the opposite command (the play/pause loop, October 11).
    player.on('play', () => setIsPlaying(!player.paused()));
    player.on('pause', () => setIsPlaying(!player.paused()));
    player.on('error', () => {
      const e = player.error();
      // MEDIA_ERR codes: 1 ABORTED 2 NETWORK 3 DECODE 4 SRC_NOT_SUPPORTED 5 ENCRYPTED
      const code = e && e.code;
      // Link refresh: the stream died (e.g. its link ran out mid-play) and a fresher link has arrived: reload
      // from it at the spot, with the overlay, instead of failing. A second failure on the fresh link falls through.
      if (code && rebuildFromFreshLinkRef.current && rebuildFromFreshLinkRef.current(true)) return;
      if (code === 4 || code === 5) setError('You don’t have access to this video');
      else if (code) {
        // The overlay, not the error screen, wherever there is a spot to come back to (October 30, 2026): the tap
        // reloads the source from a fresh session token and a fresh link.
        resumeFromRef.current = latestRef.current.pos || resumeFromRef.current || 0; setIsPlaying(false); setLinkOverlay('ready');   // the saved spot stays if nothing has played yet
      }
      setIsLoading(false);
    });

    // ── source + FairPlay key system ─────────────────────────────────────────
    if (!isHls) {
      // Non-DRM / non-HLS preview: plain source, no key system.
      player.src({ src: videoUrl });
      sourceForRef.current = (u) => ({ src: u });
    } else {
      // FairPlay certificate — same override hooks as the prior investigation:
      //   sessionStorage.fpsCertB64  or  ?certUrl=<url>   (no param = DRMtoday default)
      let certOverride = null;
      try {
        const b64 = window.sessionStorage.getItem('fpsCertB64');
        if (b64) { certOverride = 'data:application/octet-stream;base64,' + b64.trim(); }
        else {
          const u = new URLSearchParams(window.location.search).get('certUrl');
          if (u) { certOverride = u; }
        }
      } catch (e) { /* default */ }
      const DRMTODAY_FPS_CERT = certOverride
        || `https://lic.drmtoday.com/license-server-fairplay/cert/${DRMTODAY_MERCHANT}`;

      const licenseBase = (API_CONTENT_BASE || '').replace(/\/$/, '');
      const fairplayLicense = `${licenseBase}/license/fairplay?assetId=${encodeURIComponent(assetId || '')}`;

      const getCertificate = (emeOptions, callback) => {
        fetch(DRMTODAY_FPS_CERT, { method: 'GET' })
          .then((r) => { if (!r.ok) throw new Error('cert HTTP ' + r.status); return r.arrayBuffer(); })
          .then((buf) => { callback(null, new Uint8Array(buf)); })
          .catch((err) => { callback(err); });
      };

      // CONTENT-ID: strip ONLY variantId. DRMtoday returns LIC_KEYS_NOT_FOUND when
      // variantId is present (keys were ingested without it) — see the matching note
      // in the Shaka page.
      //
      // STALE AS OF 2026-07-22 — kept as history, do not act on it:
      //   "DO NOT also strip &trk=audio. The CDN Worker deliberately injects that tag
      //    on the audio rendition's #EXT-X-KEY so the audio track's content-id differs
      //    from video's."
      // The Worker REMOVED that injection (see rewriteManifest), on the grounds that
      // every playlist carries one identical key, so the tag invented a content-id the
      // packager never wrote. No manifest emits &trk= any more; the [^&]* strip below
      // is a no-op against it either way, so this code needs no change.
      //
      // What DOES follow from the removal: both tracks now request the SAME content-id
      // again — the exact condition the tag existed to avoid. If playback ever freezes
      // with a decoded first frame and currentTime pinned at 0, that is the audio track
      // never being keyed.
      //
      // The durable fix is per-track key URIs at packaging time; shaka-packager's
      // --hls_key_uri is a single global flag (#439 / #470), which is why neither the
      // player nor the Worker has been able to solve this cleanly.
      const getContentId = (emeOptions, initData) => {
        const { uri } = parseSkdInitDataDesktop(initData);
        const contentId = uri.replace(/[?&]variantId=[^&]*/i, '');
        return contentId;
      };

      const getLicense = (emeOptions, contentId, keyMessage, callback) => {
        const spcB64 = toBase64Desktop(keyMessage);
        const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
        const authToken = authTokenRef.current;
        if (authToken) headers['Authorization'] = 'Bearer ' + authToken;
        fetch(fairplayLicense, { method: 'POST', headers, body: 'spc=' + encodeURIComponent(spcB64) })
          .then(async (r) => {
            const text = await r.text();
            if (!r.ok) throw new Error('license HTTP ' + r.status);
            let txt = text.trim();
            const wrapped = txt.startsWith('<ckc>') && txt.endsWith('</ckc>');
            if (wrapped) txt = txt.slice(5, -6);
            callback(null, fromBase64Desktop(txt));
          })
          .catch((err) => { callback(err); });
      };

      player.eme();
      // ── THE REAL FIX: 1, NOT 0. ────────────────────────────────────────────
      // videojs-contrib-eme reads this as:
      //     getOptions(player).firstWebkitneedkeyTimeout || 1000
      // so a literal 0 is FALSY and silently becomes 1000ms. That 1s window is a
      // cancel-and-reschedule (last-event-wins) debounce: the VIDEO needkey gets
      // clearTimeout'd by the AUDIO needkey ~330ms later, so the video key is
      // DROPPED and only re-requested by Safari's own ~20s retry.
      // 1ms survives the `||`, fires effectively immediately, and flips
      // handledFirstEvent=true so every later needkey takes the immediate path.
      // DO NOT "clean this up" back to 0.
      try { player.eme.options = { ...(player.eme.options || {}), firstWebkitneedkeyTimeout: 1 }; } catch (e) {}
      if (player.eme.initLegacyFairplay) player.eme.initLegacyFairplay();

      player.src({
        src: videoUrl,
        type: 'application/x-mpegurl',
        firstWebkitneedkeyTimeout: 1,  // see note above — 1, never 0 (|| 1000 trap)
        keySystems: { 'com.apple.fps.1_0': { getCertificate, getContentId, getLicense } },
      });
      sourceForRef.current = (u) => ({ src: u, type: 'application/x-mpegurl', firstWebkitneedkeyTimeout: 1, keySystems: { 'com.apple.fps.1_0': { getCertificate, getContentId, getLicense } } });
    }
    loadedUrlRef.current = videoUrl;   // link refresh: the link this player loaded

    return () => {
      cancelled = true;
      const p = vjsRef.current;
      if (p && !p.isDisposed()) { try { p.dispose(); } catch (e) {} }
      vjsRef.current = null;
      mediaRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrlKey, isHls, assetId, API_CONTENT_BASE]);

  // -- YouTube-style controls --
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (isPlaying) hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying) { setShowControls(true); if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); }
    else hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    return () => { if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); };
  }, [isPlaying]);

  const handleVideoAreaClick = useCallback((e) => {
    if (e.target.closest('[data-controls]')) return;
    if (isMobile) { if (showControls) { setShowControls(false); if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); } else showControlsTemporarily(); }
    else { setIsPlaying(!isPlaying); showControlsTemporarily(); }
  }, [isMobile, isPlaying, showControls, showControlsTemporarily, setIsPlaying]);

  const handleMouseMove = useCallback(() => { if (!isMobile) showControlsTemporarily(); }, [isMobile, showControlsTemporarily]);
  const handleMouseLeave = useCallback(() => { if (!isMobile && isPlaying) { if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); hideTimeoutRef.current = setTimeout(() => setShowControls(false), 1000); } }, [isMobile, isPlaying]);

  // -- Play/pause (driven through the Video.js player) --
  // AbortError-aware: ONLY NotAllowedError triggers a muted retry, because only that
  // one means an autoplay block. An AbortError usually means play() was interrupted
  // mid-load (a late FairPlay key, a source swap) — retrying muted there compounds the
  // abort and costs the user their audio for no reason, so we leave it paused and let
  // the recovery guard below re-attempt once the media is genuinely playable.
  // ── Starting playback (fix: "stuck after the next lesson", "the next lesson is muted") ─────────────────
  // A refused autoplay and an interrupted play are different things:
  //   NotAllowedError · the browser refuses sound without a tap (always so on iOS after an automatic lesson change).
  //     Start muted, which every browser allows, but only temporarily: "Tap for sound" shows, and the first tap,
  //     click or key press anywhere restores the sound, inside that tap (what iOS requires).
  //   anything else (AbortError: interrupted, e.g. by the stream still loading or a late FairPlay key) · never mute;
  //     show paused at once, with the play button; the student taps again (October 11: the retries are removed).
  const [autoMuted, setAutoMuted] = useState(false);
  useEffect(() => {
    const player = vjsRef.current;
    if (!videoUrl || !player) return undefined;
    if (isPlaying) {
      let cancelled = false;
      const attempt = () => {
        if (cancelled || (typeof player.isDisposed === 'function' && player.isDisposed() === true)) return;
        const p = player.play();
        if (!p || !p.then) return;
        p.catch((err) => {
          if (cancelled) return;
          const name = err && err.name;
          if (name === 'NotAllowedError') {
            if (player.muted()) { setIsPlaying(false); return; }   // refused even muted: show paused
            player.muted(true); setIsMuted(true); setAutoMuted(true);
            const p2 = player.play();
            if (p2 && p2.then) p2.catch(() => { if (!cancelled) setIsPlaying(false); });
          } else {
            // AbortError (a late FairPlay key, a source swap): no retry; show paused, never mute.
            setIsPlaying(false);
          }
        });
      };
      attempt();
      return () => { cancelled = true; };
    }
    player.pause();
    return undefined;
  }, [isPlaying, videoUrl, setIsPlaying]);
  useEffect(() => {
    if (!autoMuted) return undefined;
    // a tap on the player itself only restores the sound (it must not also pause or play); elsewhere it carries on.
    // M also means "restore the sound" here: the player's own M toggle would otherwise flip it straight back to muted.
    const restore = (e) => { const p = vjsRef.current; if (p) p.muted(false); setIsMuted(false); setAutoMuted(false); if (e && e.type !== 'keydown' && containerRef.current && e.target && containerRef.current.contains(e.target)) { e.stopPropagation(); e.preventDefault(); } if (e && e.type === 'keydown' && (e.key === 'm' || e.key === 'M')) { e.stopImmediatePropagation(); e.preventDefault(); } };
    const o = { capture: true };
    window.addEventListener('touchend', restore, o); window.addEventListener('click', restore, o); window.addEventListener('keydown', restore, o);
    return () => { window.removeEventListener('touchend', restore, o); window.removeEventListener('click', restore, o); window.removeEventListener('keydown', restore, o); };
  }, [autoMuted]);
  useEffect(() => { if (!isMuted) setAutoMuted(false); }, [isMuted]);
  // "Tap for sound": big and centred for SOUND_PROMPT_BIG_MS, then the small corner pill until the sound is on.
  // Display only: a tap anywhere restores the sound either way (the effect above).
  const [soundPromptBig, setSoundPromptBig] = useState(false);
  useEffect(() => {
    if (!autoMuted) { setSoundPromptBig(false); return undefined; }
    setSoundPromptBig(true);
    const t = setTimeout(() => setSoundPromptBig(false), SOUND_PROMPT_BIG_MS);
    return () => clearTimeout(t);
  }, [autoMuted]);

  // -- Recovery guard: re-attempt play once the media actually becomes playable. --
  // If a FairPlay key lands late, autoplay's play() can reject with AbortError while
  // the pipeline has no video key; without this the player then sits frozen at 0
  // forever even though decryption later succeeds. One-shot per source, and it only
  // fires if the user still intends to be playing.
  const replayGuardRef = useRef(false);
  const isPlayingRef = useRef(false);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { replayGuardRef.current = false; }, [videoUrlKey]);
  useEffect(() => {
    const player = vjsRef.current;
    if (!videoUrl || !player) return;
    const onReady = () => {
      const v = mediaRef.current;
      if (replayGuardRef.current) return;
      if (!isPlayingRef.current) return;          // user paused on purpose — respect it
      if (!v || v.videoWidth > 0) return;         // already decoding; nothing to recover
      replayGuardRef.current = true;
      const p = player.play();
      if (p && p.catch) p.catch(() => {});
    };
    player.on('canplaythrough', onReady);
    return () => { try { player.off('canplaythrough', onReady); } catch (e) {} };
  }, [videoUrl, videoUrlKey]);

  useEffect(() => { const p = vjsRef.current; if (p) { p.muted(isMuted); p.volume(isMuted ? 0 : volume / 100); } }, [volume, isMuted]);
  // Media ready: counts each time the media reports its metadata (first load, a new lesson). The
  // speed and the subtitle tracks apply from this, because the Video.js player is created inside
  // the load effect and isLoading can stay false if the player mounts before its URL.
  const [mediaReady, setMediaReady] = useState(0);
  useEffect(() => {
    const p = vjsRef.current;
    if (!p) return undefined;
    const onReady = () => setMediaReady((n) => n + 1);
    p.on('loadedmetadata', onReady);
    return () => { try { p.off('loadedmetadata', onReady); } catch (err) { /* player already disposed */ } };
  }, [videoUrl]);

  // ── End of lesson (1.23) ──────────────────────────────────────────────────
  // The page passes upNext / upNextLocked / onUpNext / onUnlock to the LESSON player only, so the
  // answer-key player never shows the card. The video's end is detected by these new listeners;
  // the existing end handler is untouched.
  const endCardEnabled = typeof onUpNext === 'function' || typeof onUnlock === 'function';
  const [ended, setEnded] = useState(false);
  const [nativeFs, setNativeFs] = useState(false);     // iPhone's own full-screen player hides our card
  useEffect(() => {
    setEnded(false);
    const p = vjsRef.current;
    if (!p) return undefined;
    const onEnd = () => setEnded(true);
    const onAgain = () => setEnded(false);
    p.on('ended', onEnd); p.on('play', onAgain); p.on('seeking', onAgain);
    return () => { try { p.off('ended', onEnd); p.off('play', onAgain); p.off('seeking', onAgain); } catch (err) { /* player already disposed */ } };
  }, [videoUrlKey]);   // a new video, not a new link (link refresh): a fresh link must not reset the end card
  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return undefined;
    const on = () => setNativeFs(true); const off = () => setNativeFs(false);
    el.addEventListener('webkitbeginfullscreen', on); el.addEventListener('webkitendfullscreen', off);
    return () => { el.removeEventListener('webkitbeginfullscreen', on); el.removeEventListener('webkitendfullscreen', off); };
  }, [videoUrl]);
  // No automatic advance: the next lesson starts only when the student taps "Play next lesson" (or Up Next),
  // so it always starts from a tap, the way every browser (iPad and iPhone included) lets a new video start.
  // ── Link refresh, step 2: reload the source from a fresher link when the loaded one has run short ──────
  // Runs while paused and not on the end card. The loaded link is short when it has less time than a play from
  // the current spot needs (the page's own arithmetic). `fromError` is the mid-play case: the stream died, so
  // any fresher link is reason enough. Returns whether a reload was started.
  const rebuildFromFreshLink = (fromError) => {
    const p = vjsRef.current; const was = loadedUrlRef.current; const now = videoUrlRef.current;
    if (!p || !was || !now || was === now || !sourceForRef.current) return false;
    const pos = latestRef.current.pos || 0;
    const dur = duration || latestRef.current.dur || 0;
    const leftWas = cdnTokenSecondsLeft(was); const leftNow = cdnTokenSecondsLeft(now);
    if (leftNow !== null && leftWas !== null && leftNow <= leftWas) return false;   // the new link is no fresher
    if (!fromError) {
      const need = Math.max(CDN_TOKEN_MIN_SECONDS, videoNeedSeconds(dur, pos));
      if (leftWas === null || leftWas >= need) return false;                        // the loaded link still has the time
    }
    const silent = !(pos >= 1);                    // never played: a quiet reload, the play button stays
    loadedUrlRef.current = now;
    resumeFromRef.current = pos; resumedRef.current = false;   // the load effect's loadedmetadata seeks back to the spot
    setError(null);
    if (fromError) setIsPlaying(false);
    if (!silent) {
      setLinkOverlay('loading');
      const onReady = () => {
        try { p.off('loadedmetadata', onReady); } catch (err) { /* disposed */ }
        if (loadedUrlRef.current === now) setLinkOverlay('ready');
      };
      p.on('loadedmetadata', onReady);
    }
    try { p.src(sourceForRef.current(now)); } catch (err) { setLinkOverlay(null); return false; }
    return true;
  };
  rebuildFromFreshLinkRef.current = rebuildFromFreshLink;
  useEffect(() => {
    if (isPlaying || ended || linkOverlay) return;
    rebuildFromFreshLink(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl, isPlaying, ended]);
  useEffect(() => { if (isPlaying && linkOverlay && !resumingRef.current) setLinkOverlay(null); }, [isPlaying, linkOverlay]);   // started another way: the overlay is done
  useEffect(() => { setLinkOverlay(null); }, [videoUrlKey]);   // a new lesson starts clean
  // Resume (October 20, 2026): the overlay may have said "Resume lesson" for hours. The tap re-establishes everything
  // at tap time instead of trusting the source rebuilt back then: 1. a fresh session token, 2. a fresh link into page
  // state (through the page's hook, so both players and the PDF links see it), 3. the source reloaded from it at the
  // spot (a new FairPlay key session whose license carries the fresh token), 4. play. A play-then-pause inside the tap
  // captures the activation without playing the old source; the overlay stays up ('resuming') until the reload plays.
  const continueFromLink = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const p = vjsRef.current;
    if (!p) return;
    resumingRef.current = true;
    const first = p.play(); if (first && first.catch) first.catch(() => {});
    p.pause();   // play-then-pause inside the tap: the activation is captured, nothing plays until the reload
    setLinkOverlay('resuming');
    const cur = typeof p.currentTime === 'function' ? p.currentTime() : 0;
    const pos = cur > 0 ? cur : (latestRef.current.pos || resumeFromRef.current || 0);
    const wasLoaded = loadedUrlRef.current;   // put back if the reload fails, so the next fresher link rebuilds
    (async () => {
      try {
        // offline: nothing to refresh
        if (navigator.onLine !== false) await api.refreshToken();
        if (typeof refreshLink === 'function') {
          const before = videoUrlRef.current;
          const r = await refreshLink();
          // the fresh link reaches this player through a render; wait for it (normally one tick, at most 1 s)
          if (r === 'fresh') for (let i = 0; i < 40 && videoUrlRef.current === before; i++) await new Promise((res) => setTimeout(res, 25));
        }
        if (vjsRef.current !== p || !sourceForRef.current) return;   // the lesson changed meanwhile
        const now = videoUrlRef.current;
        const curNow = typeof p.currentTime === 'function' ? p.currentTime() : 0;
        const at = curNow > 0 ? curNow : pos;   // the spot now (the tap's play() may have moved on)
        loadedUrlRef.current = now;   // step 2's own check sees this link as the loaded one
        resumeFromRef.current = at; resumedRef.current = false;   // the load effect's loadedmetadata seeks back to the spot
        setError(null);
        await new Promise((resolve, reject) => {
          let timer = null;
          const done = () => { if (timer) clearTimeout(timer); try { p.off('loadedmetadata', onReady); p.off('error', onFail); } catch (err) { /* disposed */ } };
          const onReady = () => { done(); resolve(); };
          const onFail = () => { done(); reject(new Error('load failed')); };
          timer = setTimeout(onFail, 25000);   // no metadata and no error in 25 s: the error screen, not a spinner forever
          p.on('loadedmetadata', onReady); p.on('error', onFail);
          try { p.src(sourceForRef.current(now)); } catch (err) { onFail(); }
        });
        if (vjsRef.current !== p) return;
        setLinkOverlay(null); setIsLoading(false);
        const again = p.play(); if (again && again.catch) again.catch(() => {});
        setIsPlaying(true);
      } catch (err) {
        if (vjsRef.current !== p) return;
        if (loadedUrlRef.current !== wasLoaded) loadedUrlRef.current = wasLoaded;   // the loaded link is still the old one: step 2 rebuilds on the next fresher link
        resumeFromRef.current = pos;
        setLinkOverlay('ready'); setIsLoading(false);   // back to Resume lesson, never the error screen (October 30, 2026)
        setIsPlaying(false);
      } finally {
        resumingRef.current = false;
      }
    })();
  };
  const replay = () => {
    setEnded(false);
    const p = vjsRef.current;
    if (p) { p.currentTime(0); setCurrentTime(0); }
    setIsPlaying(true);
  };
  useEffect(() => { const p = vjsRef.current; if (p) p.playbackRate(playbackSpeed); }, [playbackSpeed, mediaReady]);   // also once the media is ready: a speed chosen before it existed (e.g. a saved one) must still apply

  // ── Subtitles (1.21) ──────────────────────────────────────────────────────
  // Tracks come from the lesson data: video.subtitles = [{ lang, label, url, kind }], kind
  // 'subtitles' or 'captions' (English (CC) keeps its own key, 'en-cc'). With none, the
  // Subtitles control doesn't appear. Tracks are added once per player instance, after
  // the media is ready (mediaReady), by the effect below; never inside the load effect above.
  const subtitleOptions = useMemo(() => (Array.isArray(video && video.subtitles) ? video.subtitles : [])
    .filter((s) => s && s.url && s.lang)
    .map((s) => ({ ...s, kind: s.kind === 'captions' ? 'captions' : 'subtitles', key: s.kind === 'captions' ? s.lang + '-cc' : s.lang })), [video]);
  // Saved subtitles (1.22): start from the profile's preferredSubtitles, else Off. A lesson without
  // that language shows Off (activeSubtitleKey) without changing the saved value.
  const [subtitleKey, setSubtitleKey] = useState(() => (profile && typeof profile.preferredSubtitles === 'string' ? profile.preferredSubtitles : 'off'));
  const activeSubtitleKey = subtitleOptions.some((s) => s.key === subtitleKey) ? subtitleKey : 'off';
  // Saving (1.22) switches itself on: it only saves a field the profile already has (the sign the
  // backend supports it), so no request goes to an endpoint that doesn't exist yet. The in-memory
  // profile is updated too, so the next lesson starts with the choice.
  const savePreference = (field, value) => {
    if (!api || !profile || !Object.prototype.hasOwnProperty.call(profile, field)) return;
    if (profile[field] === value) return;   // unchanged: nothing to save (also keeps React's double-run in development quiet)
    profile[field] = value;
    try {
      const req = api.fetch('/profile/preferences', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }) });
      if (req && req.catch) req.catch(() => { /* a failed save changes nothing on screen */ });
    } catch (err) { /* a failed save changes nothing on screen */ }
  };
  const prefsReadyRef = useRef(false);
  useEffect(() => { if (prefsReadyRef.current) savePreference('preferredPlaybackSpeed', playbackSpeed); }, [playbackSpeed]);
  useEffect(() => { if (prefsReadyRef.current) savePreference('preferredSubtitles', subtitleKey); }, [subtitleKey]);
  useEffect(() => { prefsReadyRef.current = true; }, []);
  const [tracksReady, setTracksReady] = useState(0);
  const subsAddedFor = useRef(null);
  useEffect(() => {
    const p = vjsRef.current;
    if (!mediaReady || !p || !subtitleOptions.length || subsAddedFor.current === p) return;
    subsAddedFor.current = p;
    for (const s of subtitleOptions) {
      try { p.addRemoteTextTrack({ kind: s.kind, src: s.url, srclang: s.lang, label: s.label }, false); }
      catch (err) { /* one bad entry must not block the others */ }
    }
    setTracksReady((n) => n + 1);
  }, [mediaReady, subtitleOptions]);
  useEffect(() => {
    const p = vjsRef.current;
    if (!p || !tracksReady) return;
    const opt = subtitleOptions.find((s) => s.key === subtitleKey);
    try {
      const list = p.textTracks();
      for (let i = 0; i < list.length; i++) {
        const t = list[i];
        if (t.kind !== 'subtitles' && t.kind !== 'captions') continue;
        const on = !!opt && t.language === opt.lang && t.kind === opt.kind && (!opt.label || t.label === opt.label);
        t.mode = on ? 'showing' : 'disabled';
      }
    } catch (err) { /* subtitles are optional: never disturb playback */ }
  }, [subtitleKey, tracksReady, subtitleOptions]);

  const formatTime = (s) => { if (!s || isNaN(s)) return '0:00'; return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`; };

  const timeAtX = (clientX) => {
    if (!progressRef.current || !totalSeconds) return null;
    const r = progressRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(totalSeconds, ((clientX - r.left) / r.width) * totalSeconds));
  };
  const commitDragSeek = (t) => {
    setCurrentTime(t);
    const p = vjsRef.current;
    if (p) p.currentTime(t);
  };
  const handleProgressClick = (e) => { e.stopPropagation(); if (progressRef.current) { const r = progressRef.current.getBoundingClientRect(); const t = Math.floor(((e.clientX - r.left) / r.width) * totalSeconds); setCurrentTime(t); const p = vjsRef.current; if (p) p.currentTime(t); } };

  const handleVolumeChange = useCallback((cx) => { if (volumeRef.current) { const r = volumeRef.current.getBoundingClientRect(); setVolume(Math.max(0, Math.min(100, ((cx - r.left) / r.width) * 100))); setIsMuted(false); } }, []);
  const handleVolumeClick = (e) => { e.stopPropagation(); handleVolumeChange(e.clientX); };
  const handleVolumeTouchMove = useCallback((e) => { e.preventDefault(); if (e.touches[0]) handleVolumeChange(e.touches[0].clientX); }, [handleVolumeChange]);

  const skip = (s) => { const p = vjsRef.current; const t = Math.max(0, Math.min(totalSeconds, currentTime + s)); setCurrentTime(t); if (p) p.currentTime(t); showControlsTemporarily(); };
  // ── Lock-screen and headphone controls (1.24) ───────────────────────────
  // The Media Session API. Registered when this player starts playing, and kept after a pause so the
  // lock-screen play button still works; cleared when this player unmounts, if it is still the one
  // registered. Handlers go through a ref, so they always call this render's functions.
  const mediaSessionRef = useRef({});
  mediaSessionRef.current = {
    play: () => setIsPlaying(true),
    pause: () => setIsPlaying(false),
    skip: (s) => skip(s),
    seekTo: (t) => { const p = vjsRef.current; if (p) { p.currentTime(t); setCurrentTime(t); } },
  };
  const mediaSessionOwner = useRef({});
  useEffect(() => {
    if (!isPlaying || typeof navigator === 'undefined' || !navigator.mediaSession) return;
    const ms = navigator.mediaSession;
    try {
      if (typeof window.MediaMetadata === 'function') ms.metadata = new window.MediaMetadata({ title: (video && video.title) || '', artist: (video && video.series && video.series.name) || '', album: 'RoastBrew' });
    } catch (err) { /* metadata is optional */ }
    const set = (a, h) => { try { ms.setActionHandler(a, h); } catch (err) { /* this action isn't supported here */ } };
    set('play', () => mediaSessionRef.current.play());
    set('pause', () => mediaSessionRef.current.pause());
    set('seekbackward', () => mediaSessionRef.current.skip(-10));
    set('seekforward', () => mediaSessionRef.current.skip(10));
    set('seekto', (d) => { if (d && typeof d.seekTime === 'number') mediaSessionRef.current.seekTo(d.seekTime); });
    window.__rbMediaSessionOwner = mediaSessionOwner.current;
  }, [isPlaying, video]);
  useEffect(() => () => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession || window.__rbMediaSessionOwner !== mediaSessionOwner.current) return;
    ['play', 'pause', 'seekbackward', 'seekforward', 'seekto'].forEach((a) => { try { navigator.mediaSession.setActionHandler(a, null); } catch (err) { /* not supported */ } });
    window.__rbMediaSessionOwner = null;
  }, []);

  const toggleFullscreen = async () => {
    try {
      const techEl = mediaRef.current;
      if (isMobile && techEl && techEl.webkitEnterFullscreen) { techEl.webkitEnterFullscreen(); return; }
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        if (containerRef.current?.requestFullscreen) await containerRef.current.requestFullscreen();
        else if (containerRef.current?.webkitRequestFullscreen) await containerRef.current.webkitRequestFullscreen();
        try { await window.screen.orientation.lock('landscape'); } catch (e) {}
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
        try { await window.screen.orientation.lock('portrait'); } catch (e) { try { window.screen.orientation.unlock(); } catch (e2) {} }
      }
    } catch (e) {}
  };
  // ── KEYBOARD SHORTCUTS ─────────────────────────────────────────────────────
  //   ← / →     skip by skipAmount (the same distance the on-screen buttons use)
  //   Space / K play-pause
  //   M         mute
  //   F         fullscreen
  //
  // PRECEDENCE, in this order:
  //   1. Focus in a text field wins over everything. Typing "quadratic" into the
  //      search bar while a lesson plays must move the caret and type spaces, not
  //      scrub and pause the video. Checked against the event target AND
  //      document.activeElement so it covers every input on the page — the search
  //      bar today, and anything added later without knowing this handler exists.
  //   2. Space always toggles play/pause when focus is free. Someone who paused
  //      with Space expects Space to resume, and under the "only while playing"
  //      rule below it would not.
  //   3. Everything else applies ONLY while this player is playing, so the arrow
  //      keys still scroll the page when nothing is running.
  //
  // Two players exist on this page (lesson + answer key) but they cannot both be
  // playing — an effect forces the other false whenever one starts — so "the
  // playing one" is unambiguous and no targeting logic is needed.
  //
  // NOTE: this does NOT stopPropagation. App.jsx's ensureFresh listens for keydown
  // to re-mint expiring signed URLs; swallowing the event would silently disable
  // that. preventDefault is called only for keys actually handled, so page
  // scrolling is untouched otherwise.
  // ── Desktop shortcuts (1.25) ─────────────────────────────────────────────
  // J/L ±10 s, < > through the speeds, C subtitles on/off, 0–9 jump to 0–90%, ? the shortcut sheet.
  // Handled above the "idle" line in onKey, so they also work while paused.
  const [showShortcuts, setShowShortcuts] = useState(false);
  const lastSubtitleRef = useRef(null);
  useEffect(() => { if (activeSubtitleKey !== 'off') lastSubtitleRef.current = activeSubtitleKey; }, [activeSubtitleKey]);
  const stepSpeed = (dir) => {
    const i = PLAYBACK_SPEEDSDesktop.indexOf(playbackSpeed);
    const next = PLAYBACK_SPEEDSDesktop[Math.max(0, Math.min(PLAYBACK_SPEEDSDesktop.length - 1, (i < 0 ? PLAYBACK_SPEEDSDesktop.indexOf(1) : i) + dir))];
    setPlaybackSpeed(next); showControlsTemporarily();
  };
  const toggleSubtitles = () => {
    if (!subtitleOptions.length) return;
    if (activeSubtitleKey !== 'off') { setSubtitleKey('off'); return; }
    const last = lastSubtitleRef.current;
    setSubtitleKey(last && subtitleOptions.some((s) => s.key === last) ? last : subtitleOptions[0].key);
  };
  const jumpTo = (fraction) => {
    const p = vjsRef.current; const pd = p ? p.duration() : 0; const d = (Number.isFinite(pd) && pd > 0) ? pd : totalSeconds;
    if (!d) return;
    const t = d * fraction; setCurrentTime(t); if (p) p.currentTime(t); showControlsTemporarily();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const t = e.target;
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      const isField = (el) => !!(el && el.closest && el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]'));
      if (isField(t) || isField(active)) return;      // focus wins, always

      const key = e.key;

      if (key === ' ' || key === 'Spacebar' || key === 'k' || key === 'K') {
        e.preventDefault();                            // Space would scroll the page
        setIsPlaying(!isPlaying);
        showControlsTemporarily();
        return;
      }

      // New shortcuts (1.25): they work whether or not the video is playing.
      if (key === 'Escape' && showShortcuts) { e.preventDefault(); setShowShortcuts(false); return; }
      if (key === '?') { e.preventDefault(); setShowShortcuts((v) => !v); return; }
      if (key === 'j' || key === 'J') { e.preventDefault(); skip(-10); return; }
      if (key === 'l' || key === 'L') { e.preventDefault(); skip(10); return; }
      if (key === '<' || key === ',') { e.preventDefault(); stepSpeed(-1); return; }
      if (key === '>' || key === '.') { e.preventDefault(); stepSpeed(1); return; }
      if ((key === 'c' || key === 'C') && subtitleOptions.length) { e.preventDefault(); toggleSubtitles(); return; }
      if (/^[0-9]$/.test(key)) { e.preventDefault(); jumpTo(Number(key) / 10); return; }

      if (!isPlaying) return;                          // idle: leave the keys alone

      if (key === 'ArrowRight') { e.preventDefault(); skip(skipAmount); }
      else if (key === 'ArrowLeft') { e.preventDefault(); skip(-skipAmount); }
      else if (key === 'm' || key === 'M') { e.preventDefault(); setIsMuted(!isMuted); showControlsTemporarily(); }
      else if (key === 'f' || key === 'F') { e.preventDefault(); toggleFullscreen(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const togglePiP = async () => {
    const p = vjsRef.current; if (!p) return;
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else if (document.pictureInPictureEnabled && p.requestPictureInPicture) await p.requestPictureInPicture();
    } catch (e) {}
  };

  useEffect(() => {
    const h = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', h); document.addEventListener('webkitfullscreenchange', h);
    const v = mediaRef.current; const ib = () => setIsFullscreen(true); const ie = () => setIsFullscreen(false);
    if (v) { v.addEventListener('webkitbeginfullscreen', ib); v.addEventListener('webkitendfullscreen', ie); }
    return () => {
      document.removeEventListener('fullscreenchange', h); document.removeEventListener('webkitfullscreenchange', h);
      if (v) { v.removeEventListener('webkitbeginfullscreen', ib); v.removeEventListener('webkitendfullscreen', ie); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrlKey]);

  useEffect(() => { if (!showSpeedMenu) return; const h = () => setShowSpeedMenu(false); document.addEventListener('click', h); return () => document.removeEventListener('click', h); }, [showSpeedMenu]);

  const playBtnSize = isMobile ? 52 : 88, playIconSz = isMobile ? 22 : 36;

  // Subtitles menu (desktop, 1.21): built like the speed menu: Off, then each language this lesson has.
  const [showSubtitleMenu, setShowSubtitleMenu] = useState(false);
  useEffect(()=>{if(!showSubtitleMenu)return;const h=()=>setShowSubtitleMenu(false);document.addEventListener('click',h);return()=>document.removeEventListener('click',h);},[showSubtitleMenu]);
  useEffect(()=>{if(showSpeedMenu)setShowSubtitleMenu(false);},[showSpeedMenu]);
  const subtitleMenuEl = showSubtitleMenu && (
    <div onClick={(e)=>e.stopPropagation()} role="menu" aria-label="Subtitles" style={{position:'absolute',bottom:'100%',right:0,marginBottom:'8px',background:'rgba(20,20,20,0.95)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',borderRadius:'10px',padding:'8px',minWidth:'170px',boxShadow:'0 8px 32px rgba(0,0,0,0.5)',border:'1px solid rgba(255,255,255,0.1)',zIndex:100}}>
      {[{ key: 'off', label: 'Off' }, ...subtitleOptions].map((s) => (
        <button key={s.key} role="menuitemradio" aria-checked={activeSubtitleKey===s.key} onClick={(e)=>{e.stopPropagation();setSubtitleKey(s.key);setShowSubtitleMenu(false);}} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',width:'100%',padding:'8px 12px',background:activeSubtitleKey===s.key?'rgba(255,255,255,0.1)':'transparent',border:'none',borderRadius:'6px',cursor:'pointer',color:activeSubtitleKey===s.key?colors.accent:'#fff',fontSize:'14px',fontFamily:'"DM Sans",sans-serif',fontWeight:activeSubtitleKey===s.key?'600':'400',textAlign:'left',WebkitTapHighlightColor:'transparent'}}>
          <span>{s.label}</span>
          {activeSubtitleKey===s.key&&<svg width="14" height="14" viewBox="0 0 24 24" fill={colors.accent}><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>}
        </button>
      ))}
    </div>
  );

  const speedMenuEl = showSpeedMenu && (
    <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', bottom: '100%', right: 0, marginBottom: '8px', background: 'rgba(20,20,20,0.95)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: '10px', padding: '8px', minWidth: isMobile ? '120px' : '140px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)', zIndex: 100 }}>
      {PLAYBACK_SPEEDSDesktop.map(spd => (
        <button key={spd} onClick={(e) => { e.stopPropagation(); setPlaybackSpeed(spd); setShowSpeedMenu(false); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: isMobile ? '10px 12px' : '8px 12px', background: playbackSpeed === spd ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', color: playbackSpeed === spd ? colors.accent : '#fff', fontSize: isMobile ? '13px' : '14px', fontFamily: '"DM Sans",sans-serif', fontWeight: playbackSpeed === spd ? '600' : '400', WebkitTapHighlightColor: 'transparent' }}>
          <span>{spd === 1 ? 'Normal' : `${spd}x`}</span>
          {playbackSpeed === spd && <svg width="14" height="14" viewBox="0 0 24 24" fill={colors.accent}><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>}
        </button>
      ))}
    </div>
  );

  const topBarBtn = (children, extraStyle = {}, onClick = undefined) => (
    <button onClick={onClick} style={{ flex: 1, minHeight: '32px', padding: '6px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', color: 'rgba(255,255,255,0.9)', fontSize: '9px', fontWeight: '600', fontFamily: '"DM Sans",sans-serif', backdropFilter: 'blur(10px)', WebkitTapHighlightColor: 'transparent', ...extraStyle }}>{children}</button>
  );

  // ── B3 (2.12i) · the bar's tier follows the player's own width (the approved design) ──────────────────
//   large  720 px and up: the full bar, exactly as before (its code is unchanged)
//   medium 480–719 px:    the volume slider and Picture-in-Picture move into a settings menu
//   small  under 480 px:  play, time, settings and full screen; skip, speed, subtitles and volume move into
//                         the settings menu
//   Every keyboard shortcut keeps working at every tier. The settings menu behaves like the speed and subtitle
//   menus: one menu at a time, an outside click closes it, and so does Esc. In medium and small, speed and subtitles
//   are views of the settings menu (with a back row), and the menu is capped at the player's height and scrolls, so
//   nothing is clipped on a short player. While it's open, the centred play button hides: it sits above the controls
//   overlay (z-index 20 over 15) and would otherwise cover the menu.
  const [barTier, setBarTier] = useState('large');
  const [playerH, setPlayerH] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const pick = () => { const r = el.getBoundingClientRect(); setBarTier(r.width >= 720 ? 'large' : r.width >= 480 ? 'medium' : 'small'); setPlayerH(Math.round(r.height)); };
    pick();
    const observer = new ResizeObserver(pick);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsView, setSettingsView] = useState('main');   // 'main' · 'speed' · 'subtitles'
  const openSettings = (view) => { setSettingsView(view); setShowSettings(true); };
  useEffect(()=>{if(!showSettings)setSettingsView('main');},[showSettings]);
  useEffect(()=>{if(!showSettings)return;const h=()=>setShowSettings(false);document.addEventListener('click',h);return()=>document.removeEventListener('click',h);},[showSettings]);
  useEffect(()=>{if(!showSettings)return;const k=(e)=>{if(e.key==='Escape')setShowSettings(false);};window.addEventListener('keydown',k);return()=>window.removeEventListener('keydown',k);},[showSettings]);
  useEffect(()=>{if(showSettings){setShowSpeedMenu(false);setShowSubtitleMenu(false);}},[showSettings]);
  useEffect(()=>{if(showSpeedMenu||showSubtitleMenu)setShowSettings(false);},[showSpeedMenu,showSubtitleMenu]);
  useEffect(()=>{if(barTier==='large')setShowSettings(false);},[barTier]);
  const compactMenuOpen = barTier !== 'large' && showSettings;
  const menuMaxH = Math.max(72, playerH - 96);
  const barIconBtn = { padding:'8px', background:'none', border:'none', cursor:'pointer', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center' };
  const volumeIcon = (sz) => (<svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{isMuted||volume===0?<><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></>:<><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14"/></>}</svg>);
  const activeSubtitleLabel = activeSubtitleKey === 'off' ? 'Off' : ((subtitleOptions.find((o) => o.key === activeSubtitleKey) || {}).label || 'Off');
  const rowStyle = {display:'flex',alignItems:'center',justifyContent:'space-between',gap:'16px',width:'100%',padding:'8px 12px',background:'none',border:'none',borderRadius:'6px',cursor:'pointer',color:'#fff',fontFamily:'"DM Sans",sans-serif',fontSize:'13px',textAlign:'left',whiteSpace:'nowrap'};
  const settingsRow = (label, onClick, value) => (
    <button role="menuitem" onClick={(e)=>{e.stopPropagation();onClick();}} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'16px',width:'100%',padding:'8px 12px',background:'none',border:'none',borderRadius:'6px',cursor:'pointer',color:'#fff',fontFamily:'"DM Sans",sans-serif',fontSize:'13px',textAlign:'left',whiteSpace:'nowrap'}}>
      <span>{label}</span>{value !== undefined && <span style={{color:'rgba(255,255,255,0.6)'}}>{value}</span>}
    </button>
  );
  const backRow = (label) => (<button role="menuitem" onClick={(e)=>{e.stopPropagation();setSettingsView('main');}} style={{...rowStyle,color:'rgba(255,255,255,0.75)'}}><span>‹ {label}</span></button>);
  const choiceRow = (key, label, checked, onPick) => (
    <button key={key} role="menuitemradio" aria-checked={checked} onClick={(e)=>{e.stopPropagation();onPick();setShowSettings(false);}} style={rowStyle}>
      <span>{label}</span>{checked&&<svg width="14" height="14" viewBox="0 0 24 24" fill={colors.accent} aria-hidden="true"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>}
    </button>
  );
  const settingsMenuEl = showSettings && (
    <div onClick={(e)=>e.stopPropagation()} role="menu" aria-label={settingsView==='speed'?'Playback speed':settingsView==='subtitles'?'Subtitles':'Settings'} style={{position:'absolute',bottom:'100%',right:0,marginBottom:'8px',background:'rgba(20,20,20,0.95)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',borderRadius:'10px',padding:'6px',minWidth:'210px',maxHeight:menuMaxH+'px',overflowY:'auto',zIndex:30,boxShadow:'0 8px 32px rgba(0,0,0,0.5)'}}>
      {settingsView==='speed' && <>{backRow('Speed')}{PLAYBACK_SPEEDSDesktop.map((spd)=>choiceRow(spd, spd===1?'Normal':`${spd}x`, playbackSpeed===spd, ()=>setPlaybackSpeed(spd)))}</>}
      {settingsView==='subtitles' && <>{backRow('Subtitles')}{[{ key: 'off', label: 'Off' }, ...subtitleOptions].map((o)=>choiceRow(o.key, o.label, activeSubtitleKey===o.key, ()=>setSubtitleKey(o.key)))}</>}
      {settingsView==='main' && <>
      {barTier==='small' && settingsRow(`Back ${skipAmount} s`, ()=>skip(-skipAmount))}
      {barTier==='small' && settingsRow(`Forward ${skipAmount} s`, ()=>skip(skipAmount))}
      {barTier==='small' && settingsRow('Speed', ()=>setSettingsView('speed'), `${playbackSpeed===1?'Normal':`${playbackSpeed}x`} ›`)}
      {barTier==='small' && subtitleOptions.length>0 && settingsRow('Subtitles', ()=>setSettingsView('subtitles'), `${activeSubtitleLabel} ›`)}
      <div role="group" aria-label="Volume" style={{display:'flex',alignItems:'center',gap:'8px',padding:'4px 8px'}}>
        <button aria-label={isMuted||volume===0?'Unmute':'Mute'} onClick={(e)=>{e.stopPropagation();setIsMuted(!isMuted);}} style={barIconBtn}>{volumeIcon(20)}</button>
        <div ref={volumeRef} role="slider" aria-label="Volume" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(isMuted?0:volume)} onClick={handleVolumeClick} style={{flex:1,height:'4px',background:'rgba(255,255,255,0.25)',borderRadius:'2px',cursor:'pointer',position:'relative'}}>
          <div style={{width:`${isMuted?0:volume}%`,height:'100%',borderRadius:'2px',background:colors.accent,pointerEvents:'none'}} />
          <div style={{position:'absolute',left:`${isMuted?0:volume}%`,top:'50%',transform:'translate(-50%,-50%)',width:'12px',height:'12px',borderRadius:'50%',background:'#fff',pointerEvents:'none'}} />
        </div>
      </div>
      {settingsRow('Picture-in-Picture', ()=>{setShowSettings(false);togglePiP();})}
      </>}
    </div>
  );
  const compactRow = (
    <div data-bar-tier={barTier} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'8px'}}>
      <div style={{display:'flex',alignItems:'center',gap:'6px',minWidth:0}}>
        <button aria-label={isPlaying?'Pause':'Play'} onClick={(e)=>{e.stopPropagation();setIsPlaying(!isPlaying);showControlsTemporarily();}} style={barIconBtn}>
          <svg width={24} height={24} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">{isPlaying?<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>:<path d="M8 5v14l11-7z"/>}</svg>
        </button>
        {barTier==='medium' && <SkipButtonDesktop direction="back" onClick={()=>skip(-skipAmount)} currentTier={currentTier} isMobile seconds={skipAmount} />}
        {barTier==='medium' && <SkipButtonDesktop direction="forward" onClick={()=>skip(skipAmount)} currentTier={currentTier} isMobile seconds={skipAmount} />}
        {barTier==='medium' && <button aria-label={isMuted||volume===0?'Unmute':'Mute'} onClick={(e)=>{e.stopPropagation();setIsMuted(!isMuted);}} style={barIconBtn}>{volumeIcon(22)}</button>}
        <span style={{fontSize:'13px',color:'rgba(255,255,255,0.9)',fontFamily:'"DM Sans",sans-serif',whiteSpace:'nowrap'}}>{formatTime(currentTime)} / {formatTime(totalSeconds)}</span>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:'6px',flexShrink:0}}>
        {barTier==='medium' && (
          <div style={{position:'relative'}}>
            <button aria-label="Playback speed" onClick={(e)=>{e.stopPropagation();if(showSettings&&settingsView==='speed')setShowSettings(false);else openSettings('speed');}} style={{padding:'6px 10px',background:'rgba(255,255,255,0.1)',border:'none',borderRadius:'6px',cursor:'pointer',color:'#fff',fontFamily:'"DM Sans",sans-serif',fontSize:'12px',whiteSpace:'nowrap'}}>{playbackSpeed===1?'1x':`${playbackSpeed}x`}</button>
          </div>
        )}
        {barTier==='medium' && subtitleOptions.length>0 && (
          <div style={{position:'relative'}}>
            <button aria-label="Subtitles" aria-pressed={activeSubtitleKey!=='off'} onClick={(e)=>{e.stopPropagation();if(showSettings&&settingsView==='subtitles')setShowSettings(false);else openSettings('subtitles');}} style={barIconBtn}>
              <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M7 15h4M15 15h2M7 11h2M13 11h4"/></svg>
            </button>
          </div>
        )}
        <div style={{position:'relative'}}>
          <button aria-label="Settings" aria-haspopup="menu" aria-expanded={showSettings} onClick={(e)=>{e.stopPropagation();if(showSettings)setShowSettings(false);else openSettings('main');}} style={barIconBtn}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
          </button>
          {settingsMenuEl}
        </div>
        <button aria-label={isFullscreen?'Exit full screen':'Full screen'} onClick={(e)=>{e.stopPropagation();toggleFullscreen();}} style={barIconBtn}>
          <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">{isFullscreen?<path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3"/>:<path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3"/>}</svg>
        </button>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="video-player-container" style={{ position: 'relative', aspectRatio: '16/9', borderRadius: isFullscreen ? 0 : (isMobile ? '10px' : '16px'), overflow: 'hidden', background: '#000', cursor: showControls ? 'default' : 'none' }}
      onClick={handleVideoAreaClick} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      {autoMuted && (soundPromptBig ? (
        <button type="button" aria-label="Tap for sound" onClick={(e) => { e.stopPropagation(); }} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: 'clamp(14px, 3.5vw, 22px) clamp(22px, 5vw, 34px)', borderRadius: '18px', border: 'none', background: '#111111', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 'clamp(16px, 2.4vw, 22px)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', boxShadow: '0 10px 40px rgba(0,0,0,0.45)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: 'clamp(34px, 7vw, 48px)', height: 'clamp(34px, 7vw, 48px)' }}><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>
          Tap for sound
        </button>
      ) : (
        <button type="button" aria-label="Tap for sound" onClick={(e) => { e.stopPropagation(); }} style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 40, display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '999px', border: 'none', background: 'rgba(0,0,0,0.72)', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>
          Tap for sound
        </button>
      ))}

      <div className="video-landscape-bg" />
      <div className="video-player-inner" style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', borderRadius: 'inherit' }}>

        {/* Video.js mounts its <video> into this host div (React does not own the media element) */}
        {videoUrl && <div ref={mediaHostRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />}

        {/* Gradient placeholder */}
        {!videoUrl && <div style={{ position: 'absolute', inset: 0, background: '#0a0a0a' }} />}

        {/* Loading */}
        {isLoading && !showError && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', zIndex: 20 }}><div style={{ width: isMobile ? 40 : 48, height: isMobile ? 40 : 48, border: '3px solid rgba(255,255,255,0.2)', borderTopColor: colors.accent, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /></div>}

        {/* Error (debounced) */}
        {showError && <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg,${gradientFor(video.id)[0]}40,${gradientFor(video.id)[1]}40)`, zIndex: 20 }}>
          <svg width={isMobile ? 40 : 48} height={isMobile ? 40 : 48} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
          <p style={{ marginTop: 16, color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: isMobile ? 12 : 14 }}>{error}</p>
        </div>}
        <LinkOverlay state={linkOverlay} at={resumeFromRef.current} colors={colors} onContinue={continueFromLink} small={isMobile} />

        {/* Cinematic vignette (mobile) */}
        {isMobile && <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center,transparent 40%,rgba(0,0,0,0.05) 100%)', pointerEvents: 'none', zIndex: 1 }} />}

        {/* Dark overlay when paused */}
        {!showError && <div style={{ position: 'absolute', inset: 0, background: '#000', opacity: isPlaying ? 0 : 0.4, zIndex: 5, pointerEvents: 'none', transition: 'opacity 0.5s ease' }} />}

        {/* ===== MOBILE LAYOUT ===== */}
        {isMobile && <>
          <div data-controls="true" onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '8px 10px', background: 'linear-gradient(to bottom,rgba(0,0,0,0.8) 0%,rgba(0,0,0,0.4) 70%,transparent 100%)', display: 'flex', justifyContent: 'space-evenly', alignItems: 'center', zIndex: 16, gap: '8px', opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: showControls ? 'auto' : 'none' }}>
            {subtitleOptions.length > 0 && (
              <div style={{flex:1,position:'relative'}}>
                {topBarBtn(<><svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M7 12h4M13 12h4M7 16h10" /></svg><span>CC</span></>,{},e=>{e.stopPropagation();setShowSpeedMenu(false);setShowSubtitleMenu(!showSubtitleMenu);})}
                {subtitleMenuEl}
              </div>
            )}
            <div style={{ flex: 1, position: 'relative' }}>
              {topBarBtn(<span>{playbackSpeed === 1 ? '1x' : `${playbackSpeed}x`}</span>, {}, e => { e.stopPropagation(); setShowSpeedMenu(!showSpeedMenu); })}
              {speedMenuEl}
            </div>
            {topBarBtn(<><svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" /><rect x="11" y="9" width="9" height="6" rx="1" fill="currentColor" stroke="none" /></svg><span>PiP</span></>, {}, e => { e.stopPropagation(); togglePiP(); })}
            {topBarBtn(<svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{isMuted || volume === 0 ? <><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M23 9l-6 6M17 9l6 6" /></> : <><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" /></>}</svg>, {}, e => { e.stopPropagation(); setIsMuted(!isMuted); })}
          </div>

          <div data-controls="true" onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: '45%', left: '50%', transform: 'translate(-50%,-50%)', display: 'flex', alignItems: 'center', gap: '14px', zIndex: 16, opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: showControls ? 'auto' : 'none' }}>
            <SkipButtonDesktop direction="back" onClick={() => skip(-skipAmount)} currentTier={currentTier} isMobile seconds={skipAmount} />
            <button onClick={() => { setIsPlaying(!isPlaying); showControlsTemporarily(); }} style={{ width: `${playBtnSize}px`, height: `${playBtnSize}px`, borderRadius: '50%', background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', cursor: 'pointer', border: 'none', animation: !isPlaying ? 'pulse 2.2s ease-in-out infinite' : 'none' }}>
              <svg width={playIconSz} height={playIconSz} viewBox="0 0 24 24" fill="#000" style={{ marginLeft: !isPlaying ? '3px' : '0' }}>
                {isPlaying ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}
              </svg>
            </button>
            <SkipButtonDesktop direction="forward" onClick={() => skip(skipAmount)} currentTier={currentTier} isMobile seconds={skipAmount} />
          </div>

          <div data-controls="true" onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px 12px', background: 'linear-gradient(to top,rgba(0,0,0,0.9) 0%,rgba(0,0,0,0.5) 50%,transparent 100%)', zIndex: 16, opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: showControls ? 'auto' : 'none' }}>
            <div ref={progressRef} onClick={handleProgressClick} style={{ height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '3px', cursor: 'pointer', marginBottom: '10px', position: 'relative' }}>
              <div style={{ position: 'absolute', height: '100%', width: `${bufferedPercent}%`, background: 'rgba(255,255,255,0.3)', borderRadius: '3px', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', height: '100%', width: `${progressPercent}%`, borderRadius: '3px', pointerEvents: 'none', ...pStyles.played }} />
              <div style={{ position: 'absolute', left: `${progressPercent}%`, top: '50%', transform: 'translate(-50%,-50%)', width: '12px', height: '12px', borderRadius: '50%', pointerEvents: 'none', ...pStyles.thumb }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)', fontFamily: '"DM Sans",sans-serif', fontVariantNumeric: 'tabular-nums' }}>{formatTime(currentTime)} / {formatTime(totalSeconds)}</span>
              <button onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }} style={{ padding: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#fff' }}>
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{isFullscreen ? <path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3" /> : <path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3" />}</svg>
              </button>
            </div>
          </div>
        </>}

        {/* ===== DESKTOP/TABLET LAYOUT ===== */}
        {!isMobile && <>
          {(!isPlaying || showControls) && !compactMenuOpen && <div data-controls="true" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 20, opacity: !isPlaying ? 1 : (showControls ? 0.9 : 0), transition: 'opacity 0.3s ease', pointerEvents: (!isPlaying || showControls) ? 'auto' : 'none' }} onClick={(e) => { e.stopPropagation(); setIsPlaying(!isPlaying); showControlsTemporarily(); }}>
            <div style={{ width: `${playBtnSize}px`, height: `${playBtnSize}px`, borderRadius: '50%', background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', cursor: 'pointer', animation: !isPlaying ? 'pulse 2.2s ease-in-out infinite' : 'none' }}>
              <svg width={playIconSz} height={playIconSz} viewBox="0 0 24 24" fill="#000" style={{ marginLeft: !isPlaying ? '4px' : '0' }}>{isPlaying ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}</svg>
            </div>
          </div>}

          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(0,0,0,0.9) 0%,rgba(0,0,0,0.6) 30%,transparent 60%,transparent 80%,rgba(0,0,0,0.4) 100%)', opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: showControls ? 'auto' : 'none', zIndex: 15 }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>{/* The course and lesson names are no longer drawn on the video (October 15, 2026): the title stack above it has them. The box stays so the layout does not shift. */}</div>
            </div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px 24px' }}>
              <div ref={!isMobile ? progressRef : undefined}
            onClick={(e) => { if (dragRef.current.justEnded) { dragRef.current.justEnded = false; e.stopPropagation(); return; } handleProgressClick(e); }}
            onMouseMove={(e) => setHoverTime(timeAtX(e.clientX))}
            onMouseLeave={() => setHoverTime(null)}
            onPointerDown={(e) => { if (e.pointerType !== 'mouse' || e.button !== 0) return; try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* capture is optional */ } dragRef.current = { active: true, moved: false, x: e.clientX, justEnded: false }; }}
            onPointerMove={(e) => { const d = dragRef.current; if (!d.active) return; if (!d.moved && Math.abs(e.clientX - d.x) < 3) return; d.moved = true; setDragTime(timeAtX(e.clientX)); }}
            onPointerUp={(e) => { const d = dragRef.current; if (!d.active) return; d.active = false; if (!d.moved) return; const t = timeAtX(e.clientX); setDragTime(null); if (t !== null) commitDragSeek(t); d.justEnded = true; setTimeout(() => { dragRef.current.justEnded = false; }, 0); }}
            onPointerCancel={() => { dragRef.current.active = false; setDragTime(null); }}
            style={{ height: '5px', background: 'rgba(255,255,255,0.2)', borderRadius: '3px', cursor: 'pointer', marginBottom: '16px', position: 'relative' }}>
                <div style={{ position: 'absolute', height: '100%', width: `${bufferedPercent}%`, background: 'rgba(255,255,255,0.3)', borderRadius: '3px', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', height: '100%', width: `${progressPercent}%`, borderRadius: '3px', pointerEvents: 'none', ...pStyles.played }} />
                <div style={{ position: 'absolute', left: `${progressPercent}%`, top: '50%', transform: 'translate(-50%,-50%)', width: '14px', height: '14px', borderRadius: '50%', pointerEvents: 'none', ...pStyles.thumb }} />
                {(dragTime ?? hoverTime) !== null && (
                  <div style={{ position: 'absolute', left: `${((dragTime ?? hoverTime) / totalSeconds) * 100}%`, bottom: '16px', transform: 'translateX(-50%)', padding: '3px 8px', borderRadius: '6px', background: 'rgba(0,0,0,0.85)', color: '#fff', fontSize: '12px', fontWeight: 700, fontFamily: '"DM Sans",sans-serif', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                    {formatTime(dragTime ?? hoverTime)}
                  </div>
                )}
              </div>
              {barTier === 'large' ? (
<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button aria-label={isPlaying?'Pause':'Play'} onClick={(e) => { e.stopPropagation(); setIsPlaying(!isPlaying); showControlsTemporarily(); }} style={{ padding: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#fff' }}>
                    <svg width={24} height={24} viewBox="0 0 24 24" fill="currentColor">{isPlaying ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}</svg>
                  </button>
                  <SkipButtonDesktop direction="back" onClick={() => skip(-skipAmount)} currentTier={currentTier} isMobile={false} seconds={skipAmount} />
                  <SkipButtonDesktop direction="forward" onClick={() => skip(skipAmount)} currentTier={currentTier} isMobile={false} seconds={skipAmount} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button aria-label={isMuted||volume===0?'Unmute':'Mute'} onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }} style={{ padding: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#fff' }}>
                      <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{isMuted || volume === 0 ? <><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M23 9l-6 6M17 9l6 6" /></> : <><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" /></>}</svg>
                    </button>
                    <div ref={volumeRef} onClick={handleVolumeClick} onTouchStart={(e) => { e.stopPropagation(); handleVolumeChange(e.touches[0].clientX); }} onTouchMove={handleVolumeTouchMove} style={{ width: '80px', height: '4px', background: 'rgba(255,255,255,0.3)', borderRadius: '2px', cursor: 'pointer', position: 'relative' }}>
                      <div style={{ width: `${isMuted ? 0 : volume}%`, height: '100%', borderRadius: '2px', pointerEvents: 'none', ...vStyles }} />
                      <div style={{ position: 'absolute', left: `${isMuted ? 0 : volume}%`, top: '50%', transform: 'translate(-50%,-50%)', width: '10px', height: '10px', background: '#fff', borderRadius: '50%', pointerEvents: 'none' }} />
                    </div>
                  </div>
                  <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)', fontFamily: '"DM Sans",sans-serif', fontVariantNumeric: 'tabular-nums' }}>{formatTime(currentTime)} / {formatTime(totalSeconds)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ position: 'relative' }}>
                    <button onClick={(e) => { e.stopPropagation(); setShowSpeedMenu(!showSpeedMenu); }} style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#fff', fontSize: '13px', fontFamily: '"DM Sans",sans-serif', fontWeight: '500' }}>{playbackSpeed === 1 ? 'Normal' : `${playbackSpeed}x`}</button>
                    {speedMenuEl}
                  </div>
                  {subtitleOptions.length > 0 && (
                    <div style={{position:'relative'}}>
                      <button aria-label="Subtitles" aria-pressed={activeSubtitleKey!=='off'} onClick={(e)=>{e.stopPropagation();setShowSpeedMenu(false);setShowSubtitleMenu(!showSubtitleMenu);}} style={{padding:'8px',background:'none',border:'none',cursor:'pointer',color:activeSubtitleKey!=='off'?colors.accent:'#fff'}}><svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M7 12h4M13 12h4M7 16h10" /></svg></button>
                      {subtitleMenuEl}
                    </div>
                  )}
                  <button aria-label="Picture-in-Picture" onClick={(e) => { e.stopPropagation(); togglePiP(); }} style={{ padding: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#fff' }}><svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" /><rect x="11" y="9" width="9" height="6" rx="1" fill="currentColor" stroke="none" /></svg></button>
                  <button aria-label={isFullscreen?'Exit full screen':'Full screen'} onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }} style={{ padding: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#fff' }}>
                    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{isFullscreen ? <path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3" /> : <path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3" />}</svg>
                  </button>
                </div>
              </div>
) : compactRow}
            </div>
          </div>
        </>}

        <style>{`
          @keyframes pulse{0%,100%{box-shadow:0 8px 32px rgba(0,0,0,0.4),0 0 0 0 rgba(255,255,255,0.4)}50%{box-shadow:0 8px 32px rgba(0,0,0,0.4),0 0 0 12px rgba(255,255,255,0)}}
          @keyframes spin{to{transform:rotate(360deg)}}
          .video-js{background-color:transparent!important}
          .video-js video,.video-js .vjs-tech{object-fit:contain}
          .video-js .vjs-control-bar,.video-js .vjs-big-play-button,.video-js .vjs-loading-spinner,.video-js .vjs-error-display,.video-js .vjs-poster,.video-js .vjs-text-track-display{display:none!important}
        `}</style>
      </div>
      {/* Shortcut sheet (1.25): ? opens it; Esc, ? or a click closes it. */}
      {showShortcuts && (
        <div role="dialog" aria-label="Keyboard shortcuts" onClick={(e) => { e.stopPropagation(); setShowShortcuts(false); }}
          style={{ position: 'absolute', inset: 0, zIndex: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', fontFamily: '"DM Sans",sans-serif' }}>
          <div style={{ minWidth: '300px', maxWidth: '90%', padding: '18px 22px', borderRadius: '14px', background: 'rgba(20,20,20,0.97)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}>
            <div style={{ fontFamily: '"Cormorant Garamond",Georgia,serif', fontSize: '22px', marginBottom: '10px' }}>Keyboard shortcuts</div>
            {[['Space / K', 'Play or pause'], ['J / L', 'Back or forward 10 s'], ['← / →', 'Back or forward (while playing)'], ['< / >', 'Slower or faster'], ['C', 'Subtitles on or off'], ['0–9', 'Jump to 0–90%'], ['M', 'Mute'], ['F', 'Full screen'], ['?', 'This list']].map(([k, d]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', padding: '5px 0', fontSize: '13.5px', color: 'rgba(255,255,255,0.85)' }}>
                <span>{d}</span>
                <kbd style={{ padding: '2px 8px', borderRadius: '6px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', fontFamily: 'inherit', fontWeight: 700, color: '#fff' }}>{k}</kbd>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* End of lesson (1.23): Up next / Unlock / Lesson complete, over the video. */}
      {ended && endCardEnabled && !nativeFs && <EndOfLessonCard colors={colors} upNext={upNext} upNextLocked={upNextLocked} info={endCard} onUpNext={onUpNext} onUnlock={onUnlock} onReplay={replay} />}
    </div>
  );
}

// ════════════════ Tablet player · moved as is from VideoViewPageFairPlayTablet.jsx (MobilePlayer) ════════════════

const PLAYBACK_SPEEDSTablet = [0.5, 0.75, 1, 1.25, 1.5, 2];

// ============================================================================
// FAIRPLAY VIA NATIVE HLS  (Video.js + videojs-contrib-eme)
// ============================================================================
// FairPlay on Safari runs through NATIVE HLS (src=), where Safari's AVFoundation
// demuxes the separate audio + video tracks and fires a webkitneedkey PER TRACK.
// videojs-contrib-eme's legacy path (initLegacyFairplay) creates a key session
// per event and calls getLicense per track — which is exactly what Shaka's single
// MSE session could not do (shaka-player#9268: "the same assets play correctly in
// Video.js and other HTML5 players"). That per-track keying is the whole fix.
//
// THE ONE TRAP: firstWebkitneedkeyTimeout (default 1000ms) debounces the first
// webkitneedkey to swallow Safari's startup rendition-switch duplicates — but it
// is track-blind and can collapse the audio+video events into one, re-creating the
// silent-audio bug. We set it to 1 (NOT 0 — see the note at the src() call) so
// BOTH tracks get keyed.

// SPC (Uint8Array/ArrayBuffer) -> standard base64 for the DRMtoday license POST.
function toBase64Tablet(input) {
  const bytes = input instanceof Uint8Array ? input
    : input instanceof ArrayBuffer ? new Uint8Array(input)
    : new Uint8Array(input && input.buffer ? input.buffer : input);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary);
}

// base64 CKC -> ArrayBuffer for the CDM.
function fromBase64Tablet(b64) {
  const binary = window.atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

// Replacement for shaka.util.StringUtils.fromBytesAutoDetect (utf-8, then raw).
function fromBytesAutoDetectTablet(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  try { return new TextDecoder('utf-8').decode(u8); }
  catch (e) { let s = ''; for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]); return s; }
}

// ============================================================================
// TIER-SPECIFIC STYLE HELPERS
// ============================================================================
const getSkipButtonStylesTablet = (currentTier, isHovered, isMobile) => {
  const base = { position:'relative', borderRadius:isMobile?'16px':'20px', cursor:'pointer', display:'flex', alignItems:'center', gap:isMobile?'5px':'8px', padding:isMobile?'7px 12px':'12px 20px', transition:'all 0.4s cubic-bezier(0.4,0,0.2,1)', overflow:'hidden', border:'none', fontFamily:'"DM Sans",sans-serif', WebkitTapHighlightColor:'transparent' };
  if (tierRank(currentTier) === 0) return { ...base, background:isHovered?'linear-gradient(135deg,rgba(26,26,26,0.5),rgba(42,42,42,0.45))':'linear-gradient(135deg,rgba(26,26,26,0.4),rgba(42,42,42,0.35))', border:`1px solid ${isHovered?'rgba(255,255,255,0.3)':'rgba(255,255,255,0.2)'}`, boxShadow:isHovered?'0 4px 16px rgba(0,0,0,0.4),inset 0 1px 0 rgba(255,255,255,0.2)':'0 2px 8px rgba(0,0,0,0.3),inset 0 1px 0 rgba(255,255,255,0.15)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
  if (tierRank(currentTier) === 1) return { ...base, background:isHovered?'linear-gradient(135deg,rgba(201,169,98,0.35),rgba(201,169,98,0.25))':'linear-gradient(135deg,rgba(201,169,98,0.25),rgba(201,169,98,0.15))', border:`1px solid ${isHovered?'rgba(201,169,98,0.6)':'rgba(201,169,98,0.4)'}`, boxShadow:isHovered?'0 6px 24px rgba(0,0,0,0.4),0 0 36px rgba(201,169,98,0.4),inset 0 1px 0 rgba(201,169,98,0.5)':'0 3px 12px rgba(0,0,0,0.3),0 0 20px rgba(201,169,98,0.18),inset 0 1px 0 rgba(201,169,98,0.3)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
  return { ...base, background:isHovered?'linear-gradient(135deg,rgba(232,213,163,0.4),rgba(232,213,163,0.3))':'linear-gradient(135deg,rgba(232,213,163,0.3),rgba(232,213,163,0.2))', border:`1px solid ${isHovered?'rgba(232,213,163,0.7)':'rgba(232,213,163,0.5)'}`, boxShadow:isHovered?'0 8px 32px rgba(0,0,0,0.5),0 0 44px rgba(232,213,163,0.45),inset 0 1px 0 rgba(232,213,163,0.6)':'0 4px 16px rgba(0,0,0,0.4),0 0 24px rgba(232,213,163,0.24),inset 0 1px 0 rgba(232,213,163,0.4)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
};
const getSkipIconColorTablet = (t,h) => tierRank(t)===0?'#FFF':tierRank(t)===1?(h?'#E8D5A3':'#C9A962'):(h?'#FFF8E7':'#E8D5A3');
const getSkipNumberColorTablet = (t) => tierRank(t)===0?'#FFF':tierRank(t)===1?'#C9A962':'#E8D5A3';
const getProgressStylesTablet = (t) => {
  if (tierRank(t)===0) return { played:{background:'#8B8B7A',boxShadow:'0 0 8px rgba(139,139,122,0.4)'}, thumb:{background:'#8B8B7A',boxShadow:'0 0 8px rgba(139,139,122,0.5)'} };
  if (tierRank(t)===1) return { played:{background:'linear-gradient(90deg,#C9A962,#E8D5A3)',boxShadow:'0 0 12px rgba(201,169,98,0.5)'}, thumb:{background:'linear-gradient(135deg,#E8D5A3,#C9A962)',boxShadow:'0 0 12px rgba(201,169,98,0.6)'} };
  return { played:{background:'linear-gradient(90deg,#E8D5A3,#FFF8E7)',boxShadow:'0 0 16px rgba(232,213,163,0.6)'}, thumb:{background:'linear-gradient(135deg,#FFF8E7,#E8D5A3)',boxShadow:'0 0 16px rgba(232,213,163,0.7)'} };
};

// ============================================================================
// SKIP BUTTON - Pill-shaped with tier styling
// ============================================================================
function SkipButtonTablet({ direction, onClick, currentTier, isMobile, seconds=15, big=false }) {
  const [isHovered, setIsHovered] = useState(false);
  // big: the tablet centre cluster, a full 44px touch target
  const styles = { ...getSkipButtonStylesTablet(currentTier, isHovered, isMobile), ...(big ? { minHeight:'44px', padding:'0 14px', borderRadius:'18px', gap:'6px' } : {}) };
  const iconColor = getSkipIconColorTablet(currentTier, isHovered);
  const numberColor = getSkipNumberColorTablet(currentTier);
  const iconSize = big?14:isMobile?13:18, numberSize = big?14:isMobile?12:16, unitSize = big?10:isMobile?9:12;
  const iconFilter = tierRank(currentTier)===0?'drop-shadow(0 1px 3px rgba(0,0,0,0.5))':tierRank(currentTier)===1?(isHovered?'drop-shadow(0 0 8px rgba(201,169,98,0.5))':'drop-shadow(0 0 4px rgba(201,169,98,0.3))'):(isHovered?'drop-shadow(0 0 12px rgba(232,213,163,0.6))':'drop-shadow(0 0 6px rgba(232,213,163,0.4))');
  return (
    <button onClick={onClick} onMouseEnter={()=>setIsHovered(true)} onMouseLeave={()=>setIsHovered(false)} style={styles}>
      {direction==='back' && <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{filter:iconFilter,transition:'all 0.3s ease'}}><path d="M19 12H5M12 5l-7 7 7 7"/></svg>}
      <span style={{display:'flex',alignItems:'baseline',gap:'2px',color:'#F5F5F0',textShadow:isLightTheme(currentTier)?'0 1px 3px rgba(0,0,0,0.5)':'0 1px 2px rgba(0,0,0,0.3)'}}>
        <span style={{fontSize:`${numberSize}px`,fontWeight:'700',color:numberColor,letterSpacing:'-0.02em'}}>{seconds}</span>
        <span style={{fontSize:`${unitSize}px`,fontWeight:'500',opacity:0.8}}>s</span>
      </span>
      {direction==='forward' && <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{filter:iconFilter,transition:'all 0.3s ease'}}><path d="M5 12h14M12 5l7 7-7 7"/></svg>}
    </button>
  );
}

// ============================================================================
// VIDEO PLAYER COMPONENT
// ============================================================================
// ┌───────────────────────────────────────────────────────────────────────────┐
// │ TEST HOOK — pin playback to ONE rung of the ladder.                       │
// │   ?forceVariant=v0   on the PAGE url → play v0/video.m3u8 directly        │
// │   ?forceVariant=v3                   → play v3/video.m3u8 directly        │
// │   (absent)                           → normal, master.m3u8               │
// └───────────────────────────────────────────────────────────────────────────┘
// In native HLS Safari picks the variant, so pinning a rung means handing the
// player a MEDIA playlist instead of the MASTER. The CDN token is prefix-scoped
// to videos/<slug>, so it already authorizes v0/video.m3u8 and its segments.
// ── MUXED FAIRPLAY PACKAGE ───────────────────────────────────────────────────
// Safari gets the single-track SAMPLE-AES TS package instead of the demuxed cbcs
// fMP4 one:
//     videos/<slug>/main-video/master.m3u8         (Widevine — Chrome keeps this)
//     videos/<slug>/main-video-single/prog_index.m3u8   (FairPlay — muxed)
//
// Same lesson prefix, so the SAME ?t= token authorizes it: no API, DB or Worker
// change. The Worker is already path-agnostic (.ts → video/mp2t; the EXT-X-MEDIA
// DEFAULT=YES fix only matches TYPE=AUDIO lines, which a muxed package has none of).
//
// Chrome is unaffected — this file is only loaded when player === 'videojs'.
//
// GATING: 'all' once the whole catalogue is packaged; 'list' while rolling out
// (only slugs in MUXED_SLUGS are swapped); 'off' to fall back to the demuxed
// cbcs fMP4 package everywhere. An unpackaged lesson under 'all' will 404 on the
// manifest request — check the network tab for the rewritten URL.
const MUXED_MODETablet = 'all';          // 'all' | 'list' | 'off'
const MUXED_SLUGSTablet = [];            // used only when MUXED_MODE === 'list'

// The package layout written by mux-fairplay-all.sh:
//     videos/<slug>/main-video-fairplay/master.m3u8      <- multi-rung
//     videos/<slug>/main-video-fairplay/v0/prog_index.m3u8
// If the multi-rung master ever misbehaves, set MUXED_INDEX to
// 'v0/prog_index.m3u8' to pin the single proven rendition — no repackaging.
const MUXED_SUBDIRTablet = 'main-video-fairplay';
const MUXED_INDEXTablet = 'master.m3u8';

function toMuxedPackageTablet(url) {
  if (!url || MUXED_MODETablet === 'off') return url;
  const m = String(url).match(/\/videos\/([^/]+)\/main-video\/master\.m3u8/);
  if (!m) return url;                                     // answer-key etc. untouched
  if (MUXED_MODETablet === 'list' && MUXED_SLUGSTablet.indexOf(m[1]) < 0) return url;
  const out = String(url).replace('/main-video/master.m3u8',
                                  '/' + MUXED_SUBDIRTablet + '/' + MUXED_INDEXTablet);
  return out;
}

// ── skd content-id extraction ────────────────────────────────────────────────
// videojs-contrib-eme hands getContentId the initData as a String, but it can
// arrive either clean ("skd://drmtoday?...") or in the legacy length-prefixed
// UTF-16LE shape. Handle both and always return the skd URI.
function parseSkdInitDataTablet(initData) {
  if (typeof initData === 'string') {
    const idx = initData.indexOf('skd:');
    if (idx >= 0) return { uri: initData.slice(idx).replace(/\u0000/g, ''), shape: 'string' };
    return { uri: initData, shape: 'string_raw' };
  }
  const u8 = initData instanceof Uint8Array ? initData : new Uint8Array(initData);
  if (u8.byteLength >= 6) {
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    const byteLen = dv.getUint32(0, true);
    if (byteLen + 4 === u8.byteLength && byteLen % 2 === 0) {
      const uri = new TextDecoder('utf-16le').decode(new Uint8Array(u8.buffer, u8.byteOffset + 4, byteLen));
      if (uri.startsWith('skd:')) return { uri, shape: 'legacy_len_utf16le' };
    }
  }
  return { uri: fromBytesAutoDetectTablet(u8), shape: 'plain' };
}

const fmtTimeTablet = (s) => {
  if (!s || isNaN(s)) return '0:00';
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
};

// ── constants the mobile player expects ──────────────────────────────────────
// If the media never reports ready, stop showing a spinner forever and let the
// user press play themselves. A stuck `isLoading` used to hide the controls
// behind an overlay with no way out.
const LOADING_GIVEUP_MSTablet = 15000;

// +/-10s on mobile (the desktop page uses 15).
const SKIP_SECONDSTablet = 10;

// ============================================================================
// PLAYER — copied verbatim from VideoViewPageFairPlayMobile.jsx
// ============================================================================

// ── PICTURE IN PICTURE BUTTON (tablet) ───────────────────────────────────────
// OFF for now, to be worked on later. false = the tablet option row has no
// Picture in Picture button. Only our button is hidden: nothing is blocked in
// the browser. The code behind it (togglePiP) stays, so true brings it back.
const PIP_BUTTON_ENABLED = false;

function MobilePlayerTablet({
  video, colors, currentTier, isPlaying, setIsPlaying, currentTime, setCurrentTime,
  videoUrl: rawVideoUrl, videoId, assetId, resumeFrom = 0,
  upNext = null, upNextLocked = false, onUpNext, onUnlock, endCard = null, refreshLink,
}) {
  // Muxed package swap: the FairPlay path plays the single-track SAMPLE-AES TS
  // package, not the demuxed cbcs fMP4 one. The desktop page also layers
  // ?forceVariant on top of this; that diagnostic hook is deliberately not
  // carried over here.
  const videoUrl = useMemo(() => toMuxedPackageTablet(rawVideoUrl), [rawVideoUrl]);

  const containerRef = useRef(null);   // fullscreen target
  const mediaHostRef = useRef(null);   // div Video.js mounts into (React owns this)
  const mediaRef = useRef(null);       // raw <video> (webkitEnterFullscreen)
  const vjsRef = useRef(null);
  const progressRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const { token, profile, api } = useAuth();
  const authTokenRef = useRef(token);
  authTokenRef.current = token;

  const API_CONTENT_BASE = process.env.REACT_APP_CONTENT_API_URL || process.env.REACT_APP_API_URL || '';
  const DRMTODAY_MERCHANT = process.env.REACT_APP_DRMTODAY_MERCHANT || 'f100090a-e116-42ba-b03a-1d157c6be3f3';

  const latestRef = useRef({ pos: 0, dur: 0 });
  const resumedRef = useRef(false);
  const resumeFromRef = useRef(resumeFrom);
  // Link refresh, step 2: the link this player's source was last set from, how to build a source for a link
  // (set by the load effect, which owns the FairPlay callbacks), the latest link prop for the player's own
  // event handlers, the reload function for the error handler, and the overlay state.
  const loadedUrlRef = useRef(null);
  const sourceForRef = useRef(null);
  const videoUrlRef = useRef(null);
  videoUrlRef.current = videoUrl;
  const rebuildFromFreshLinkRef = useRef(null);
  const [linkOverlay, setLinkOverlay] = useState(null);   // null | 'loading' | 'resuming' | 'ready' ('resuming' = the Resume tap's own reload)
  const resumingRef = useRef(false);   // the Resume tap's reload is in progress: its own play() must not close the overlay

  // Heartbeats (the 10-second send while playing, and the final send on unmount) live in useHeartbeat.
  const heartbeatOnTime = useHeartbeat(videoId);

  const [showControls, setShowControls] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Saved speed (1.22): start from the profile's preferredPlaybackSpeed when it is one of our speeds, else 1×.
  const [playbackSpeed, setPlaybackSpeed] = useState(() => { const s = Number(profile && profile.preferredPlaybackSpeed); return PLAYBACK_SPEEDSTablet.includes(s) ? s : 1; });
  const [showSpeedSheet, setShowSpeedSheet] = useState(false);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isLoading, setIsLoading] = useState(!!videoUrl);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!error) { setShowError(false); return; }
    const t = setTimeout(() => setShowError(true), 600);
    return () => clearTimeout(t);
  }, [error]);

  // Give up on the spinner rather than covering the player forever.
  useEffect(() => {
    if (!isLoading) return undefined;
    const t = setTimeout(() => setIsLoading(false), LOADING_GIVEUP_MSTablet);
    return () => clearTimeout(t);
  }, [isLoading]);

  const isHls = videoUrl && /\.m3u8($|\?)/.test(videoUrl);
  const videoUrlKey = videoUrl ? String(videoUrl).split('?')[0] : null;

  const totalSeconds = duration || 0;
  // Scrubbing (touch): while a finger drags the bar, the thumb and time follow it without
  // seeking; the video seeks once, on release (scrubTo / endScrub, beside seekTo).
  const [scrubTime, setScrubTime] = useState(null);
  const scrubRef = useRef(null);
  const pct = totalSeconds > 0 ? ((scrubTime ?? currentTime) / totalSeconds) * 100 : 0;
  const bufPct = totalSeconds > 0 ? (buffered / totalSeconds) * 100 : 0;
  const pStyles = getProgressStylesTablet(currentTier);

  // ── Video.js + FairPlay (native HLS) setup ────────────────────────────────
  useEffect(() => {
    if (!videoUrl || !mediaHostRef.current) return;
    let cancelled = false;

    // Create the media element IMPERATIVELY and append it, so Video.js can own and
    // dispose it without fighting React's reconciler (React only owns the host div).
    const videoEl = document.createElement('video');
    videoEl.className = 'video-js vjs-tech';
    videoEl.setAttribute('playsinline', '');
    applyAirplayPolicy(videoEl);
    videoEl.playsInline = true;
    videoEl.preload = 'metadata';
    videoEl.style.width = '100%';
    videoEl.style.height = '100%';
    videoEl.style.objectFit = 'contain';
    mediaHostRef.current.appendChild(videoEl);
    mediaRef.current = videoEl;

    const player = videojs(videoEl, {
      controls: false, autoplay: false, preload: 'metadata',
      playsinline: true, fill: true, fluid: false,
      bigPlayButton: false, loadingSpinner: false, errorDisplay: false, controlBar: false,
      // overrideNative:false → Safari plays HLS natively → webkitneedkey per track (the FairPlay path).
      html5: { vhs: { overrideNative: false }, nativeAudioTracks: true, nativeVideoTracks: true },
    });
    vjsRef.current = player;

    try {
      const rootEl = player.el();
      rootEl.style.position = 'absolute'; rootEl.style.inset = '0';
      rootEl.style.width = '100%'; rootEl.style.height = '100%';
    } catch (e) { /* non-fatal */ }

    // ── player state wiring (replaces the old <video> JSX handlers) ──────────
    player.on('loadedmetadata', () => {
      if (cancelled) return;
      const d = player.duration() || 0;
      setDuration(d); setIsLoading(false); setError(null);
      if (!resumedRef.current) {
        const r = resumeFromRef.current;
        if (r > 0 && r < d) { player.currentTime(r); setCurrentTime(r); }
        resumedRef.current = true;
      }
    });
    player.on('timeupdate', () => {
      const t = player.currentTime();
      setCurrentTime(t);
      const d = player.duration() || 0;
      latestRef.current = { pos: t, dur: d };
      heartbeatOnTime(t, d, player.playbackRate(), !player.paused() && !player.seeking());
    });
    player.on('seeking', () => heartbeatOnTime.seeking());
    player.on('progress', () => { try { setBuffered(player.bufferedEnd()); } catch (e) {} });
    player.on('waiting', () => setIsLoading(true));
    player.on('canplay', () => { setIsLoading(false); setError(null); });
    player.on('playing', () => { setIsLoading(false); setError(null); });
    player.on('ended', () => setIsPlaying(false));
    // play/pause events only REPORT what the video is doing: they copy its real state, never the event's name.
    // A late event (a 'pause' that arrives while the video plays, or a 'play' while it is paused) then changes
    // nothing, so it can't make the play effect send the opposite command (the play/pause loop, October 11).
    player.on('play', () => setIsPlaying(!player.paused()));
    player.on('pause', () => setIsPlaying(!player.paused()));
    player.on('error', () => {
      const e = player.error();
      // MEDIA_ERR codes: 1 ABORTED 2 NETWORK 3 DECODE 4 SRC_NOT_SUPPORTED 5 ENCRYPTED
      const code = e && e.code;
      // Link refresh: the stream died (e.g. its link ran out mid-play) and a fresher link has arrived: reload
      // from it at the spot, with the overlay, instead of failing. A second failure on the fresh link falls through.
      if (code && rebuildFromFreshLinkRef.current && rebuildFromFreshLinkRef.current(true)) return;
      if (code === 4 || code === 5) setError('You don’t have access to this video');
      else if (code) {
        // The overlay, not the error screen, wherever there is a spot to come back to (October 30, 2026): the tap
        // reloads the source from a fresh session token and a fresh link.
        resumeFromRef.current = latestRef.current.pos || resumeFromRef.current || 0; setIsPlaying(false); setLinkOverlay('ready');   // the saved spot stays if nothing has played yet
      }
      setIsLoading(false);
    });

    // ── source + FairPlay key system ─────────────────────────────────────────
    if (!isHls) {
      // Non-DRM / non-HLS preview: plain source, no key system.
      player.src({ src: videoUrl });
      sourceForRef.current = (u) => ({ src: u });
    } else {
      // FairPlay certificate — same override hooks as the prior investigation:
      //   sessionStorage.fpsCertB64  or  ?certUrl=<url>   (no param = DRMtoday default)
      let certOverride = null;
      try {
        const b64 = window.sessionStorage.getItem('fpsCertB64');
        if (b64) { certOverride = 'data:application/octet-stream;base64,' + b64.trim(); }
        else {
          const u = new URLSearchParams(window.location.search).get('certUrl');
          if (u) { certOverride = u; }
        }
      } catch (e) { /* default */ }
      const DRMTODAY_FPS_CERT = certOverride
        || `https://lic.drmtoday.com/license-server-fairplay/cert/${DRMTODAY_MERCHANT}`;

      const licenseBase = (API_CONTENT_BASE || '').replace(/\/$/, '');
      const fairplayLicense = `${licenseBase}/license/fairplay?assetId=${encodeURIComponent(assetId || '')}`;

      const getCertificate = (emeOptions, callback) => {
        fetch(DRMTODAY_FPS_CERT, { method: 'GET' })
          .then((r) => { if (!r.ok) throw new Error('cert HTTP ' + r.status); return r.arrayBuffer(); })
          .then((buf) => { callback(null, new Uint8Array(buf)); })
          .catch((err) => { callback(err); });
      };

      // CONTENT-ID: strip ONLY variantId. DRMtoday returns LIC_KEYS_NOT_FOUND when
      // variantId is present (keys were ingested without it) — see the matching note
      // in the Shaka page.
      //
      // STALE AS OF 2026-07-22 — kept as history, do not act on it:
      //   "DO NOT also strip &trk=audio. The CDN Worker deliberately injects that tag
      //    on the audio rendition's #EXT-X-KEY so the audio track's content-id differs
      //    from video's."
      // The Worker REMOVED that injection (see rewriteManifest), on the grounds that
      // every playlist carries one identical key, so the tag invented a content-id the
      // packager never wrote. No manifest emits &trk= any more; the [^&]* strip below
      // is a no-op against it either way, so this code needs no change.
      //
      // What DOES follow from the removal: both tracks now request the SAME content-id
      // again — the exact condition the tag existed to avoid. If playback ever freezes
      // with a decoded first frame and currentTime pinned at 0, that is the audio track
      // never being keyed.
      //
      // The durable fix is per-track key URIs at packaging time; shaka-packager's
      // --hls_key_uri is a single global flag (#439 / #470), which is why neither the
      // player nor the Worker has been able to solve this cleanly.
      const getContentId = (emeOptions, initData) => {
        const { uri } = parseSkdInitDataTablet(initData);
        const contentId = uri.replace(/[?&]variantId=[^&]*/i, '');
        return contentId;
      };

      const getLicense = (emeOptions, contentId, keyMessage, callback) => {
        const spcB64 = toBase64Tablet(keyMessage);
        const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
        const authToken = authTokenRef.current;
        if (authToken) headers['Authorization'] = 'Bearer ' + authToken;
        fetch(fairplayLicense, { method: 'POST', headers, body: 'spc=' + encodeURIComponent(spcB64) })
          .then(async (r) => {
            const text = await r.text();
            if (!r.ok) throw new Error('license HTTP ' + r.status);
            let txt = text.trim();
            const wrapped = txt.startsWith('<ckc>') && txt.endsWith('</ckc>');
            if (wrapped) txt = txt.slice(5, -6);
            callback(null, fromBase64Tablet(txt));
          })
          .catch((err) => { callback(err); });
      };

      player.eme();
      // ── THE REAL FIX: 1, NOT 0. ────────────────────────────────────────────
      // videojs-contrib-eme reads this as:
      //     getOptions(player).firstWebkitneedkeyTimeout || 1000
      // so a literal 0 is FALSY and silently becomes 1000ms. That 1s window is a
      // cancel-and-reschedule (last-event-wins) debounce: the VIDEO needkey gets
      // clearTimeout'd by the AUDIO needkey ~330ms later, so the video key is
      // DROPPED and only re-requested by Safari's own ~20s retry.
      // 1ms survives the `||`, fires effectively immediately, and flips
      // handledFirstEvent=true so every later needkey takes the immediate path.
      // DO NOT "clean this up" back to 0.
      try { player.eme.options = { ...(player.eme.options || {}), firstWebkitneedkeyTimeout: 1 }; } catch (e) {}
      if (player.eme.initLegacyFairplay) player.eme.initLegacyFairplay();

      player.src({
        src: videoUrl,
        type: 'application/x-mpegurl',
        firstWebkitneedkeyTimeout: 1,  // see note above — 1, never 0 (|| 1000 trap)
        keySystems: { 'com.apple.fps.1_0': { getCertificate, getContentId, getLicense } },
      });
      sourceForRef.current = (u) => ({ src: u, type: 'application/x-mpegurl', firstWebkitneedkeyTimeout: 1, keySystems: { 'com.apple.fps.1_0': { getCertificate, getContentId, getLicense } } });
    }
    loadedUrlRef.current = videoUrl;   // link refresh: the link this player loaded

    return () => {
      cancelled = true;
      const p = vjsRef.current;
      if (p && !p.isDisposed()) { try { p.dispose(); } catch (e) {} }
      vjsRef.current = null;
      mediaRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrlKey, isHls, assetId, API_CONTENT_BASE]);

  // -- Play/pause (driven through the Video.js player) --
  // AbortError-aware: ONLY NotAllowedError triggers a muted retry, because only that
  // one means an autoplay block. An AbortError usually means play() was interrupted
  // mid-load (a late FairPlay key, a source swap) — retrying muted there compounds the
  // abort and costs the user their audio for no reason, so we leave it paused and let
  // the recovery guard below re-attempt once the media is genuinely playable.
  // ── Starting playback (fix: "stuck after the next lesson", "the next lesson is muted") ─────────────────
  // A refused autoplay and an interrupted play are different things:
  //   NotAllowedError · the browser refuses sound without a tap (always so on iOS after an automatic lesson change).
  //     Start muted, which every browser allows, but only temporarily: "Tap for sound" shows, and the first tap,
  //     click or key press anywhere restores the sound, inside that tap (what iOS requires).
  //   anything else (AbortError: interrupted, e.g. by the stream still loading or a late FairPlay key) · never mute;
  //     show paused at once, with the play button; the student taps again (October 11: the retries are removed).
  const [autoMuted, setAutoMuted] = useState(false);
  useEffect(() => {
    const player = vjsRef.current;
    if (!videoUrl || !player) return undefined;
    if (isPlaying) {
      let cancelled = false;
      const attempt = () => {
        if (cancelled || (typeof player.isDisposed === 'function' && player.isDisposed() === true)) return;
        const p = player.play();
        if (!p || !p.then) return;
        p.catch((err) => {
          if (cancelled) return;
          const name = err && err.name;
          if (name === 'NotAllowedError') {
            if (player.muted()) { setIsPlaying(false); return; }   // refused even muted: show paused
            player.muted(true); setIsMuted(true); setAutoMuted(true);
            const p2 = player.play();
            if (p2 && p2.then) p2.catch(() => { if (!cancelled) setIsPlaying(false); });
          } else {
            // AbortError (a late FairPlay key, a source swap): no retry; show paused, never mute.
            setIsPlaying(false);
          }
        });
      };
      attempt();
      return () => { cancelled = true; };
    }
    player.pause();
    return undefined;
  }, [isPlaying, videoUrl, setIsPlaying]);
  useEffect(() => {
    if (!autoMuted) return undefined;
    // a tap on the player itself only restores the sound (it must not also pause or play); elsewhere it carries on.
    // M also means "restore the sound" here: the player's own M toggle would otherwise flip it straight back to muted.
    const restore = (e) => { const p = vjsRef.current; if (p) p.muted(false); setIsMuted(false); setAutoMuted(false); if (e && e.type !== 'keydown' && containerRef.current && e.target && containerRef.current.contains(e.target)) { e.stopPropagation(); e.preventDefault(); } if (e && e.type === 'keydown' && (e.key === 'm' || e.key === 'M')) { e.stopImmediatePropagation(); e.preventDefault(); } };
    const o = { capture: true };
    window.addEventListener('touchend', restore, o); window.addEventListener('click', restore, o); window.addEventListener('keydown', restore, o);
    return () => { window.removeEventListener('touchend', restore, o); window.removeEventListener('click', restore, o); window.removeEventListener('keydown', restore, o); };
  }, [autoMuted]);
  useEffect(() => { if (!isMuted) setAutoMuted(false); }, [isMuted]);
  // "Tap for sound": big and centred for SOUND_PROMPT_BIG_MS, then the small corner pill until the sound is on.
  // Display only: a tap anywhere restores the sound either way (the effect above).
  const [soundPromptBig, setSoundPromptBig] = useState(false);
  useEffect(() => {
    if (!autoMuted) { setSoundPromptBig(false); return undefined; }
    setSoundPromptBig(true);
    const t = setTimeout(() => setSoundPromptBig(false), SOUND_PROMPT_BIG_MS);
    return () => clearTimeout(t);
  }, [autoMuted]);

  // -- Recovery guard: re-attempt play once the media actually becomes playable. --
  // If a FairPlay key lands late, autoplay's play() can reject with AbortError while
  // the pipeline has no video key; without this the player then sits frozen at 0
  // forever even though decryption later succeeds. One-shot per source, and it only
  // fires if the user still intends to be playing.
  const replayGuardRef = useRef(false);
  const isPlayingRef = useRef(false);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { replayGuardRef.current = false; }, [videoUrlKey]);
  useEffect(() => {
    const player = vjsRef.current;
    if (!videoUrl || !player) return;
    const onReady = () => {
      const v = mediaRef.current;
      if (replayGuardRef.current) return;
      if (!isPlayingRef.current) return;          // user paused on purpose — respect it
      if (!v || v.videoWidth > 0) return;         // already decoding; nothing to recover
      replayGuardRef.current = true;
      const p = player.play();
      if (p && p.catch) p.catch(() => {});
    };
    player.on('canplaythrough', onReady);
    return () => { try { player.off('canplaythrough', onReady); } catch (e) {} };
  }, [videoUrl, videoUrlKey]);

  // TOUCH/CLICK DEDUPE — PER CONTROL.
  // iOS fires `touchend` and then synthesises a `click` for the same tap, so any
  // element with both handlers ran its action twice.
  //
  // The first version of this used ONE shared timestamp for every control, which
  // created a worse bug than the one it fixed: while the video plays the controls
  // auto-hide, so reaching any button takes TWO taps in quick succession — one on
  // the tap layer to reveal, one on the button. With a shared guard the second tap
  // fell inside the window and was discarded. Fullscreen and PiP looked completely
  // dead; play/pause only worked if you happened to pause between taps.
  //
  // The guard is now keyed per control, so a tap on the reveal layer can never
  // suppress the next tap on a different button.
  const tapGuards = useRef({});
  const once = useCallback((key, fn) => (e) => {
    // A handled touch cancels the browser's follow-up click (preventDefault), so one tap is
    // one action, and that click can never land on whatever just opened under the finger
    // (a "ghost click": it used to close the speed sheet the moment a tap opened it).
    // Quick repeated taps now all count. Safety net, still keyed per control: a click on
    // the same control within 400 ms of a handled touch is ignored, in case a browser sends one anyway.
    const now = Date.now();
    if (e && e.type === 'touchend') {
      if (e.cancelable && e.preventDefault) e.preventDefault();
      tapGuards.current[key] = now;
    } else if (now - (tapGuards.current[key] || 0) < 400) {
      return;
    }
    if (e && e.stopPropagation) e.stopPropagation();
    fn(e);
  }, []);

  // ── controls auto-hide ───────────────────────────────────────────────────
  const bump = useCallback(() => {
    setShowControls(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (isPlaying) hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3800);
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying) { setShowControls(true); if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); }
    else hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3800);
    return () => { if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); };
  }, [isPlaying]);

  useEffect(() => { if (isPlaying) setHasStarted(true); }, [isPlaying]);

  // WHY A DEDICATED TAP LAYER, AND NOT onClick ON THE CONTAINER:
  // On iOS a tap on a <video> element does not reliably emit a `click` that
  // bubbles to an ancestor — the media element swallows it. Relying on bubbling
  // meant that once the controls hid themselves (pointerEvents:'none'), the tap
  // landed on the video, nothing fired, and the controls could never be brought
  // back. This transparent sibling sits ABOVE the video and BELOW the controls,
  // and its pointerEvents are ALWAYS 'auto'.
  const toggleControls = useCallback(() => {
    if (showControls) { setShowControls(false); if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); }
    else bump();
  }, [showControls, bump]);

  useEffect(() => { const p = vjsRef.current; if (p) { p.muted(isMuted); p.volume(isMuted ? 0 : 1); } }, [isMuted]);
  // Hold for 2× (touch): while the finger is held on the video, play at 2×; on release, back to the chosen speed.
  const [hold, setHold] = useState(false);
  const holdRef = useRef({ timer: null, active: false, x: 0, y: 0 });
  useEffect(() => () => clearTimeout(holdRef.current.timer), []);
  // Media ready: counts each time the media reports its metadata (first load, a new lesson). The
  // speed and the subtitle tracks apply from this, because the Video.js player is created inside
  // the load effect and isLoading can stay false if the player mounts before its URL.
  const [mediaReady, setMediaReady] = useState(0);
  useEffect(() => {
    const p = vjsRef.current;
    if (!p) return undefined;
    const onReady = () => setMediaReady((n) => n + 1);
    p.on('loadedmetadata', onReady);
    return () => { try { p.off('loadedmetadata', onReady); } catch (err) { /* player already disposed */ } };
  }, [videoUrl]);

  // ── End of lesson (1.23) ──────────────────────────────────────────────────
  // The page passes upNext / upNextLocked / onUpNext / onUnlock to the LESSON player only, so the
  // answer-key player never shows the card. The video's end is detected by these new listeners;
  // the existing end handler is untouched.
  const endCardEnabled = typeof onUpNext === 'function' || typeof onUnlock === 'function';
  const [ended, setEnded] = useState(false);
  // Option row (tablet): the player's width decides whether the written labels fit, the two
  // menu buttons are remembered so each menu can open right above the button that was tapped,
  // and Picture in Picture shows only where the browser offers it for this video.
  const speedBtnRef = useRef(null);
  const subsBtnRef = useRef(null);
  const [playerWidth, setPlayerWidth] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => setPlayerWidth(Math.round(el.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [pipAvailable, setPipAvailable] = useState(false);
  useEffect(() => {
    const el = mediaRef.current;
    if (!el) { setPipAvailable(false); return; }
    const webkitOk = typeof el.webkitSupportsPresentationMode === 'function' && typeof el.webkitSetPresentationMode === 'function'
      && el.webkitSupportsPresentationMode('picture-in-picture');
    const standardOk = !!document.pictureInPictureEnabled && typeof el.requestPictureInPicture === 'function' && !el.disablePictureInPicture;
    setPipAvailable(PIP_BUTTON_ENABLED && !!(webkitOk || standardOk));
  }, [videoUrl, mediaReady]);
  const [nativeFs, setNativeFs] = useState(false);     // iPhone's own full-screen player hides our card
  useEffect(() => {
    setEnded(false);
    const p = vjsRef.current;
    if (!p) return undefined;
    const onEnd = () => setEnded(true);
    const onAgain = () => setEnded(false);
    p.on('ended', onEnd); p.on('play', onAgain); p.on('seeking', onAgain);
    return () => { try { p.off('ended', onEnd); p.off('play', onAgain); p.off('seeking', onAgain); } catch (err) { /* player already disposed */ } };
  }, [videoUrlKey]);   // a new video, not a new link (link refresh): a fresh link must not reset the end card
  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return undefined;
    const on = () => setNativeFs(true); const off = () => setNativeFs(false);
    el.addEventListener('webkitbeginfullscreen', on); el.addEventListener('webkitendfullscreen', off);
    return () => { el.removeEventListener('webkitbeginfullscreen', on); el.removeEventListener('webkitendfullscreen', off); };
  }, [videoUrl]);
  // AirPlay (1.28, FairPlay tablet): shown only when Safari reports an AirPlay device nearby.
  const [airplayAvailable, setAirplayAvailable] = useState(false);
  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return undefined;
    const h = (e) => setAirplayAvailable(AIRPLAY_ENABLED && !!e && e.availability === 'available');
    el.addEventListener('webkitplaybacktargetavailabilitychanged', h);
    return () => el.removeEventListener('webkitplaybacktargetavailabilitychanged', h);
  }, [videoUrl, mediaReady]);
  const showAirplayPicker = () => { const el = mediaRef.current; try { if (el && el.webkitShowPlaybackTargetPicker) el.webkitShowPlaybackTargetPicker(); } catch (err) { /* picker unavailable */ } };
  // No automatic advance: the next lesson starts only when the student taps "Play next lesson" (or Up Next),
  // so it always starts from a tap, the way every browser (iPad and iPhone included) lets a new video start.
  // ── Link refresh, step 2: reload the source from a fresher link when the loaded one has run short ──────
  // Runs while paused and not on the end card. The loaded link is short when it has less time than a play from
  // the current spot needs (the page's own arithmetic). `fromError` is the mid-play case: the stream died, so
  // any fresher link is reason enough. Returns whether a reload was started.
  const rebuildFromFreshLink = (fromError) => {
    const p = vjsRef.current; const was = loadedUrlRef.current; const now = videoUrlRef.current;
    if (!p || !was || !now || was === now || !sourceForRef.current) return false;
    const pos = latestRef.current.pos || 0;
    const dur = duration || latestRef.current.dur || 0;
    const leftWas = cdnTokenSecondsLeft(was); const leftNow = cdnTokenSecondsLeft(now);
    if (leftNow !== null && leftWas !== null && leftNow <= leftWas) return false;   // the new link is no fresher
    if (!fromError) {
      const need = Math.max(CDN_TOKEN_MIN_SECONDS, videoNeedSeconds(dur, pos));
      if (leftWas === null || leftWas >= need) return false;                        // the loaded link still has the time
    }
    const silent = !(pos >= 1);                    // never played: a quiet reload, the play button stays
    loadedUrlRef.current = now;
    resumeFromRef.current = pos; resumedRef.current = false;   // the load effect's loadedmetadata seeks back to the spot
    setError(null);
    if (fromError) setIsPlaying(false);
    if (!silent) {
      setLinkOverlay('loading');
      const onReady = () => {
        try { p.off('loadedmetadata', onReady); } catch (err) { /* disposed */ }
        if (loadedUrlRef.current === now) setLinkOverlay('ready');
      };
      p.on('loadedmetadata', onReady);
    }
    try { p.src(sourceForRef.current(now)); } catch (err) { setLinkOverlay(null); return false; }
    return true;
  };
  rebuildFromFreshLinkRef.current = rebuildFromFreshLink;
  useEffect(() => {
    if (isPlaying || ended || linkOverlay) return;
    rebuildFromFreshLink(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl, isPlaying, ended]);
  useEffect(() => { if (isPlaying && linkOverlay && !resumingRef.current) setLinkOverlay(null); }, [isPlaying, linkOverlay]);   // started another way: the overlay is done
  useEffect(() => { setLinkOverlay(null); }, [videoUrlKey]);   // a new lesson starts clean
  // Resume (October 20, 2026): the overlay may have said "Resume lesson" for hours. The tap re-establishes everything
  // at tap time instead of trusting the source rebuilt back then: 1. a fresh session token, 2. a fresh link into page
  // state (through the page's hook, so both players and the PDF links see it), 3. the source reloaded from it at the
  // spot (a new FairPlay key session whose license carries the fresh token), 4. play. A play-then-pause inside the tap
  // captures the activation without playing the old source; the overlay stays up ('resuming') until the reload plays.
  const continueFromLink = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const p = vjsRef.current;
    if (!p) return;
    resumingRef.current = true;
    const first = p.play(); if (first && first.catch) first.catch(() => {});
    p.pause();   // play-then-pause inside the tap: the activation is captured, nothing plays until the reload
    setLinkOverlay('resuming');
    const cur = typeof p.currentTime === 'function' ? p.currentTime() : 0;
    const pos = cur > 0 ? cur : (latestRef.current.pos || resumeFromRef.current || 0);
    const wasLoaded = loadedUrlRef.current;   // put back if the reload fails, so the next fresher link rebuilds
    (async () => {
      try {
        // offline: nothing to refresh
        if (navigator.onLine !== false) await api.refreshToken();
        if (typeof refreshLink === 'function') {
          const before = videoUrlRef.current;
          const r = await refreshLink();
          // the fresh link reaches this player through a render; wait for it (normally one tick, at most 1 s)
          if (r === 'fresh') for (let i = 0; i < 40 && videoUrlRef.current === before; i++) await new Promise((res) => setTimeout(res, 25));
        }
        if (vjsRef.current !== p || !sourceForRef.current) return;   // the lesson changed meanwhile
        const now = videoUrlRef.current;
        const curNow = typeof p.currentTime === 'function' ? p.currentTime() : 0;
        const at = curNow > 0 ? curNow : pos;   // the spot now (the tap's play() may have moved on)
        loadedUrlRef.current = now;   // step 2's own check sees this link as the loaded one
        resumeFromRef.current = at; resumedRef.current = false;   // the load effect's loadedmetadata seeks back to the spot
        setError(null);
        await new Promise((resolve, reject) => {
          let timer = null;
          const done = () => { if (timer) clearTimeout(timer); try { p.off('loadedmetadata', onReady); p.off('error', onFail); } catch (err) { /* disposed */ } };
          const onReady = () => { done(); resolve(); };
          const onFail = () => { done(); reject(new Error('load failed')); };
          timer = setTimeout(onFail, 25000);   // no metadata and no error in 25 s: the error screen, not a spinner forever
          p.on('loadedmetadata', onReady); p.on('error', onFail);
          try { p.src(sourceForRef.current(now)); } catch (err) { onFail(); }
        });
        if (vjsRef.current !== p) return;
        setLinkOverlay(null); setIsLoading(false);
        const again = p.play(); if (again && again.catch) again.catch(() => {});
        setIsPlaying(true);
      } catch (err) {
        if (vjsRef.current !== p) return;
        if (loadedUrlRef.current !== wasLoaded) loadedUrlRef.current = wasLoaded;   // the loaded link is still the old one: step 2 rebuilds on the next fresher link
        resumeFromRef.current = pos;
        setLinkOverlay('ready'); setIsLoading(false);   // back to Resume lesson, never the error screen (October 30, 2026)
        setIsPlaying(false);
      } finally {
        resumingRef.current = false;
      }
    })();
  };
  const replay = () => {
    setEnded(false);
    const p = vjsRef.current;
    if (p) { p.currentTime(0); setCurrentTime(0); }
    setIsPlaying(true);
  };
  useEffect(() => { const p = vjsRef.current; if (p) p.playbackRate(hold ? 2 : playbackSpeed); }, [playbackSpeed, hold, mediaReady]);   // also once the media is ready: a speed chosen before it existed (e.g. a saved one) must still apply

  // ── Subtitles (1.21) ──────────────────────────────────────────────────────
  // Tracks come from the lesson data: video.subtitles = [{ lang, label, url, kind }], kind
  // 'subtitles' or 'captions' (English (CC) keeps its own key, 'en-cc'). With none, the
  // Subtitles control doesn't appear. Tracks are added once per player instance, after
  // the media is ready (mediaReady), by the effect below; never inside the load effect above.
  const subtitleOptions = useMemo(() => (Array.isArray(video && video.subtitles) ? video.subtitles : [])
    .filter((s) => s && s.url && s.lang)
    .map((s) => ({ ...s, kind: s.kind === 'captions' ? 'captions' : 'subtitles', key: s.kind === 'captions' ? s.lang + '-cc' : s.lang })), [video]);
  // Saved subtitles (1.22): start from the profile's preferredSubtitles, else Off. A lesson without
  // that language shows Off (activeSubtitleKey) without changing the saved value.
  const [subtitleKey, setSubtitleKey] = useState(() => (profile && typeof profile.preferredSubtitles === 'string' ? profile.preferredSubtitles : 'off'));
  const activeSubtitleKey = subtitleOptions.some((s) => s.key === subtitleKey) ? subtitleKey : 'off';
  // Saving (1.22) switches itself on: it only saves a field the profile already has (the sign the
  // backend supports it), so no request goes to an endpoint that doesn't exist yet. The in-memory
  // profile is updated too, so the next lesson starts with the choice.
  const savePreference = (field, value) => {
    if (!api || !profile || !Object.prototype.hasOwnProperty.call(profile, field)) return;
    if (profile[field] === value) return;   // unchanged: nothing to save (also keeps React's double-run in development quiet)
    profile[field] = value;
    try {
      const req = api.fetch('/profile/preferences', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }) });
      if (req && req.catch) req.catch(() => { /* a failed save changes nothing on screen */ });
    } catch (err) { /* a failed save changes nothing on screen */ }
  };
  const prefsReadyRef = useRef(false);
  useEffect(() => { if (prefsReadyRef.current) savePreference('preferredPlaybackSpeed', playbackSpeed); }, [playbackSpeed]);
  useEffect(() => { if (prefsReadyRef.current) savePreference('preferredSubtitles', subtitleKey); }, [subtitleKey]);
  useEffect(() => { prefsReadyRef.current = true; }, []);
  const [tracksReady, setTracksReady] = useState(0);
  const subsAddedFor = useRef(null);
  const [showSubtitleSheet, setShowSubtitleSheet] = useState(false);
  useEffect(() => {
    const p = vjsRef.current;
    if (!mediaReady || !p || !subtitleOptions.length || subsAddedFor.current === p) return;
    subsAddedFor.current = p;
    for (const s of subtitleOptions) {
      try { p.addRemoteTextTrack({ kind: s.kind, src: s.url, srclang: s.lang, label: s.label }, false); }
      catch (err) { /* one bad entry must not block the others */ }
    }
    setTracksReady((n) => n + 1);
  }, [mediaReady, subtitleOptions]);
  useEffect(() => {
    const p = vjsRef.current;
    if (!p || !tracksReady) return;
    const opt = subtitleOptions.find((s) => s.key === subtitleKey);
    try {
      const list = p.textTracks();
      for (let i = 0; i < list.length; i++) {
        const t = list[i];
        if (t.kind !== 'subtitles' && t.kind !== 'captions') continue;
        const on = !!opt && t.language === opt.lang && t.kind === opt.kind && (!opt.label || t.label === opt.label);
        t.mode = on ? 'showing' : 'disabled';
      }
    } catch (err) { /* subtitles are optional: never disturb playback */ }
  }, [subtitleKey, tracksReady, subtitleOptions]);

  // Read duration/position from the PLAYER, not React state. `duration` only
  // populates on loadedmetadata and `currentTime` lags a timeupdate behind, so
  // seeking off React state silently no-ops before metadata lands — which is
  // exactly when someone taps skip on a fresh lesson.
  const liveDuration = () => {
    const p = vjsRef.current;
    const d = p ? p.duration() : 0;
    return Number.isFinite(d) && d > 0 ? d : (totalSeconds || 0);
  };
  const livePosition = () => {
    const p = vjsRef.current;
    const t = p ? p.currentTime() : 0;
    return Number.isFinite(t) ? t : (currentTime || 0);
  };

  const seekTo = (clientX) => {
    const p = vjsRef.current;
    const d = liveDuration();
    if (!progressRef.current || !p || d <= 0) return;
    const r = progressRef.current.getBoundingClientRect();
    const t = Math.max(0, Math.min(d, ((clientX - r.left) / r.width) * d));
    p.currentTime(t);
    setCurrentTime(t);
  };

  // Drag on the bar: move the thumb only (no seek), and keep the controls up.
  const scrubTo = (clientX) => {
    const d = liveDuration();
    if (!progressRef.current || d <= 0) return;
    const r = progressRef.current.getBoundingClientRect();
    const t = Math.max(0, Math.min(d, ((clientX - r.left) / r.width) * d));
    scrubRef.current = t;
    setScrubTime(t);
    bump();
  };

  // Release: one seek, to where the finger let go.
  const endScrub = () => {
    const t = scrubRef.current;
    scrubRef.current = null;
    setScrubTime(null);
    if (t === null) return;
    const p = vjsRef.current;
    if (!p) return;
    p.currentTime(t);
    setCurrentTime(t);
  };

  const skip = (s) => {
    const p = vjsRef.current;
    if (!p) return;
    const d = liveDuration();
    const target = livePosition() + s;
    const t = Math.max(0, d > 0 ? Math.min(d - 0.25, target) : target);
    p.currentTime(t);
    setCurrentTime(t);
    bump();
  };

  // ── Lock-screen and headphone controls (1.24) ───────────────────────────
  // The Media Session API. Registered when this player starts playing, and kept after a pause so the
  // lock-screen play button still works; cleared when this player unmounts, if it is still the one
  // registered. Handlers go through a ref, so they always call this render's functions.
  const mediaSessionRef = useRef({});
  mediaSessionRef.current = {
    play: () => setIsPlaying(true),
    pause: () => setIsPlaying(false),
    skip: (s) => skip(s),
    seekTo: (t) => { const p = vjsRef.current; if (p) { p.currentTime(t); setCurrentTime(t); } },
  };
  const mediaSessionOwner = useRef({});
  useEffect(() => {
    if (!isPlaying || typeof navigator === 'undefined' || !navigator.mediaSession) return;
    const ms = navigator.mediaSession;
    try {
      if (typeof window.MediaMetadata === 'function') ms.metadata = new window.MediaMetadata({ title: (video && video.title) || '', artist: (video && video.series && video.series.name) || '', album: 'RoastBrew' });
    } catch (err) { /* metadata is optional */ }
    const set = (a, h) => { try { ms.setActionHandler(a, h); } catch (err) { /* this action isn't supported here */ } };
    set('play', () => mediaSessionRef.current.play());
    set('pause', () => mediaSessionRef.current.pause());
    set('seekbackward', () => mediaSessionRef.current.skip(-10));
    set('seekforward', () => mediaSessionRef.current.skip(10));
    set('seekto', (d) => { if (d && typeof d.seekTime === 'number') mediaSessionRef.current.seekTo(d.seekTime); });
    window.__rbMediaSessionOwner = mediaSessionOwner.current;
  }, [isPlaying, video]);
  useEffect(() => () => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession || window.__rbMediaSessionOwner !== mediaSessionOwner.current) return;
    ['play', 'pause', 'seekbackward', 'seekforward', 'seekto'].forEach((a) => { try { navigator.mediaSession.setActionHandler(a, null); } catch (err) { /* not supported */ } });
    window.__rbMediaSessionOwner = null;
  }, []);

  // Tablet menus open as popovers under the top band (1.28), not as bottom sheets.
  // A menu opens right ABOVE the option button that was tapped (the buttons now live in the
  // bottom row), kept inside the player's left and right edges and no taller than the space
  // above the button. position:fixed, so nothing can clip it.
  const popoverStyle = (anchorRef) => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
    const r = containerRef.current ? containerRef.current.getBoundingClientRect() : { top: 0, left: 0, right: vw, bottom: vh };
    const b = anchorRef && anchorRef.current ? anchorRef.current.getBoundingClientRect() : { top: r.bottom - 56, left: r.left, width: r.right - r.left };
    const W = 220;
    const left = Math.max(r.left + 8, Math.min(b.left + b.width / 2 - W / 2, r.right - W - 8));
    return {
      position: 'fixed', bottom: `${Math.round(vh - b.top + 10)}px`, left: `${Math.round(left)}px`,
      width: `${W}px`, maxHeight: `${Math.max(150, Math.round(b.top - r.top - 18))}px`, overflowY: 'auto', boxSizing: 'border-box',
      padding: '8px', borderRadius: '14px',
      background: colors.cardBg || '#141414',
      border: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)'}`,
      boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
    };
  };

  // Double-tap to skip (touch). A second tap on the same side third within 300 ms skips
  // ±10 s, and further taps keep adding (the ripple shows the running total). A first tap
  // on a side waits 300 ms before showing or hiding the controls, so a second tap can cancel
  // that; a tap in the centre shows or hides them at once.
  const tapRef = useRef({ side: null, at: 0, total: 0, timer: null });
  const [ripple, setRipple] = useState(null);
  const rippleTimerRef = useRef(null);
  useEffect(() => () => { clearTimeout(tapRef.current.timer); clearTimeout(rippleTimerRef.current); }, []);
  const handleSurfaceTap = (e) => {
    const h = holdRef.current;
    clearTimeout(h.timer);
    if (h.active) { h.active = false; setHold(false); return; }   // releasing a hold is not a tap
    const el = e && e.currentTarget;
    const pt = e && e.changedTouches && e.changedTouches[0] ? e.changedTouches[0] : e;
    const tr = tapRef.current;
    if (!el || !pt || pt.clientX == null) { toggleControls(); return; }
    const r = el.getBoundingClientRect();
    const x = (pt.clientX - r.left) / r.width;
    const side = x < 1 / 3 ? 'back' : x > 2 / 3 ? 'fwd' : null;
    const now = Date.now();
    if (!side) { clearTimeout(tr.timer); tr.side = null; toggleControls(); return; }
    if (tr.side === side && now - tr.at < 300) {
      clearTimeout(tr.timer);
      tr.at = now;
      tr.total += SKIP_SECONDSTablet;
      skip(side === 'back' ? -SKIP_SECONDSTablet : SKIP_SECONDSTablet);
      try { if (navigator.vibrate) navigator.vibrate(8); } catch (err) { /* no haptics here */ }   // haptic tick (Android; iPhones ignore it)
      setRipple({ side, total: tr.total });
      clearTimeout(rippleTimerRef.current);
      rippleTimerRef.current = setTimeout(() => setRipple(null), 700);
      return;
    }
    clearTimeout(tr.timer);
    tr.side = side; tr.at = now; tr.total = 0;
    tr.timer = setTimeout(() => { tr.side = null; toggleControls(); }, 300);
  };

  // ── iOS FULLSCREEN ────────────────────────────────────────────────────────
  // Two things were wrong before, both confirmed against Video.js's own
  // tech/html5.js source:
  //
  // 1. CASING. Video.js tests `typeof el.webkitEnterFullScreen === 'function'`
  //    — capital S in "FullScreen". I was calling `webkitEnterFullscreen` and
  //    gating on `webkitSupportsFullscreen`. Safari has exposed both spellings
  //    historically, but the capital-S one is what Video.js relies on, so try
  //    both rather than betting on either.
  //
  // 2. THE PRIMING SEQUENCE. iOS refuses programmatic fullscreen on a video that
  //    has not been primed. Video.js handles it: when the video is paused and
  //    networkState <= HAVE_METADATA it calls play(), then on a setTimeout(0)
  //    pauses and only THEN enters fullscreen — split across a tick, because
  //    doing both synchronously can trap older iOS in a play/pause loop. My
  //    version just called enter and gave up, which is exactly the "nothing
  //    happens" on a fresh lesson.
  //
  // So stop hand-rolling it: player.requestFullscreen() routes through the
  // tech's enterFullScreen() and inherits all of the above. The manual path
  // below is only a fallback if the Video.js player is not available.
  const nativeEnterName = (el) => {
    if (!el) return null;
    if (typeof el.webkitEnterFullScreen === 'function') return 'webkitEnterFullScreen';
    if (typeof el.webkitEnterFullscreen === 'function') return 'webkitEnterFullscreen';
    return null;
  };

  const goFullscreen = useCallback(() => {
    const p = vjsRef.current;
    const el = mediaRef.current;
    const box = containerRef.current;

    // Already fullscreen -> exit. Ask the DOCUMENT first: when our container is
    // the full-screen element, the video's own webkitExitFullScreen does nothing.
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) { const r = document.exitFullscreen(); if (r && r.catch) r.catch(() => {}); }
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      return;
    }
    if (el && el.webkitDisplayingFullscreen) {
      if (p && p.isFullscreen && p.isFullscreen()) { try { p.exitFullscreen(); return; } catch (e) {} }
      if (typeof el.webkitExitFullScreen === 'function') el.webkitExitFullScreen();
      else if (typeof el.webkitExitFullscreen === 'function') el.webkitExitFullscreen();
      return;
    }

    // The platform player: Video.js knows the iOS priming sequence (see above);
    // the manual mirror below it is only for when the Video.js player is missing.
    const platformFullscreen = () => {
      if (p && typeof p.requestFullscreen === 'function') {
        try {
          const r = p.requestFullscreen();
          if (r && typeof r.catch === 'function') r.catch(() => {});
          return;
        } catch (e) { /* fall through */ }
      }
      if (!el) return;
      const method = nativeEnterName(el);
      if (!method) return;
      if (el.paused && el.networkState <= 1 /* HAVE_METADATA */) {
        const pr = el.play();
        if (pr && pr.catch) pr.catch(() => {});
        setTimeout(() => {
          el.pause();
          try { el[method](); } catch (e) { /* iOS refused */ }
        }, 0);
      } else {
        try { el[method](); } catch (e) { /* iOS refused */ }
      }
    };

    // Preferred (iPad): full screen on OUR CONTAINER, so our controls come along.
    // player.requestFullscreen() only reaches Apple's native player where the
    // browser has no element full screen (iPhone). On iPad it full-screens
    // Video.js's own wrapper, and our controls sit outside that wrapper — which
    // is why full screen there showed a bare video with no controls.
    if (box && typeof box.requestFullscreen === 'function') {
      try {
        const r = box.requestFullscreen();
        if (r && typeof r.catch === 'function') r.catch(() => platformFullscreen());
        return;
      } catch (e) { /* fall through */ }
    }
    if (box && typeof box.webkitRequestFullscreen === 'function') {
      try { box.webkitRequestFullscreen(); return; } catch (e) { /* fall through */ }
    }
    platformFullscreen();
  }, []);

  useEffect(() => {
    const h = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', h);
    document.addEventListener('webkitfullscreenchange', h);
    const v = mediaRef.current;
    const ib = () => setIsFullscreen(true); const ie = () => setIsFullscreen(false);
    if (v) { v.addEventListener('webkitbeginfullscreen', ib); v.addEventListener('webkitendfullscreen', ie); }
    return () => {
      document.removeEventListener('fullscreenchange', h);
      document.removeEventListener('webkitfullscreenchange', h);
      if (v) { v.removeEventListener('webkitbeginfullscreen', ib); v.removeEventListener('webkitendfullscreen', ie); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrlKey]);


  // ── OPTION BUTTONS ────────────────────────────────────────────────────────
  // An icon with its name written out, 44px tall: the bottom row (Mute, Speed,
  // Subtitles, Picture in Picture, Full Screen) and AirPlay in the top corner.
  // When the player is too narrow for every label in the row, the row drops to
  // icons only (the name stays as the accessible label). `always` keeps the label.
  // Do the labels fit? Judged on the LONGEST wording each button can show ("Unmute", "1.25x"),
  // so the row never flips between words and icons because of a mute or a speed change.
  // Per button: icon 20 + gap 6 + padding 20, and about 7px a letter at 13px.
  const labelW = (t) => 46 + 7 * t.length;
  const labelsNeed = 24 + labelW('Unmute') + labelW('Speed (1.25x)')
    + (subtitleOptions.length > 0 ? 6 + labelW('Subtitles') : 0)
    + (pipAvailable ? 6 + labelW('Picture in Picture') : 0)
    + labelW(isFullscreen ? 'Exit Full Screen' : 'Full Screen');
  const showLabels = playerWidth === 0 || playerWidth >= labelsNeed;
  const barBtn = (icon, label, onClick, key, opts = {}) => {
    const withLabel = opts.always || showLabels;
    return (
      <button type="button" ref={opts.btnRef || null} aria-label={label}
        onClick={once(key, onClick)} onTouchEnd={once(key, onClick)}
        style={{
          height: '44px', minWidth: '44px', padding: withLabel ? '0 10px' : '0 6px', flexShrink: 0,
          background: opts.active ? 'rgba(255,255,255,0.16)' : 'none', border: 'none', borderRadius: '10px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          color: opts.tint || '#fff', fontSize: '13px', fontWeight: 600, fontFamily: '"DM Sans",sans-serif', whiteSpace: 'nowrap',
          WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
        }}>
        {withLabel ? <>{icon}<span>{label}</span></> : (opts.compact || icon)}
      </button>
    );
  };
  const barIcon = (children) => (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
  );

  // ── PICTURE IN PICTURE ────────────────────────────────────────────────────
  // Always from the tap itself (browsers only grant it inside a user gesture).
  // Safari on iPad uses its own webkitSetPresentationMode and wants the video
  // playing, so a paused lesson is started by the same tap. Everything else
  // uses the standard requestPictureInPicture. A refusal is silent: the button
  // simply does nothing, and nothing else changes.
  const togglePiP = () => {
    const el = mediaRef.current;
    if (!el) return;
    try {
      if (typeof el.webkitSetPresentationMode === 'function' && typeof el.webkitSupportsPresentationMode === 'function'
        && el.webkitSupportsPresentationMode('picture-in-picture')) {
        if (el.webkitPresentationMode === 'picture-in-picture') { el.webkitSetPresentationMode('inline'); return; }
        if (el.paused) { const pr = el.play(); if (pr && pr.catch) pr.catch(() => {}); setIsPlaying(true); }
        el.webkitSetPresentationMode('picture-in-picture');
        return;
      }
      if (document.pictureInPictureElement) { const r = document.exitPictureInPicture(); if (r && r.catch) r.catch(() => {}); }
      else if (typeof el.requestPictureInPicture === 'function') { const r = el.requestPictureInPicture(); if (r && r.catch) r.catch(() => {}); }
    } catch (err) { /* the browser refused */ }
  };


  return (
    <div ref={containerRef} className="video-player-container"
      style={{
        position: 'relative', aspectRatio: '16/9', width: '100%',
        borderRadius: isFullscreen ? 0 : '10px', overflow: 'hidden', background: '#000',
      }}>
      {autoMuted && (soundPromptBig ? (
        <button type="button" aria-label="Tap for sound" onClick={(e) => { e.stopPropagation(); }} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: 'clamp(14px, 3.5vw, 22px) clamp(22px, 5vw, 34px)', borderRadius: '18px', border: 'none', background: '#111111', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 'clamp(16px, 2.4vw, 22px)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', boxShadow: '0 10px 40px rgba(0,0,0,0.45)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: 'clamp(34px, 7vw, 48px)', height: 'clamp(34px, 7vw, 48px)' }}><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>
          Tap for sound
        </button>
      ) : (
        <button type="button" aria-label="Tap for sound" onClick={(e) => { e.stopPropagation(); }} style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 40, display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '999px', border: 'none', background: 'rgba(0,0,0,0.72)', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>
          Tap for sound
        </button>
      ))}
      <div className="video-player-inner" style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', borderRadius: 'inherit' }}>

        {/* z0 — media */}
        {videoUrl && <div ref={mediaHostRef} style={{ position: 'absolute', inset: 0 }} />}
        {!videoUrl && <div style={{ position: 'absolute', inset: 0, background: '#0a0a0a' }} />}

        {/* z1 — cinematic vignette (mobile only) */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.45) 100%)',
        }} />

        {/* z2 — paused dim */}
        <div style={{ position: 'absolute', inset: 0, background: '#000', opacity: isPlaying ? 0 : 0.3, zIndex: 2, pointerEvents: 'none', transition: 'opacity 0.4s ease' }} />

        {/* z3 — spinner. pointerEvents NONE: an overlay above the controls without
            this is what made them unclickable in the first place. */}
        {isLoading && !showError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3, pointerEvents: 'none' }}>
            <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.25)', borderTopColor: colors.accent, borderRadius: '50%', animation: 'rbspin 0.8s linear infinite' }} />
          </div>
        )}

        {/* z3 — error */}
        {showError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', background: 'rgba(0,0,0,0.7)', zIndex: 3, pointerEvents: 'none', padding: '16px', textAlign: 'center' }}>
            <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
            <p style={{ margin: 0, color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 12 }}>{error}</p>
          </div>
        )}
        {/* z30 — link refresh overlay (above the tap layer, so Continue can be tapped) */}
        <LinkOverlay state={linkOverlay} at={resumeFromRef.current} colors={colors} onContinue={continueFromLink} small />

        {/* z15 — TAP LAYER. Always interactive. iOS does not reliably bubble a
            click off a <video>, so the reveal tap needs a real element above it.
            Single logical handler via once(): touchend + synthesised click both
            firing is what made this toggle twice and appear dead. */}
        <div
          onTouchStart={(e) => {
            const t = e.touches && e.touches[0]; const h = holdRef.current;
            clearTimeout(h.timer); h.active = false;
            if (!t || e.touches.length > 1) return;
            h.x = t.clientX; h.y = t.clientY;
            h.timer = setTimeout(() => { h.active = true; setHold(true); try { if (navigator.vibrate) navigator.vibrate(8); } catch (err) { /* no haptics here */ } }, 450);
          }}
          onTouchMove={(e) => {
            const t = e.touches && e.touches[0]; const h = holdRef.current;
            if (t && !h.active && Math.hypot(t.clientX - h.x, t.clientY - h.y) > 10) clearTimeout(h.timer);
          }}
          onTouchCancel={() => { const h = holdRef.current; clearTimeout(h.timer); if (h.active) { h.active = false; setHold(false); } }}
          onTouchEnd={once('tap', (e) => handleSurfaceTap(e))}
          onClick={once('tap', (e) => handleSurfaceTap(e))}
          style={{ position: 'absolute', inset: 0, zIndex: 15, pointerEvents: 'auto', background: 'transparent', touchAction: 'manipulation', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
        />
        {ripple && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, [ripple.side === 'back' ? 'left' : 'right']: 0, width: '38%', zIndex: 16, pointerEvents: 'none',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', background: 'rgba(255,255,255,0.12)',
            borderRadius: ripple.side === 'back' ? '0 50% 50% 0 / 0 50% 50% 0' : '50% 0 0 50% / 50% 0 0 50%',
            color: '#fff', fontFamily: '"DM Sans",sans-serif', fontWeight: 700, fontSize: '14px', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
            <span>{ripple.side === 'back' ? '«' : '»'}</span>
            <span>{ripple.side === 'back' ? '−' : '+'}{ripple.total} s</span>
          </div>
        )}
        {hold && (
          <div style={{ position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', zIndex: 17, pointerEvents: 'none',
            padding: '6px 12px', borderRadius: '999px', background: 'rgba(0,0,0,0.62)', color: '#fff',
            fontFamily: '"DM Sans",sans-serif', fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap' }}>
            2× ▸▸
          </div>
        )}

        {/* ── z16 CONTROLS — title on top, skip / play / skip in the centre, and one
            row of written-out options under the scrubber ─────────────────────── */}

        {/* TOP: the lesson's title and AirPlay. The band itself lets taps through to the tap layer. */}
        <div data-controls="true"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, padding: '10px 10px 24px 20px',
            background: 'linear-gradient(to bottom,rgba(0,0,0,0.6) 0%,rgba(0,0,0,0.3) 60%,transparent 100%)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px',
            zIndex: 16, opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: 'none',
          }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', paddingTop: autoMuted && !soundPromptBig ? '52px' : '6px', minWidth: 0, fontFamily: '"DM Sans",sans-serif' }}>
            {/* The course and lesson names are no longer drawn on the video (October 15, 2026): the title stack above it has them. The box stays so the layout does not shift. */}
          </div>
          <div onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
            style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0, pointerEvents: showControls ? 'auto' : 'none' }}>
            {AIRPLAY_ENABLED && airplayAvailable && barBtn(
              barIcon(<><path d="M5 17H4a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2h-1" /><path d="M12 15l5 6H7z" /></>),
              'AirPlay', () => { showAirplayPicker(); bump(); }, 'airplay', { always: true })}
          </div>
        </div>

        {/* CENTRE: skip 10s / play / skip 10s — original SkipButton, top 45%. */}
        <div data-controls="true" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: '45%', left: '50%', transform: 'translate(-50%,-50%)',
            display: 'flex', alignItems: 'center', gap: '18px', zIndex: 16,
            opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease',
            pointerEvents: showControls ? 'auto' : 'none',
          }}>
          <SkipButtonTablet direction="back" onClick={once('skipback', () => skip(-SKIP_SECONDSTablet))} currentTier={currentTier} isMobile big seconds={SKIP_SECONDSTablet} />
          <button aria-label={isPlaying ? 'Pause' : 'Play'}
            onClick={once('play', () => { setIsPlaying(!isPlaying); bump(); })}
            onTouchEnd={once('play', () => { setIsPlaying(!isPlaying); bump(); })}
            style={{
              width: '60px', height: '60px', borderRadius: '50%', border: 'none', flexShrink: 0,
              background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)', cursor: 'pointer',
              animation: !isPlaying ? 'rbpulse 2.2s ease-in-out infinite' : 'none',
              WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
            }}>
            <svg width={26} height={26} viewBox="0 0 24 24" fill="#000" style={{ marginLeft: !isPlaying ? '3px' : '0' }}>
              {isPlaying ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}
            </svg>
          </button>
          <SkipButtonTablet direction="forward" onClick={once('skipfwd', () => skip(SKIP_SECONDSTablet))} currentTier={currentTier} isMobile big seconds={SKIP_SECONDSTablet} />
        </div>

        {/* While a menu is open the picture dims a little, so the menu reads as the thing to answer. */}
        {(showSpeedSheet || showSubtitleSheet) && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 16, background: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }} />
        )}

        {/* BOTTOM: scrubber with the time at its end, then the option row. */}
        <div data-controls="true" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, padding: '18px 8px 6px',
            background: 'linear-gradient(to top,rgba(0,0,0,0.9) 0%,rgba(0,0,0,0.55) 55%,transparent 100%)',
            zIndex: 16, opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease',
            pointerEvents: showControls ? 'auto' : 'none',
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '0 10px' }}>
          {/* A 4px bar inside a 24px hit area, so a thumb can actually land on it. */}
          <div ref={progressRef}
            onClick={(e) => { e.stopPropagation(); seekTo(e.clientX); }}
            onTouchStart={(e) => { e.stopPropagation(); if (e.touches[0]) scrubTo(e.touches[0].clientX); }}
            onTouchMove={(e) => { e.stopPropagation(); if (e.touches[0]) scrubTo(e.touches[0].clientX); }}
            onTouchEnd={(e) => { e.stopPropagation(); if (e.cancelable) e.preventDefault(); endScrub(); bump(); }}
            onTouchCancel={() => { scrubRef.current = null; setScrubTime(null); }}
            style={{ position: 'relative', height: '24px', display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1, minWidth: 0, touchAction: 'none' }}>
            <div style={{ position: 'relative', height: '4px', width: '100%', background: 'rgba(255,255,255,0.2)', borderRadius: '3px' }}>
              <div style={{ position: 'absolute', height: '100%', width: `${bufPct}%`, background: 'rgba(255,255,255,0.3)', borderRadius: '3px', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', height: '100%', width: `${pct}%`, borderRadius: '3px', pointerEvents: 'none', ...pStyles.played }} />
              <div style={{ position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%,-50%)', width: '12px', height: '12px', borderRadius: '50%', pointerEvents: 'none', ...pStyles.thumb }} />
            </div>
            {scrubTime !== null && (
              <div style={{ position: 'absolute', left: `${pct}%`, bottom: '26px', transform: 'translateX(-50%)', padding: '3px 8px', borderRadius: '6px', background: 'rgba(0,0,0,0.8)', color: '#fff', fontSize: '12px', fontWeight: 700, fontFamily: '"DM Sans",sans-serif', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                {fmtTimeTablet(scrubTime)}
              </div>
            )}
          </div>
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.92)', fontFamily: '"DM Sans",sans-serif', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
              {fmtTimeTablet(scrubTime ?? currentTime)} / {fmtTimeTablet(totalSeconds)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '4px' }}>
            {barBtn(
              barIcon(isMuted
                ? <><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M23 9l-6 6M17 9l6 6" /></>
                : <><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" /></>),
              isMuted ? 'Unmute' : 'Mute', () => { setIsMuted(!isMuted); bump(); }, 'mute')}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: showLabels ? '6px' : '4px', flex: 1, minWidth: 0 }}>
              {barBtn(
                barIcon(<><path d="M5.6 18a9 9 0 1112.8 0" /><path d="M12 13l4-4" /></>),
                `Speed (${playbackSpeed}x)`, () => { setShowSpeedSheet(true); bump(); }, 'speed',
                { btnRef: speedBtnRef, active: showSpeedSheet, compact: <span>{playbackSpeed}x</span> })}
              {/* Subtitles: only when this lesson has subtitle tracks. */}
              {subtitleOptions.length > 0 && barBtn(
                barIcon(<><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M7 12h4M13 12h4M7 16h10" /></>),
                'Subtitles', () => { setShowSubtitleSheet(true); bump(); }, 'subs',
                { btnRef: subsBtnRef, active: showSubtitleSheet, tint: activeSubtitleKey !== 'off' ? colors.accent : null })}
              {pipAvailable && barBtn(
                barIcon(<><rect x="2" y="3" width="20" height="14" rx="2" /><rect x="11" y="9" width="9" height="6" rx="1" fill="currentColor" /></>),
                'Picture in Picture', () => { togglePiP(); bump(); }, 'pip')}
            </div>
            {barBtn(
              barIcon(isFullscreen
                ? <path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3" />
                : <path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3" />),
              isFullscreen ? 'Exit Full Screen' : 'Full Screen', () => goFullscreen(), 'fs')}
          </div>
        </div>

        <style>{`
          @keyframes rbspin { to { transform: rotate(360deg); } }
          @keyframes rbpulse { 0%,100% { transform: scale(1); box-shadow: 0 8px 32px rgba(0,0,0,0.4); } 50% { transform: scale(1.06); box-shadow: 0 10px 40px rgba(0,0,0,0.5); } }
          @keyframes rbsheet { from { transform: translateY(100%); } to { transform: translateY(0); } }
          .video-js { background-color: transparent !important; }
          .video-js video, .video-js .vjs-tech { object-fit: contain; }
          .video-js .vjs-control-bar, .video-js .vjs-big-play-button, .video-js .vjs-loading-spinner,
          .video-js .vjs-error-display, .video-js .vjs-poster, .video-js .vjs-text-track-display { display: none !important; }
        `}</style>
      </div>

      {/* ── SPEED MENU ── opens above the Speed button (see popoverStyle). position:fixed with a
          full-screen backdrop: a tap anywhere outside closes it, and no ancestor can clip it. */}
      {showSpeedSheet && (
        <div
          onClick={once('sheetbg', () => setShowSpeedSheet(false))}
          onTouchEnd={once('sheetbg', () => setShowSpeedSheet(false))}
          style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: 'transparent' }}>
          <div onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
            style={popoverStyle(speedBtnRef)}>
            <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.muted, fontFamily: '"DM Sans",sans-serif', padding: '0 8px 8px' }}>
              Playback Speed
            </div>
            {PLAYBACK_SPEEDSTablet.map((spd) => (
              <button key={spd}
                onClick={once('spd' + spd, () => { setPlaybackSpeed(spd); setShowSpeedSheet(false); })}
                onTouchEnd={once('spd' + spd, () => { setPlaybackSpeed(spd); setShowSpeedSheet(false); })}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', minHeight: '44px', padding: '0 12px',
                  background: playbackSpeed === spd ? `${colors.accent}22` : 'transparent',
                  border: 'none', borderRadius: '10px', cursor: 'pointer',
                  color: playbackSpeed === spd ? colors.accent : colors.text,
                  fontSize: '15px', fontWeight: playbackSpeed === spd ? 700 : 500,
                  fontFamily: '"DM Sans",sans-serif',
                  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
                }}>
                <span>{`${spd}x`}</span>
                {playbackSpeed === spd && (
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* End of lesson (1.23): Up next / Unlock / Lesson complete, over the video. */}
      {ended && endCardEnabled && !nativeFs && <EndOfLessonCard colors={colors} upNext={upNext} upNextLocked={upNextLocked} info={endCard} onUpNext={onUpNext} onUnlock={onUnlock} onReplay={replay} />}
      {/* Subtitles sheet (1.21): built like the speed sheet. */}
      {showSubtitleSheet && (
        <div
          onClick={once('subbg', () => setShowSubtitleSheet(false))}
          onTouchEnd={once('subbg', () => setShowSubtitleSheet(false))}
          style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: 'transparent' }}>
          <div onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()} role="dialog" aria-label="Subtitles"
            style={popoverStyle(subsBtnRef)}>
            <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.muted, fontFamily: '"DM Sans",sans-serif', padding: '0 8px 8px' }}>
              Subtitles
            </div>
            {[{ key: 'off', label: 'Off' }, ...subtitleOptions].map((s) => (
              <button key={s.key} role="menuitemradio" aria-checked={activeSubtitleKey === s.key}
                onClick={once('sub' + s.key, () => { setSubtitleKey(s.key); setShowSubtitleSheet(false); })}
                onTouchEnd={once('sub' + s.key, () => { setSubtitleKey(s.key); setShowSubtitleSheet(false); })}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', minHeight: '44px', padding: '0 12px',
                  background: activeSubtitleKey === s.key ? `${colors.accent}22` : 'transparent',
                  border: 'none', borderRadius: '10px', cursor: 'pointer',
                  color: activeSubtitleKey === s.key ? colors.accent : colors.text,
                  fontSize: '15px', fontWeight: activeSubtitleKey === s.key ? 700 : 500,
                  fontFamily: '"DM Sans",sans-serif', textAlign: 'left',
                  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
                }}>
                <span>{s.label}</span>
                {activeSubtitleKey === s.key && (
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
                )}
              </button>
            ))}
            <div style={{ fontSize: '12px', color: colors.muted, fontFamily: '"DM Sans",sans-serif', padding: '10px 16px 0' }}>
              Only languages available for this lesson are listed.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════ Phone player · moved as is from VideoViewPageFairPlayMobile.jsx (MobilePlayer) ════════════════

const PLAYBACK_SPEEDSPhone = [0.5, 0.75, 1, 1.25, 1.5, 2];

// If the media never reports ready, stop showing a spinner forever and let the
// user press play themselves. A stuck `isLoading` used to hide the controls
// behind an overlay with no way out.
const LOADING_GIVEUP_MSPhone = 15000;

// +/-10s on mobile (the desktop page uses 15).
const SKIP_SECONDSPhone = 10;

// ============================================================================
// DRM ENGINE — COPIED VERBATIM FROM VideoViewPageFairPlay.jsx. DO NOT EDIT HERE.
// ============================================================================

// SPC (Uint8Array/ArrayBuffer) -> standard base64 for the DRMtoday license POST.
function toBase64Phone(input) {
  const bytes = input instanceof Uint8Array ? input
    : input instanceof ArrayBuffer ? new Uint8Array(input)
    : new Uint8Array(input && input.buffer ? input.buffer : input);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary);
}

// base64 CKC -> ArrayBuffer for the CDM.
function fromBase64Phone(b64) {
  const binary = window.atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

// Replacement for shaka.util.StringUtils.fromBytesAutoDetect (utf-8, then raw).
function fromBytesAutoDetectPhone(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  try { return new TextDecoder('utf-8').decode(u8); }
  catch (e) { let s = ''; for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]); return s; }
}

// ── MUXED FAIRPLAY PACKAGE ───────────────────────────────────────────────────
// Safari gets the single-track SAMPLE-AES TS package instead of the demuxed cbcs
// fMP4 one:
//     videos/<slug>/main-video/master.m3u8         (Widevine — Chrome keeps this)
//     videos/<slug>/main-video-single/prog_index.m3u8   (FairPlay — muxed)
//
// Same lesson prefix, so the SAME ?t= token authorizes it: no API, DB or Worker
// change. The Worker is already path-agnostic (.ts → video/mp2t; the EXT-X-MEDIA
// DEFAULT=YES fix only matches TYPE=AUDIO lines, which a muxed package has none of).
//
// Chrome is unaffected — this file is only loaded when player === 'videojs'.
//
// GATING: 'all' once the whole catalogue is packaged; 'list' while rolling out
// (only slugs in MUXED_SLUGS are swapped); 'off' to fall back to the demuxed
// cbcs fMP4 package everywhere. An unpackaged lesson under 'all' will 404 on the
// manifest request — check the network tab for the rewritten URL.
const MUXED_MODEPhone = 'all';          // 'all' | 'list' | 'off'
const MUXED_SLUGSPhone = [];            // used only when MUXED_MODE === 'list'

// The package layout written by mux-fairplay-all.sh:
//     videos/<slug>/main-video-fairplay/master.m3u8      <- multi-rung
//     videos/<slug>/main-video-fairplay/v0/prog_index.m3u8
// If the multi-rung master ever misbehaves, set MUXED_INDEX to
// 'v0/prog_index.m3u8' to pin the single proven rendition — no repackaging.
const MUXED_SUBDIRPhone = 'main-video-fairplay';
const MUXED_INDEXPhone = 'master.m3u8';

function toMuxedPackagePhone(url) {
  if (!url || MUXED_MODEPhone === 'off') return url;
  const m = String(url).match(/\/videos\/([^/]+)\/main-video\/master\.m3u8/);
  if (!m) return url;                                     // answer-key etc. untouched
  if (MUXED_MODEPhone === 'list' && MUXED_SLUGSPhone.indexOf(m[1]) < 0) return url;
  const out = String(url).replace('/main-video/master.m3u8',
                                  '/' + MUXED_SUBDIRPhone + '/' + MUXED_INDEXPhone);
  return out;
}

// ── skd content-id extraction ────────────────────────────────────────────────
// videojs-contrib-eme hands getContentId the initData as a String, but it can
// arrive either clean ("skd://drmtoday?...") or in the legacy length-prefixed
// UTF-16LE shape. Handle both and always return the skd URI.
function parseSkdInitDataPhone(initData) {
  if (typeof initData === 'string') {
    const idx = initData.indexOf('skd:');
    if (idx >= 0) return { uri: initData.slice(idx).replace(/\u0000/g, ''), shape: 'string' };
    return { uri: initData, shape: 'string_raw' };
  }
  const u8 = initData instanceof Uint8Array ? initData : new Uint8Array(initData);
  if (u8.byteLength >= 6) {
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    const byteLen = dv.getUint32(0, true);
    if (byteLen + 4 === u8.byteLength && byteLen % 2 === 0) {
      const uri = new TextDecoder('utf-16le').decode(new Uint8Array(u8.buffer, u8.byteOffset + 4, byteLen));
      if (uri.startsWith('skd:')) return { uri, shape: 'legacy_len_utf16le' };
    }
  }
  return { uri: fromBytesAutoDetectPhone(u8), shape: 'plain' };
}

const getProgressStylesPhone = (t) => {
  if (tierRank(t)===0) return { played:{background:'#8B8B7A',boxShadow:'0 0 8px rgba(139,139,122,0.4)'}, thumb:{background:'#8B8B7A',boxShadow:'0 0 8px rgba(139,139,122,0.5)'} };
  if (tierRank(t)===1) return { played:{background:'linear-gradient(90deg,#C9A962,#E8D5A3)',boxShadow:'0 0 12px rgba(201,169,98,0.5)'}, thumb:{background:'linear-gradient(135deg,#E8D5A3,#C9A962)',boxShadow:'0 0 12px rgba(201,169,98,0.6)'} };
  return { played:{background:'linear-gradient(90deg,#E8D5A3,#FFF8E7)',boxShadow:'0 0 16px rgba(232,213,163,0.6)'}, thumb:{background:'linear-gradient(135deg,#FFF8E7,#E8D5A3)',boxShadow:'0 0 16px rgba(232,213,163,0.7)'} };
};


const getSkipButtonStylesPhone = (currentTier, isHovered, isMobile) => {
  const base = { position:'relative', borderRadius:isMobile?'16px':'20px', cursor:'pointer', display:'flex', alignItems:'center', gap:isMobile?'5px':'8px', padding:isMobile?'7px 12px':'12px 20px', transition:'all 0.4s cubic-bezier(0.4,0,0.2,1)', overflow:'hidden', border:'none', fontFamily:'"DM Sans",sans-serif', WebkitTapHighlightColor:'transparent' };
  if (tierRank(currentTier) === 0) return { ...base, background:isHovered?'linear-gradient(135deg,rgba(26,26,26,0.5),rgba(42,42,42,0.45))':'linear-gradient(135deg,rgba(26,26,26,0.4),rgba(42,42,42,0.35))', border:`1px solid ${isHovered?'rgba(255,255,255,0.3)':'rgba(255,255,255,0.2)'}`, boxShadow:isHovered?'0 4px 16px rgba(0,0,0,0.4),inset 0 1px 0 rgba(255,255,255,0.2)':'0 2px 8px rgba(0,0,0,0.3),inset 0 1px 0 rgba(255,255,255,0.15)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
  if (tierRank(currentTier) === 1) return { ...base, background:isHovered?'linear-gradient(135deg,rgba(201,169,98,0.35),rgba(201,169,98,0.25))':'linear-gradient(135deg,rgba(201,169,98,0.25),rgba(201,169,98,0.15))', border:`1px solid ${isHovered?'rgba(201,169,98,0.6)':'rgba(201,169,98,0.4)'}`, boxShadow:isHovered?'0 6px 24px rgba(0,0,0,0.4),0 0 36px rgba(201,169,98,0.4),inset 0 1px 0 rgba(201,169,98,0.5)':'0 3px 12px rgba(0,0,0,0.3),0 0 20px rgba(201,169,98,0.18),inset 0 1px 0 rgba(201,169,98,0.3)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
  return { ...base, background:isHovered?'linear-gradient(135deg,rgba(232,213,163,0.4),rgba(232,213,163,0.3))':'linear-gradient(135deg,rgba(232,213,163,0.3),rgba(232,213,163,0.2))', border:`1px solid ${isHovered?'rgba(232,213,163,0.7)':'rgba(232,213,163,0.5)'}`, boxShadow:isHovered?'0 8px 32px rgba(0,0,0,0.5),0 0 44px rgba(232,213,163,0.45),inset 0 1px 0 rgba(232,213,163,0.6)':'0 4px 16px rgba(0,0,0,0.4),0 0 24px rgba(232,213,163,0.24),inset 0 1px 0 rgba(232,213,163,0.4)', transform:isHovered?'translateY(-2px)':'translateY(0)' };
};
const getSkipIconColorPhone = (t,h) => tierRank(t)===0?'#FFF':tierRank(t)===1?(h?'#E8D5A3':'#C9A962'):(h?'#FFF8E7':'#E8D5A3');
const getSkipNumberColorPhone = (t) => tierRank(t)===0?'#FFF':tierRank(t)===1?'#C9A962':'#E8D5A3';
function SkipButtonPhone({ direction, onClick, currentTier, isMobile, seconds=15 }) {
  const [isHovered, setIsHovered] = useState(false);
  const styles = getSkipButtonStylesPhone(currentTier, isHovered, isMobile);
  const iconColor = getSkipIconColorPhone(currentTier, isHovered);
  const numberColor = getSkipNumberColorPhone(currentTier);
  const iconSize = isMobile?13:18, numberSize = isMobile?12:16, unitSize = isMobile?9:12;
  const iconFilter = tierRank(currentTier)===0?'drop-shadow(0 1px 3px rgba(0,0,0,0.5))':tierRank(currentTier)===1?(isHovered?'drop-shadow(0 0 8px rgba(201,169,98,0.5))':'drop-shadow(0 0 4px rgba(201,169,98,0.3))'):(isHovered?'drop-shadow(0 0 12px rgba(232,213,163,0.6))':'drop-shadow(0 0 6px rgba(232,213,163,0.4))');
  return (
    <button onClick={onClick} onMouseEnter={()=>setIsHovered(true)} onMouseLeave={()=>setIsHovered(false)} style={styles}>
      {direction==='back' && <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{filter:iconFilter,transition:'all 0.3s ease'}}><path d="M19 12H5M12 5l-7 7 7 7"/></svg>}
      <span style={{display:'flex',alignItems:'baseline',gap:'2px',color:'#F5F5F0',textShadow:isLightTheme(currentTier)?'0 1px 3px rgba(0,0,0,0.5)':'0 1px 2px rgba(0,0,0,0.3)'}}>
        <span style={{fontSize:`${numberSize}px`,fontWeight:'700',color:numberColor,letterSpacing:'-0.02em'}}>{seconds}</span>
        <span style={{fontSize:`${unitSize}px`,fontWeight:'500',opacity:0.8}}>s</span>
      </span>
      {direction==='forward' && <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{filter:iconFilter,transition:'all 0.3s ease'}}><path d="M5 12h14M12 5l7 7-7 7"/></svg>}
    </button>
  );
}

const fmtTimePhone = (s) => {
  if (!s || isNaN(s)) return '0:00';
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
};

// ============================================================================
// MOBILE PLAYER
// ============================================================================
// Layer order inside the player box (all children of .video-player-inner):
//   z0  the <video> Video.js mounts
//   z1  paused dim            pointerEvents:none
//   z2  spinner / error       pointerEvents:none  ← MUST stay none, see header
//   z16 controls              pointerEvents follows showControls
// Nothing that covers the player is clickable except the controls themselves.

function MobilePlayerPhone({
  video, colors, currentTier, isPlaying, setIsPlaying, currentTime, setCurrentTime,
  videoUrl: rawVideoUrl, videoId, assetId, resumeFrom = 0,
  upNext = null, upNextLocked = false, onUpNext, onUnlock, endCard = null, refreshLink,
}) {
  // Muxed package swap: the FairPlay path plays the single-track SAMPLE-AES TS
  // package, not the demuxed cbcs fMP4 one. The desktop page also layers
  // ?forceVariant on top of this; that diagnostic hook is deliberately not
  // carried over here.
  const videoUrl = useMemo(() => toMuxedPackagePhone(rawVideoUrl), [rawVideoUrl]);

  const containerRef = useRef(null);   // fullscreen target
  const mediaHostRef = useRef(null);   // div Video.js mounts into (React owns this)
  const mediaRef = useRef(null);       // raw <video> (webkitEnterFullscreen)
  const vjsRef = useRef(null);
  const progressRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const { token, profile, api } = useAuth();
  const authTokenRef = useRef(token);
  authTokenRef.current = token;

  const API_CONTENT_BASE = process.env.REACT_APP_CONTENT_API_URL || process.env.REACT_APP_API_URL || '';
  const DRMTODAY_MERCHANT = process.env.REACT_APP_DRMTODAY_MERCHANT || 'f100090a-e116-42ba-b03a-1d157c6be3f3';

  const latestRef = useRef({ pos: 0, dur: 0 });
  const resumedRef = useRef(false);
  const resumeFromRef = useRef(resumeFrom);
  // Link refresh, step 2: the link this player's source was last set from, how to build a source for a link
  // (set by the load effect, which owns the FairPlay callbacks), the latest link prop for the player's own
  // event handlers, the reload function for the error handler, and the overlay state.
  const loadedUrlRef = useRef(null);
  const sourceForRef = useRef(null);
  const videoUrlRef = useRef(null);
  videoUrlRef.current = videoUrl;
  const rebuildFromFreshLinkRef = useRef(null);
  const [linkOverlay, setLinkOverlay] = useState(null);   // null | 'loading' | 'resuming' | 'ready' ('resuming' = the Resume tap's own reload)
  const resumingRef = useRef(false);   // the Resume tap's reload is in progress: its own play() must not close the overlay

  // Heartbeats (the 10-second send while playing, and the final send on unmount) live in useHeartbeat.
  const heartbeatOnTime = useHeartbeat(videoId);

  const [showControls, setShowControls] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Saved speed (1.22): start from the profile's preferredPlaybackSpeed when it is one of our speeds, else 1×.
  const [playbackSpeed, setPlaybackSpeed] = useState(() => { const s = Number(profile && profile.preferredPlaybackSpeed); return PLAYBACK_SPEEDSPhone.includes(s) ? s : 1; });
  const [showSpeedSheet, setShowSpeedSheet] = useState(false);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isLoading, setIsLoading] = useState(!!videoUrl);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!error) { setShowError(false); return; }
    const t = setTimeout(() => setShowError(true), 600);
    return () => clearTimeout(t);
  }, [error]);

  // Give up on the spinner rather than covering the player forever.
  useEffect(() => {
    if (!isLoading) return undefined;
    const t = setTimeout(() => setIsLoading(false), LOADING_GIVEUP_MSPhone);
    return () => clearTimeout(t);
  }, [isLoading]);

  const isHls = videoUrl && /\.m3u8($|\?)/.test(videoUrl);
  const videoUrlKey = videoUrl ? String(videoUrl).split('?')[0] : null;

  const totalSeconds = duration || 0;
  // Scrubbing (touch): while a finger drags the bar, the thumb and time follow it without
  // seeking; the video seeks once, on release (scrubTo / endScrub, beside seekTo).
  const [scrubTime, setScrubTime] = useState(null);
  const scrubRef = useRef(null);
  const pct = totalSeconds > 0 ? ((scrubTime ?? currentTime) / totalSeconds) * 100 : 0;
  const bufPct = totalSeconds > 0 ? (buffered / totalSeconds) * 100 : 0;
  const pStyles = getProgressStylesPhone(currentTier);

  // ── Video.js + FairPlay (native HLS) setup ────────────────────────────────
  useEffect(() => {
    if (!videoUrl || !mediaHostRef.current) return;
    let cancelled = false;

    // Create the media element IMPERATIVELY and append it, so Video.js can own and
    // dispose it without fighting React's reconciler (React only owns the host div).
    const videoEl = document.createElement('video');
    videoEl.className = 'video-js vjs-tech';
    videoEl.setAttribute('playsinline', '');
    applyAirplayPolicy(videoEl);
    videoEl.playsInline = true;
    videoEl.preload = 'metadata';
    videoEl.style.width = '100%';
    videoEl.style.height = '100%';
    videoEl.style.objectFit = 'contain';
    mediaHostRef.current.appendChild(videoEl);
    mediaRef.current = videoEl;

    const player = videojs(videoEl, {
      controls: false, autoplay: false, preload: 'metadata',
      playsinline: true, fill: true, fluid: false,
      bigPlayButton: false, loadingSpinner: false, errorDisplay: false, controlBar: false,
      // overrideNative:false → Safari plays HLS natively → webkitneedkey per track (the FairPlay path).
      html5: { vhs: { overrideNative: false }, nativeAudioTracks: true, nativeVideoTracks: true },
    });
    vjsRef.current = player;

    try {
      const rootEl = player.el();
      rootEl.style.position = 'absolute'; rootEl.style.inset = '0';
      rootEl.style.width = '100%'; rootEl.style.height = '100%';
    } catch (e) { /* non-fatal */ }

    // ── player state wiring (replaces the old <video> JSX handlers) ──────────
    player.on('loadedmetadata', () => {
      if (cancelled) return;
      const d = player.duration() || 0;
      setDuration(d); setIsLoading(false); setError(null);
      if (!resumedRef.current) {
        const r = resumeFromRef.current;
        if (r > 0 && r < d) { player.currentTime(r); setCurrentTime(r); }
        resumedRef.current = true;
      }
    });
    player.on('timeupdate', () => {
      const t = player.currentTime();
      setCurrentTime(t);
      const d = player.duration() || 0;
      latestRef.current = { pos: t, dur: d };
      heartbeatOnTime(t, d, player.playbackRate(), !player.paused() && !player.seeking());
    });
    player.on('seeking', () => heartbeatOnTime.seeking());
    player.on('progress', () => { try { setBuffered(player.bufferedEnd()); } catch (e) {} });
    player.on('waiting', () => setIsLoading(true));
    player.on('canplay', () => { setIsLoading(false); setError(null); });
    player.on('playing', () => { setIsLoading(false); setError(null); });
    player.on('ended', () => setIsPlaying(false));
    // play/pause events only REPORT what the video is doing: they copy its real state, never the event's name.
    // A late event (a 'pause' that arrives while the video plays, or a 'play' while it is paused) then changes
    // nothing, so it can't make the play effect send the opposite command (the play/pause loop, October 11).
    player.on('play', () => setIsPlaying(!player.paused()));
    player.on('pause', () => setIsPlaying(!player.paused()));
    player.on('error', () => {
      const e = player.error();
      // MEDIA_ERR codes: 1 ABORTED 2 NETWORK 3 DECODE 4 SRC_NOT_SUPPORTED 5 ENCRYPTED
      const code = e && e.code;
      // Link refresh: the stream died (e.g. its link ran out mid-play) and a fresher link has arrived: reload
      // from it at the spot, with the overlay, instead of failing. A second failure on the fresh link falls through.
      if (code && rebuildFromFreshLinkRef.current && rebuildFromFreshLinkRef.current(true)) return;
      if (code === 4 || code === 5) setError('You don’t have access to this video');
      else if (code) {
        // The overlay, not the error screen, wherever there is a spot to come back to (October 30, 2026): the tap
        // reloads the source from a fresh session token and a fresh link.
        resumeFromRef.current = latestRef.current.pos || resumeFromRef.current || 0; setIsPlaying(false); setLinkOverlay('ready');   // the saved spot stays if nothing has played yet
      }
      setIsLoading(false);
    });

    // ── source + FairPlay key system ─────────────────────────────────────────
    if (!isHls) {
      // Non-DRM / non-HLS preview: plain source, no key system.
      player.src({ src: videoUrl });
      sourceForRef.current = (u) => ({ src: u });
    } else {
      // FairPlay certificate — same override hooks as the prior investigation:
      //   sessionStorage.fpsCertB64  or  ?certUrl=<url>   (no param = DRMtoday default)
      let certOverride = null;
      try {
        const b64 = window.sessionStorage.getItem('fpsCertB64');
        if (b64) { certOverride = 'data:application/octet-stream;base64,' + b64.trim(); }
        else {
          const u = new URLSearchParams(window.location.search).get('certUrl');
          if (u) { certOverride = u; }
        }
      } catch (e) { /* default */ }
      const DRMTODAY_FPS_CERT = certOverride
        || `https://lic.drmtoday.com/license-server-fairplay/cert/${DRMTODAY_MERCHANT}`;

      const licenseBase = (API_CONTENT_BASE || '').replace(/\/$/, '');
      const fairplayLicense = `${licenseBase}/license/fairplay?assetId=${encodeURIComponent(assetId || '')}`;

      const getCertificate = (emeOptions, callback) => {
        fetch(DRMTODAY_FPS_CERT, { method: 'GET' })
          .then((r) => { if (!r.ok) throw new Error('cert HTTP ' + r.status); return r.arrayBuffer(); })
          .then((buf) => { callback(null, new Uint8Array(buf)); })
          .catch((err) => { callback(err); });
      };

      // CONTENT-ID: strip ONLY variantId. DRMtoday returns LIC_KEYS_NOT_FOUND when
      // variantId is present (keys were ingested without it) — see the matching note
      // in the Shaka page.
      //
      // STALE AS OF 2026-07-22 — kept as history, do not act on it:
      //   "DO NOT also strip &trk=audio. The CDN Worker deliberately injects that tag
      //    on the audio rendition's #EXT-X-KEY so the audio track's content-id differs
      //    from video's."
      // The Worker REMOVED that injection (see rewriteManifest), on the grounds that
      // every playlist carries one identical key, so the tag invented a content-id the
      // packager never wrote. No manifest emits &trk= any more; the [^&]* strip below
      // is a no-op against it either way, so this code needs no change.
      //
      // What DOES follow from the removal: both tracks now request the SAME content-id
      // again — the exact condition the tag existed to avoid. If playback ever freezes
      // with a decoded first frame and currentTime pinned at 0, that is the audio track
      // never being keyed.
      //
      // The durable fix is per-track key URIs at packaging time; shaka-packager's
      // --hls_key_uri is a single global flag (#439 / #470), which is why neither the
      // player nor the Worker has been able to solve this cleanly.
      const getContentId = (emeOptions, initData) => {
        const { uri } = parseSkdInitDataPhone(initData);
        const contentId = uri.replace(/[?&]variantId=[^&]*/i, '');
        return contentId;
      };

      const getLicense = (emeOptions, contentId, keyMessage, callback) => {
        const spcB64 = toBase64Phone(keyMessage);
        const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
        const authToken = authTokenRef.current;
        if (authToken) headers['Authorization'] = 'Bearer ' + authToken;
        fetch(fairplayLicense, { method: 'POST', headers, body: 'spc=' + encodeURIComponent(spcB64) })
          .then(async (r) => {
            const text = await r.text();
            if (!r.ok) throw new Error('license HTTP ' + r.status);
            let txt = text.trim();
            const wrapped = txt.startsWith('<ckc>') && txt.endsWith('</ckc>');
            if (wrapped) txt = txt.slice(5, -6);
            callback(null, fromBase64Phone(txt));
          })
          .catch((err) => { callback(err); });
      };

      player.eme();
      // ── THE REAL FIX: 1, NOT 0. ────────────────────────────────────────────
      // videojs-contrib-eme reads this as:
      //     getOptions(player).firstWebkitneedkeyTimeout || 1000
      // so a literal 0 is FALSY and silently becomes 1000ms. That 1s window is a
      // cancel-and-reschedule (last-event-wins) debounce: the VIDEO needkey gets
      // clearTimeout'd by the AUDIO needkey ~330ms later, so the video key is
      // DROPPED and only re-requested by Safari's own ~20s retry.
      // 1ms survives the `||`, fires effectively immediately, and flips
      // handledFirstEvent=true so every later needkey takes the immediate path.
      // DO NOT "clean this up" back to 0.
      try { player.eme.options = { ...(player.eme.options || {}), firstWebkitneedkeyTimeout: 1 }; } catch (e) {}
      if (player.eme.initLegacyFairplay) player.eme.initLegacyFairplay();

      player.src({
        src: videoUrl,
        type: 'application/x-mpegurl',
        firstWebkitneedkeyTimeout: 1,  // see note above — 1, never 0 (|| 1000 trap)
        keySystems: { 'com.apple.fps.1_0': { getCertificate, getContentId, getLicense } },
      });
      sourceForRef.current = (u) => ({ src: u, type: 'application/x-mpegurl', firstWebkitneedkeyTimeout: 1, keySystems: { 'com.apple.fps.1_0': { getCertificate, getContentId, getLicense } } });
    }
    loadedUrlRef.current = videoUrl;   // link refresh: the link this player loaded

    return () => {
      cancelled = true;
      const p = vjsRef.current;
      if (p && !p.isDisposed()) { try { p.dispose(); } catch (e) {} }
      vjsRef.current = null;
      mediaRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrlKey, isHls, assetId, API_CONTENT_BASE]);

  // -- Play/pause (driven through the Video.js player) --
  // AbortError-aware: ONLY NotAllowedError triggers a muted retry, because only that
  // one means an autoplay block. An AbortError usually means play() was interrupted
  // mid-load (a late FairPlay key, a source swap) — retrying muted there compounds the
  // abort and costs the user their audio for no reason, so we leave it paused and let
  // the recovery guard below re-attempt once the media is genuinely playable.
  // ── Starting playback (fix: "stuck after the next lesson", "the next lesson is muted") ─────────────────
  // A refused autoplay and an interrupted play are different things:
  //   NotAllowedError · the browser refuses sound without a tap (always so on iOS after an automatic lesson change).
  //     Start muted, which every browser allows, but only temporarily: "Tap for sound" shows, and the first tap,
  //     click or key press anywhere restores the sound, inside that tap (what iOS requires).
  //   anything else (AbortError: interrupted, e.g. by the stream still loading or a late FairPlay key) · never mute;
  //     show paused at once, with the play button; the student taps again (October 11: the retries are removed).
  const [autoMuted, setAutoMuted] = useState(false);
  useEffect(() => {
    const player = vjsRef.current;
    if (!videoUrl || !player) return undefined;
    if (isPlaying) {
      let cancelled = false;
      const attempt = () => {
        if (cancelled || (typeof player.isDisposed === 'function' && player.isDisposed() === true)) return;
        const p = player.play();
        if (!p || !p.then) return;
        p.catch((err) => {
          if (cancelled) return;
          const name = err && err.name;
          if (name === 'NotAllowedError') {
            if (player.muted()) { setIsPlaying(false); return; }   // refused even muted: show paused
            player.muted(true); setIsMuted(true); setAutoMuted(true);
            const p2 = player.play();
            if (p2 && p2.then) p2.catch(() => { if (!cancelled) setIsPlaying(false); });
          } else {
            // AbortError (a late FairPlay key, a source swap): no retry; show paused, never mute.
            setIsPlaying(false);
          }
        });
      };
      attempt();
      return () => { cancelled = true; };
    }
    player.pause();
    return undefined;
  }, [isPlaying, videoUrl, setIsPlaying]);
  useEffect(() => {
    if (!autoMuted) return undefined;
    // a tap on the player itself only restores the sound (it must not also pause or play); elsewhere it carries on.
    // M also means "restore the sound" here: the player's own M toggle would otherwise flip it straight back to muted.
    const restore = (e) => { const p = vjsRef.current; if (p) p.muted(false); setIsMuted(false); setAutoMuted(false); if (e && e.type !== 'keydown' && containerRef.current && e.target && containerRef.current.contains(e.target)) { e.stopPropagation(); e.preventDefault(); } if (e && e.type === 'keydown' && (e.key === 'm' || e.key === 'M')) { e.stopImmediatePropagation(); e.preventDefault(); } };
    const o = { capture: true };
    window.addEventListener('touchend', restore, o); window.addEventListener('click', restore, o); window.addEventListener('keydown', restore, o);
    return () => { window.removeEventListener('touchend', restore, o); window.removeEventListener('click', restore, o); window.removeEventListener('keydown', restore, o); };
  }, [autoMuted]);
  useEffect(() => { if (!isMuted) setAutoMuted(false); }, [isMuted]);
  // "Tap for sound": big and centred for SOUND_PROMPT_BIG_MS, then the small corner pill until the sound is on.
  // Display only: a tap anywhere restores the sound either way (the effect above).
  const [soundPromptBig, setSoundPromptBig] = useState(false);
  useEffect(() => {
    if (!autoMuted) { setSoundPromptBig(false); return undefined; }
    setSoundPromptBig(true);
    const t = setTimeout(() => setSoundPromptBig(false), SOUND_PROMPT_BIG_MS);
    return () => clearTimeout(t);
  }, [autoMuted]);

  // -- Recovery guard: re-attempt play once the media actually becomes playable. --
  // If a FairPlay key lands late, autoplay's play() can reject with AbortError while
  // the pipeline has no video key; without this the player then sits frozen at 0
  // forever even though decryption later succeeds. One-shot per source, and it only
  // fires if the user still intends to be playing.
  const replayGuardRef = useRef(false);
  const isPlayingRef = useRef(false);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { replayGuardRef.current = false; }, [videoUrlKey]);
  useEffect(() => {
    const player = vjsRef.current;
    if (!videoUrl || !player) return;
    const onReady = () => {
      const v = mediaRef.current;
      if (replayGuardRef.current) return;
      if (!isPlayingRef.current) return;          // user paused on purpose — respect it
      if (!v || v.videoWidth > 0) return;         // already decoding; nothing to recover
      replayGuardRef.current = true;
      const p = player.play();
      if (p && p.catch) p.catch(() => {});
    };
    player.on('canplaythrough', onReady);
    return () => { try { player.off('canplaythrough', onReady); } catch (e) {} };
  }, [videoUrl, videoUrlKey]);

  // TOUCH/CLICK DEDUPE — PER CONTROL.
  // iOS fires `touchend` and then synthesises a `click` for the same tap, so any
  // element with both handlers ran its action twice.
  //
  // The first version of this used ONE shared timestamp for every control, which
  // created a worse bug than the one it fixed: while the video plays the controls
  // auto-hide, so reaching any button takes TWO taps in quick succession — one on
  // the tap layer to reveal, one on the button. With a shared guard the second tap
  // fell inside the window and was discarded. Fullscreen and PiP looked completely
  // dead; play/pause only worked if you happened to pause between taps.
  //
  // The guard is now keyed per control, so a tap on the reveal layer can never
  // suppress the next tap on a different button.
  const tapGuards = useRef({});
  const once = useCallback((key, fn) => (e) => {
    // A handled touch cancels the browser's follow-up click (preventDefault), so one tap is
    // one action, and that click can never land on whatever just opened under the finger
    // (a "ghost click": it used to close the speed sheet the moment a tap opened it).
    // Quick repeated taps now all count. Safety net, still keyed per control: a click on
    // the same control within 400 ms of a handled touch is ignored, in case a browser sends one anyway.
    const now = Date.now();
    if (e && e.type === 'touchend') {
      if (e.cancelable && e.preventDefault) e.preventDefault();
      tapGuards.current[key] = now;
    } else if (now - (tapGuards.current[key] || 0) < 400) {
      return;
    }
    if (e && e.stopPropagation) e.stopPropagation();
    fn(e);
  }, []);

  // ── controls auto-hide ───────────────────────────────────────────────────
  const bump = useCallback(() => {
    setShowControls(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (isPlaying) hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3800);
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying) { setShowControls(true); if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); }
    else hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3800);
    return () => { if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); };
  }, [isPlaying]);

  useEffect(() => { if (isPlaying) setHasStarted(true); }, [isPlaying]);

  // WHY A DEDICATED TAP LAYER, AND NOT onClick ON THE CONTAINER:
  // On iOS a tap on a <video> element does not reliably emit a `click` that
  // bubbles to an ancestor — the media element swallows it. Relying on bubbling
  // meant that once the controls hid themselves (pointerEvents:'none'), the tap
  // landed on the video, nothing fired, and the controls could never be brought
  // back. This transparent sibling sits ABOVE the video and BELOW the controls,
  // and its pointerEvents are ALWAYS 'auto'.
  const toggleControls = useCallback(() => {
    if (showControls) { setShowControls(false); if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); }
    else bump();
  }, [showControls, bump]);

  useEffect(() => { const p = vjsRef.current; if (p) { p.muted(isMuted); p.volume(isMuted ? 0 : 1); } }, [isMuted]);
  // Hold for 2× (touch): while the finger is held on the video, play at 2×; on release, back to the chosen speed.
  const [hold, setHold] = useState(false);
  const holdRef = useRef({ timer: null, active: false, x: 0, y: 0 });
  useEffect(() => () => clearTimeout(holdRef.current.timer), []);
  // Media ready: counts each time the media reports its metadata (first load, a new lesson). The
  // speed and the subtitle tracks apply from this, because the Video.js player is created inside
  // the load effect and isLoading can stay false if the player mounts before its URL.
  const [mediaReady, setMediaReady] = useState(0);
  useEffect(() => {
    const p = vjsRef.current;
    if (!p) return undefined;
    const onReady = () => setMediaReady((n) => n + 1);
    p.on('loadedmetadata', onReady);
    return () => { try { p.off('loadedmetadata', onReady); } catch (err) { /* player already disposed */ } };
  }, [videoUrl]);

  // ── End of lesson (1.23) ──────────────────────────────────────────────────
  // The page passes upNext / upNextLocked / onUpNext / onUnlock to the LESSON player only, so the
  // answer-key player never shows the card. The video's end is detected by these new listeners;
  // the existing end handler is untouched.
  const endCardEnabled = typeof onUpNext === 'function' || typeof onUnlock === 'function';
  const [ended, setEnded] = useState(false);
  const [nativeFs, setNativeFs] = useState(false);     // iPhone's own full-screen player hides our card
  useEffect(() => {
    setEnded(false);
    const p = vjsRef.current;
    if (!p) return undefined;
    const onEnd = () => setEnded(true);
    const onAgain = () => setEnded(false);
    p.on('ended', onEnd); p.on('play', onAgain); p.on('seeking', onAgain);
    return () => { try { p.off('ended', onEnd); p.off('play', onAgain); p.off('seeking', onAgain); } catch (err) { /* player already disposed */ } };
  }, [videoUrlKey]);   // a new video, not a new link (link refresh): a fresh link must not reset the end card
  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return undefined;
    const on = () => setNativeFs(true); const off = () => setNativeFs(false);
    el.addEventListener('webkitbeginfullscreen', on); el.addEventListener('webkitendfullscreen', off);
    return () => { el.removeEventListener('webkitbeginfullscreen', on); el.removeEventListener('webkitendfullscreen', off); };
  }, [videoUrl]);
  // No automatic advance: the next lesson starts only when the student taps "Play next lesson" (or Up Next),
  // so it always starts from a tap, the way every browser (iPad and iPhone included) lets a new video start.
  // ── Link refresh, step 2: reload the source from a fresher link when the loaded one has run short ──────
  // Runs while paused and not on the end card. The loaded link is short when it has less time than a play from
  // the current spot needs (the page's own arithmetic). `fromError` is the mid-play case: the stream died, so
  // any fresher link is reason enough. Returns whether a reload was started.
  const rebuildFromFreshLink = (fromError) => {
    const p = vjsRef.current; const was = loadedUrlRef.current; const now = videoUrlRef.current;
    if (!p || !was || !now || was === now || !sourceForRef.current) return false;
    const pos = latestRef.current.pos || 0;
    const dur = duration || latestRef.current.dur || 0;
    const leftWas = cdnTokenSecondsLeft(was); const leftNow = cdnTokenSecondsLeft(now);
    if (leftNow !== null && leftWas !== null && leftNow <= leftWas) return false;   // the new link is no fresher
    if (!fromError) {
      const need = Math.max(CDN_TOKEN_MIN_SECONDS, videoNeedSeconds(dur, pos));
      if (leftWas === null || leftWas >= need) return false;                        // the loaded link still has the time
    }
    const silent = !(pos >= 1);                    // never played: a quiet reload, the play button stays
    loadedUrlRef.current = now;
    resumeFromRef.current = pos; resumedRef.current = false;   // the load effect's loadedmetadata seeks back to the spot
    setError(null);
    if (fromError) setIsPlaying(false);
    if (!silent) {
      setLinkOverlay('loading');
      const onReady = () => {
        try { p.off('loadedmetadata', onReady); } catch (err) { /* disposed */ }
        if (loadedUrlRef.current === now) setLinkOverlay('ready');
      };
      p.on('loadedmetadata', onReady);
    }
    try { p.src(sourceForRef.current(now)); } catch (err) { setLinkOverlay(null); return false; }
    return true;
  };
  rebuildFromFreshLinkRef.current = rebuildFromFreshLink;
  useEffect(() => {
    if (isPlaying || ended || linkOverlay) return;
    rebuildFromFreshLink(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl, isPlaying, ended]);
  useEffect(() => { if (isPlaying && linkOverlay && !resumingRef.current) setLinkOverlay(null); }, [isPlaying, linkOverlay]);   // started another way: the overlay is done
  useEffect(() => { setLinkOverlay(null); }, [videoUrlKey]);   // a new lesson starts clean
  // Resume (October 20, 2026): the overlay may have said "Resume lesson" for hours. The tap re-establishes everything
  // at tap time instead of trusting the source rebuilt back then: 1. a fresh session token, 2. a fresh link into page
  // state (through the page's hook, so both players and the PDF links see it), 3. the source reloaded from it at the
  // spot (a new FairPlay key session whose license carries the fresh token), 4. play. A play-then-pause inside the tap
  // captures the activation without playing the old source; the overlay stays up ('resuming') until the reload plays.
  const continueFromLink = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const p = vjsRef.current;
    if (!p) return;
    resumingRef.current = true;
    const first = p.play(); if (first && first.catch) first.catch(() => {});
    p.pause();   // play-then-pause inside the tap: the activation is captured, nothing plays until the reload
    setLinkOverlay('resuming');
    const cur = typeof p.currentTime === 'function' ? p.currentTime() : 0;
    const pos = cur > 0 ? cur : (latestRef.current.pos || resumeFromRef.current || 0);
    const wasLoaded = loadedUrlRef.current;   // put back if the reload fails, so the next fresher link rebuilds
    (async () => {
      try {
        // offline: nothing to refresh
        if (navigator.onLine !== false) await api.refreshToken();
        if (typeof refreshLink === 'function') {
          const before = videoUrlRef.current;
          const r = await refreshLink();
          // the fresh link reaches this player through a render; wait for it (normally one tick, at most 1 s)
          if (r === 'fresh') for (let i = 0; i < 40 && videoUrlRef.current === before; i++) await new Promise((res) => setTimeout(res, 25));
        }
        if (vjsRef.current !== p || !sourceForRef.current) return;   // the lesson changed meanwhile
        const now = videoUrlRef.current;
        const curNow = typeof p.currentTime === 'function' ? p.currentTime() : 0;
        const at = curNow > 0 ? curNow : pos;   // the spot now (the tap's play() may have moved on)
        loadedUrlRef.current = now;   // step 2's own check sees this link as the loaded one
        resumeFromRef.current = at; resumedRef.current = false;   // the load effect's loadedmetadata seeks back to the spot
        setError(null);
        await new Promise((resolve, reject) => {
          let timer = null;
          const done = () => { if (timer) clearTimeout(timer); try { p.off('loadedmetadata', onReady); p.off('error', onFail); } catch (err) { /* disposed */ } };
          const onReady = () => { done(); resolve(); };
          const onFail = () => { done(); reject(new Error('load failed')); };
          timer = setTimeout(onFail, 25000);   // no metadata and no error in 25 s: the error screen, not a spinner forever
          p.on('loadedmetadata', onReady); p.on('error', onFail);
          try { p.src(sourceForRef.current(now)); } catch (err) { onFail(); }
        });
        if (vjsRef.current !== p) return;
        setLinkOverlay(null); setIsLoading(false);
        const again = p.play(); if (again && again.catch) again.catch(() => {});
        setIsPlaying(true);
      } catch (err) {
        if (vjsRef.current !== p) return;
        if (loadedUrlRef.current !== wasLoaded) loadedUrlRef.current = wasLoaded;   // the loaded link is still the old one: step 2 rebuilds on the next fresher link
        resumeFromRef.current = pos;
        setLinkOverlay('ready'); setIsLoading(false);   // back to Resume lesson, never the error screen (October 30, 2026)
        setIsPlaying(false);
      } finally {
        resumingRef.current = false;
      }
    })();
  };
  const replay = () => {
    setEnded(false);
    const p = vjsRef.current;
    if (p) { p.currentTime(0); setCurrentTime(0); }
    setIsPlaying(true);
  };
  useEffect(() => { const p = vjsRef.current; if (p) p.playbackRate(hold ? 2 : playbackSpeed); }, [playbackSpeed, hold, mediaReady]);   // also once the media is ready: a speed chosen before it existed (e.g. a saved one) must still apply

  // ── Subtitles (1.21) ──────────────────────────────────────────────────────
  // Tracks come from the lesson data: video.subtitles = [{ lang, label, url, kind }], kind
  // 'subtitles' or 'captions' (English (CC) keeps its own key, 'en-cc'). With none, the
  // Subtitles control doesn't appear. Tracks are added once per player instance, after
  // the media is ready (mediaReady), by the effect below; never inside the load effect above.
  const subtitleOptions = useMemo(() => (Array.isArray(video && video.subtitles) ? video.subtitles : [])
    .filter((s) => s && s.url && s.lang)
    .map((s) => ({ ...s, kind: s.kind === 'captions' ? 'captions' : 'subtitles', key: s.kind === 'captions' ? s.lang + '-cc' : s.lang })), [video]);
  // Saved subtitles (1.22): start from the profile's preferredSubtitles, else Off. A lesson without
  // that language shows Off (activeSubtitleKey) without changing the saved value.
  const [subtitleKey, setSubtitleKey] = useState(() => (profile && typeof profile.preferredSubtitles === 'string' ? profile.preferredSubtitles : 'off'));
  const activeSubtitleKey = subtitleOptions.some((s) => s.key === subtitleKey) ? subtitleKey : 'off';
  // Saving (1.22) switches itself on: it only saves a field the profile already has (the sign the
  // backend supports it), so no request goes to an endpoint that doesn't exist yet. The in-memory
  // profile is updated too, so the next lesson starts with the choice.
  const savePreference = (field, value) => {
    if (!api || !profile || !Object.prototype.hasOwnProperty.call(profile, field)) return;
    if (profile[field] === value) return;   // unchanged: nothing to save (also keeps React's double-run in development quiet)
    profile[field] = value;
    try {
      const req = api.fetch('/profile/preferences', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }) });
      if (req && req.catch) req.catch(() => { /* a failed save changes nothing on screen */ });
    } catch (err) { /* a failed save changes nothing on screen */ }
  };
  const prefsReadyRef = useRef(false);
  useEffect(() => { if (prefsReadyRef.current) savePreference('preferredPlaybackSpeed', playbackSpeed); }, [playbackSpeed]);
  useEffect(() => { if (prefsReadyRef.current) savePreference('preferredSubtitles', subtitleKey); }, [subtitleKey]);
  useEffect(() => { prefsReadyRef.current = true; }, []);
  const [tracksReady, setTracksReady] = useState(0);
  const subsAddedFor = useRef(null);
  const [showSubtitleSheet, setShowSubtitleSheet] = useState(false);
  useEffect(() => {
    const p = vjsRef.current;
    if (!mediaReady || !p || !subtitleOptions.length || subsAddedFor.current === p) return;
    subsAddedFor.current = p;
    for (const s of subtitleOptions) {
      try { p.addRemoteTextTrack({ kind: s.kind, src: s.url, srclang: s.lang, label: s.label }, false); }
      catch (err) { /* one bad entry must not block the others */ }
    }
    setTracksReady((n) => n + 1);
  }, [mediaReady, subtitleOptions]);
  useEffect(() => {
    const p = vjsRef.current;
    if (!p || !tracksReady) return;
    const opt = subtitleOptions.find((s) => s.key === subtitleKey);
    try {
      const list = p.textTracks();
      for (let i = 0; i < list.length; i++) {
        const t = list[i];
        if (t.kind !== 'subtitles' && t.kind !== 'captions') continue;
        const on = !!opt && t.language === opt.lang && t.kind === opt.kind && (!opt.label || t.label === opt.label);
        t.mode = on ? 'showing' : 'disabled';
      }
    } catch (err) { /* subtitles are optional: never disturb playback */ }
  }, [subtitleKey, tracksReady, subtitleOptions]);

  // Read duration/position from the PLAYER, not React state. `duration` only
  // populates on loadedmetadata and `currentTime` lags a timeupdate behind, so
  // seeking off React state silently no-ops before metadata lands — which is
  // exactly when someone taps skip on a fresh lesson.
  const liveDuration = () => {
    const p = vjsRef.current;
    const d = p ? p.duration() : 0;
    return Number.isFinite(d) && d > 0 ? d : (totalSeconds || 0);
  };
  const livePosition = () => {
    const p = vjsRef.current;
    const t = p ? p.currentTime() : 0;
    return Number.isFinite(t) ? t : (currentTime || 0);
  };

  const seekTo = (clientX) => {
    const p = vjsRef.current;
    const d = liveDuration();
    if (!progressRef.current || !p || d <= 0) return;
    const r = progressRef.current.getBoundingClientRect();
    const t = Math.max(0, Math.min(d, ((clientX - r.left) / r.width) * d));
    p.currentTime(t);
    setCurrentTime(t);
  };

  // Drag on the bar: move the thumb only (no seek), and keep the controls up.
  const scrubTo = (clientX) => {
    const d = liveDuration();
    if (!progressRef.current || d <= 0) return;
    const r = progressRef.current.getBoundingClientRect();
    const t = Math.max(0, Math.min(d, ((clientX - r.left) / r.width) * d));
    scrubRef.current = t;
    setScrubTime(t);
    bump();
  };

  // Release: one seek, to where the finger let go.
  const endScrub = () => {
    const t = scrubRef.current;
    scrubRef.current = null;
    setScrubTime(null);
    if (t === null) return;
    const p = vjsRef.current;
    if (!p) return;
    p.currentTime(t);
    setCurrentTime(t);
  };

  const skip = (s) => {
    const p = vjsRef.current;
    if (!p) return;
    const d = liveDuration();
    const target = livePosition() + s;
    const t = Math.max(0, d > 0 ? Math.min(d - 0.25, target) : target);
    p.currentTime(t);
    setCurrentTime(t);
    bump();
  };

  // ── Lock-screen and headphone controls (1.24) ───────────────────────────
  // The Media Session API. Registered when this player starts playing, and kept after a pause so the
  // lock-screen play button still works; cleared when this player unmounts, if it is still the one
  // registered. Handlers go through a ref, so they always call this render's functions.
  const mediaSessionRef = useRef({});
  mediaSessionRef.current = {
    play: () => setIsPlaying(true),
    pause: () => setIsPlaying(false),
    skip: (s) => skip(s),
    seekTo: (t) => { const p = vjsRef.current; if (p) { p.currentTime(t); setCurrentTime(t); } },
  };
  const mediaSessionOwner = useRef({});
  useEffect(() => {
    if (!isPlaying || typeof navigator === 'undefined' || !navigator.mediaSession) return;
    const ms = navigator.mediaSession;
    try {
      if (typeof window.MediaMetadata === 'function') ms.metadata = new window.MediaMetadata({ title: (video && video.title) || '', artist: (video && video.series && video.series.name) || '', album: 'RoastBrew' });
    } catch (err) { /* metadata is optional */ }
    const set = (a, h) => { try { ms.setActionHandler(a, h); } catch (err) { /* this action isn't supported here */ } };
    set('play', () => mediaSessionRef.current.play());
    set('pause', () => mediaSessionRef.current.pause());
    set('seekbackward', () => mediaSessionRef.current.skip(-10));
    set('seekforward', () => mediaSessionRef.current.skip(10));
    set('seekto', (d) => { if (d && typeof d.seekTime === 'number') mediaSessionRef.current.seekTo(d.seekTime); });
    window.__rbMediaSessionOwner = mediaSessionOwner.current;
  }, [isPlaying, video]);
  useEffect(() => () => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession || window.__rbMediaSessionOwner !== mediaSessionOwner.current) return;
    ['play', 'pause', 'seekbackward', 'seekforward', 'seekto'].forEach((a) => { try { navigator.mediaSession.setActionHandler(a, null); } catch (err) { /* not supported */ } });
    window.__rbMediaSessionOwner = null;
  }, []);

  // Double-tap to skip (touch). A second tap on the same side third within 300 ms skips
  // ±10 s, and further taps keep adding (the ripple shows the running total). A first tap
  // on a side waits 300 ms before showing or hiding the controls, so a second tap can cancel
  // that; a tap in the centre shows or hides them at once.
  const tapRef = useRef({ side: null, at: 0, total: 0, timer: null });
  const [ripple, setRipple] = useState(null);
  const rippleTimerRef = useRef(null);
  useEffect(() => () => { clearTimeout(tapRef.current.timer); clearTimeout(rippleTimerRef.current); }, []);
  const handleSurfaceTap = (e) => {
    const h = holdRef.current;
    clearTimeout(h.timer);
    if (h.active) { h.active = false; setHold(false); return; }   // releasing a hold is not a tap
    const el = e && e.currentTarget;
    const pt = e && e.changedTouches && e.changedTouches[0] ? e.changedTouches[0] : e;
    const tr = tapRef.current;
    if (!el || !pt || pt.clientX == null) { toggleControls(); return; }
    const r = el.getBoundingClientRect();
    const x = (pt.clientX - r.left) / r.width;
    const side = x < 1 / 3 ? 'back' : x > 2 / 3 ? 'fwd' : null;
    const now = Date.now();
    if (!side) { clearTimeout(tr.timer); tr.side = null; toggleControls(); return; }
    if (tr.side === side && now - tr.at < 300) {
      clearTimeout(tr.timer);
      tr.at = now;
      tr.total += SKIP_SECONDSPhone;
      skip(side === 'back' ? -SKIP_SECONDSPhone : SKIP_SECONDSPhone);
      try { if (navigator.vibrate) navigator.vibrate(8); } catch (err) { /* no haptics here */ }   // haptic tick (Android; iPhones ignore it)
      setRipple({ side, total: tr.total });
      clearTimeout(rippleTimerRef.current);
      rippleTimerRef.current = setTimeout(() => setRipple(null), 700);
      return;
    }
    clearTimeout(tr.timer);
    tr.side = side; tr.at = now; tr.total = 0;
    tr.timer = setTimeout(() => { tr.side = null; toggleControls(); }, 300);
  };

  // ── iOS FULLSCREEN ────────────────────────────────────────────────────────
  // Two things were wrong before, both confirmed against Video.js's own
  // tech/html5.js source:
  //
  // 1. CASING. Video.js tests `typeof el.webkitEnterFullScreen === 'function'`
  //    — capital S in "FullScreen". I was calling `webkitEnterFullscreen` and
  //    gating on `webkitSupportsFullscreen`. Safari has exposed both spellings
  //    historically, but the capital-S one is what Video.js relies on, so try
  //    both rather than betting on either.
  //
  // 2. THE PRIMING SEQUENCE. iOS refuses programmatic fullscreen on a video that
  //    has not been primed. Video.js handles it: when the video is paused and
  //    networkState <= HAVE_METADATA it calls play(), then on a setTimeout(0)
  //    pauses and only THEN enters fullscreen — split across a tick, because
  //    doing both synchronously can trap older iOS in a play/pause loop. My
  //    version just called enter and gave up, which is exactly the "nothing
  //    happens" on a fresh lesson.
  //
  // So stop hand-rolling it: player.requestFullscreen() routes through the
  // tech's enterFullScreen() and inherits all of the above. The manual path
  // below is only a fallback if the Video.js player is not available.
  const nativeEnterName = (el) => {
    if (!el) return null;
    if (typeof el.webkitEnterFullScreen === 'function') return 'webkitEnterFullScreen';
    if (typeof el.webkitEnterFullscreen === 'function') return 'webkitEnterFullscreen';
    return null;
  };

  const goFullscreen = useCallback(() => {
    const p = vjsRef.current;
    const el = mediaRef.current;

    // Already fullscreen -> exit.
    const inFs = !!(document.fullscreenElement || document.webkitFullscreenElement ||
                    (el && el.webkitDisplayingFullscreen));
    if (inFs) {
      if (p && p.isFullscreen && p.isFullscreen()) { try { p.exitFullscreen(); return; } catch (e) {} }
      if (el && typeof el.webkitExitFullScreen === 'function') { el.webkitExitFullScreen(); return; }
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      return;
    }

    // Preferred path: Video.js knows about iOS.
    if (p && typeof p.requestFullscreen === 'function') {
      try {
        const r = p.requestFullscreen();
        if (r && typeof r.catch === 'function') r.catch(() => {});
        return;
      } catch (e) { /* fall through */ }
    }

    if (!el) return;
    const method = nativeEnterName(el);
    if (!method) {
      const box = containerRef.current;              // iPad / desktop Safari
      if (box && typeof box.requestFullscreen === 'function') box.requestFullscreen().catch(() => {});
      else if (box && typeof box.webkitRequestFullscreen === 'function') box.webkitRequestFullscreen();
      return;
    }

    // Manual mirror of Video.js's priming sequence.
    if (el.paused && el.networkState <= 1 /* HAVE_METADATA */) {
      const pr = el.play();
      if (pr && pr.catch) pr.catch(() => {});
      setTimeout(() => {
        el.pause();
        try { el[method](); } catch (e) { /* iOS refused */ }
      }, 0);
    } else {
      try { el[method](); } catch (e) { /* iOS refused */ }
    }
  }, []);

  useEffect(() => {
    const h = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', h);
    document.addEventListener('webkitfullscreenchange', h);
    const v = mediaRef.current;
    const ib = () => setIsFullscreen(true); const ie = () => setIsFullscreen(false);
    if (v) { v.addEventListener('webkitbeginfullscreen', ib); v.addEventListener('webkitendfullscreen', ie); }
    return () => {
      document.removeEventListener('fullscreenchange', h);
      document.removeEventListener('webkitfullscreenchange', h);
      if (v) { v.removeEventListener('webkitbeginfullscreen', ib); v.removeEventListener('webkitendfullscreen', ie); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrlKey]);


  // TOP BAR PILLS — the original mobile design, restored.
  // These are flex:1 pills spread across the bar, so the tap target is WIDE even
  // though it is not tall. The original set minHeight 32px and 9px text, which is
  // what made them fiddly; the layout was never the problem. Raised to 44px /
  // 11px and otherwise left as it was.
  const topBarBtn = (children, onClick, active = false, key = 'pill', disabled = false) => (
    <button
      disabled={disabled}
      onClick={!disabled && onClick ? once(key, onClick) : undefined}
      onTouchEnd={!disabled && onClick ? once(key, onClick) : undefined}
      style={{
        opacity: disabled ? 0.4 : 1,
        // flex:1 with a cap. With four pills flex:1 filled the bar naturally; with
        // two left, uncapped flex:1 stretched each to half the player width, which
        // reads as a banner rather than a button.
        flex: 1, maxWidth: '132px', minHeight: '44px', padding: '6px',
        background: active ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
        color: 'rgba(255,255,255,0.9)', fontSize: '11px', fontWeight: '600',
        fontFamily: '"DM Sans",sans-serif', backdropFilter: 'blur(10px)',
        WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
      }}>{children}</button>
  );

  // ── NO PiP BUTTON — BY DESIGN ─────────────────────────────────────────────
  // There used to be one here, and it was more trouble than it was worth on iOS.
  // Video.js is no help (its html5 tech implements PiP as a bare
  // el.requestPictureInPicture(), which does not exist on iOS Safari), Safari's
  // own webkitSetPresentationMode requires BOTH a live user gesture and a
  // currently-PLAYING video, and automatic PiP on page-leave is impossible —
  // WebKit only grants PiP inside a transient user activation, so requests from
  // unload / visibilitychange / pagehide report success while the window never
  // renders.
  //
  // The workaround is simply better: entering fullscreen hands off to Apple's
  // NATIVE player, which already has a PiP control, and fullscreen -> background
  // is the one automatic PiP transition WebKit does perform. So the user gets PiP
  // for free via the fullscreen button, with none of the gating to maintain.


  return (
    <div ref={containerRef} className="video-player-container"
      style={{
        position: 'relative', aspectRatio: '16/9', width: '100%',
        borderRadius: isFullscreen ? 0 : '10px', overflow: 'hidden', background: '#000',
      }}>
      {autoMuted && (soundPromptBig ? (
        <button type="button" aria-label="Tap for sound" onClick={(e) => { e.stopPropagation(); }} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: 'clamp(14px, 3.5vw, 22px) clamp(22px, 5vw, 34px)', borderRadius: '18px', border: 'none', background: '#111111', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 'clamp(16px, 2.4vw, 22px)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', boxShadow: '0 10px 40px rgba(0,0,0,0.45)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: 'clamp(34px, 7vw, 48px)', height: 'clamp(34px, 7vw, 48px)' }}><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>
          Tap for sound
        </button>
      ) : (
        <button type="button" aria-label="Tap for sound" onClick={(e) => { e.stopPropagation(); }} style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 40, display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '999px', border: 'none', background: 'rgba(0,0,0,0.72)', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>
          Tap for sound
        </button>
      ))}
      <div className="video-player-inner" style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', borderRadius: 'inherit' }}>

        {/* z0 — media */}
        {videoUrl && <div ref={mediaHostRef} style={{ position: 'absolute', inset: 0 }} />}
        {!videoUrl && <div style={{ position: 'absolute', inset: 0, background: '#0a0a0a' }} />}

        {/* z1 — cinematic vignette (mobile only) */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.45) 100%)',
        }} />

        {/* z2 — paused dim */}
        <div style={{ position: 'absolute', inset: 0, background: '#000', opacity: isPlaying ? 0 : 0.3, zIndex: 2, pointerEvents: 'none', transition: 'opacity 0.4s ease' }} />

        {/* z3 — spinner. pointerEvents NONE: an overlay above the controls without
            this is what made them unclickable in the first place. */}
        {isLoading && !showError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3, pointerEvents: 'none' }}>
            <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.25)', borderTopColor: colors.accent, borderRadius: '50%', animation: 'rbspin 0.8s linear infinite' }} />
          </div>
        )}

        {/* z3 — error */}
        {showError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', background: 'rgba(0,0,0,0.7)', zIndex: 3, pointerEvents: 'none', padding: '16px', textAlign: 'center' }}>
            <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
            <p style={{ margin: 0, color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 12 }}>{error}</p>
          </div>
        )}
        {/* z30 — link refresh overlay (above the tap layer, so Continue can be tapped) */}
        <LinkOverlay state={linkOverlay} at={resumeFromRef.current} colors={colors} onContinue={continueFromLink} small />

        {/* z15 — TAP LAYER. Always interactive. iOS does not reliably bubble a
            click off a <video>, so the reveal tap needs a real element above it.
            Single logical handler via once(): touchend + synthesised click both
            firing is what made this toggle twice and appear dead. */}
        <div
          onTouchStart={(e) => {
            const t = e.touches && e.touches[0]; const h = holdRef.current;
            clearTimeout(h.timer); h.active = false;
            if (!t || e.touches.length > 1) return;
            h.x = t.clientX; h.y = t.clientY;
            h.timer = setTimeout(() => { h.active = true; setHold(true); try { if (navigator.vibrate) navigator.vibrate(8); } catch (err) { /* no haptics here */ } }, 450);
          }}
          onTouchMove={(e) => {
            const t = e.touches && e.touches[0]; const h = holdRef.current;
            if (t && !h.active && Math.hypot(t.clientX - h.x, t.clientY - h.y) > 10) clearTimeout(h.timer);
          }}
          onTouchCancel={() => { const h = holdRef.current; clearTimeout(h.timer); if (h.active) { h.active = false; setHold(false); } }}
          onTouchEnd={once('tap', (e) => handleSurfaceTap(e))}
          onClick={once('tap', (e) => handleSurfaceTap(e))}
          style={{ position: 'absolute', inset: 0, zIndex: 15, pointerEvents: 'auto', background: 'transparent', touchAction: 'manipulation', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
        />
        {ripple && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, [ripple.side === 'back' ? 'left' : 'right']: 0, width: '38%', zIndex: 16, pointerEvents: 'none',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', background: 'rgba(255,255,255,0.12)',
            borderRadius: ripple.side === 'back' ? '0 50% 50% 0 / 0 50% 50% 0' : '50% 0 0 50% / 50% 0 0 50%',
            color: '#fff', fontFamily: '"DM Sans",sans-serif', fontWeight: 700, fontSize: '14px', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
            <span>{ripple.side === 'back' ? '«' : '»'}</span>
            <span>{ripple.side === 'back' ? '−' : '+'}{ripple.total} s</span>
          </div>
        )}
        {hold && (
          <div style={{ position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', zIndex: 17, pointerEvents: 'none',
            padding: '6px 12px', borderRadius: '999px', background: 'rgba(0,0,0,0.62)', color: '#fff',
            fontFamily: '"DM Sans",sans-serif', fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap' }}>
            2× ▸▸
          </div>
        )}

        {/* ── z16 CONTROLS — the original mobile three-band layout ──────────── */}

        {/* TOP: pills, space-evenly. CC removed (no caption tracks in the payload).
            Speed and Mute remain; PiP is reached via fullscreen (see above). */}
        <div data-controls="true" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, padding: '8px 10px',
            background: 'linear-gradient(to bottom,rgba(0,0,0,0.8) 0%,rgba(0,0,0,0.4) 70%,transparent 100%)',
            display: 'flex', justifyContent: 'space-evenly', alignItems: 'center', gap: '8px',
            zIndex: 16, opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease',
            pointerEvents: showControls ? 'auto' : 'none',
          }}>
          {/* Subtitles pill (1.21): shown only when this lesson has subtitle tracks. */}
          {subtitleOptions.length > 0 && topBarBtn(
            <><svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M7 12h4M13 12h4M7 16h10" /></svg><span>Subtitles</span></>,
            () => { setShowSubtitleSheet(true); bump(); }, showSubtitleSheet || activeSubtitleKey !== 'off', 'subs')}
          {topBarBtn(<span>{playbackSpeed === 1 ? '1x' : `${playbackSpeed}x`}</span>,
            () => { setShowSpeedSheet(true); bump(); }, showSpeedSheet, 'speed')}
          {topBarBtn(
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {isMuted
                ? <><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M23 9l-6 6M17 9l6 6" /></>
                : <><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" /></>}
            </svg>, () => { setIsMuted(!isMuted); bump(); }, false, 'mute')}
        </div>

        {/* CENTRE: skip 10s / play / skip 10s — original SkipButton, top 45%. */}
        <div data-controls="true" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: '45%', left: '50%', transform: 'translate(-50%,-50%)',
            display: 'flex', alignItems: 'center', gap: '14px', zIndex: 16,
            opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease',
            pointerEvents: showControls ? 'auto' : 'none',
          }}>
          <SkipButtonPhone direction="back" onClick={once('skipback', () => skip(-SKIP_SECONDSPhone))} currentTier={currentTier} isMobile seconds={SKIP_SECONDSPhone} />
          <button aria-label={isPlaying ? 'Pause' : 'Play'}
            onClick={once('play', () => { setIsPlaying(!isPlaying); bump(); })}
            onTouchEnd={once('play', () => { setIsPlaying(!isPlaying); bump(); })}
            style={{
              width: '52px', height: '52px', borderRadius: '50%', border: 'none', flexShrink: 0,
              background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)', cursor: 'pointer',
              animation: !isPlaying ? 'rbpulse 2.2s ease-in-out infinite' : 'none',
              WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
            }}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="#000" style={{ marginLeft: !isPlaying ? '3px' : '0' }}>
              {isPlaying ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}
            </svg>
          </button>
          <SkipButtonPhone direction="forward" onClick={once('skipfwd', () => skip(SKIP_SECONDSPhone))} currentTier={currentTier} isMobile seconds={SKIP_SECONDSPhone} />
        </div>

        {/* BOTTOM: progress + time + fullscreen (original composition). */}
        <div data-controls="true" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px 12px',
            background: 'linear-gradient(to top,rgba(0,0,0,0.9) 0%,rgba(0,0,0,0.5) 50%,transparent 100%)',
            zIndex: 16, opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease',
            pointerEvents: showControls ? 'auto' : 'none',
          }}>
          {/* The original was a bare 4px div — a 4px tall tap target. Same visual
              bar, wrapped in a 24px hit area so a thumb can actually land on it. */}
          <div ref={progressRef}
            onClick={(e) => { e.stopPropagation(); seekTo(e.clientX); }}
            onTouchStart={(e) => { e.stopPropagation(); if (e.touches[0]) scrubTo(e.touches[0].clientX); }}
            onTouchMove={(e) => { e.stopPropagation(); if (e.touches[0]) scrubTo(e.touches[0].clientX); }}
            onTouchEnd={(e) => { e.stopPropagation(); if (e.cancelable) e.preventDefault(); endScrub(); bump(); }}
            onTouchCancel={() => { scrubRef.current = null; setScrubTime(null); }}
            style={{ position: 'relative', height: '24px', display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '2px', touchAction: 'none' }}>
            <div style={{ position: 'relative', height: '4px', width: '100%', background: 'rgba(255,255,255,0.2)', borderRadius: '3px' }}>
              <div style={{ position: 'absolute', height: '100%', width: `${bufPct}%`, background: 'rgba(255,255,255,0.3)', borderRadius: '3px', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', height: '100%', width: `${pct}%`, borderRadius: '3px', pointerEvents: 'none', ...pStyles.played }} />
              <div style={{ position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%,-50%)', width: '12px', height: '12px', borderRadius: '50%', pointerEvents: 'none', ...pStyles.thumb }} />
            </div>
            {scrubTime !== null && (
              <div style={{ position: 'absolute', left: `${pct}%`, bottom: '26px', transform: 'translateX(-50%)', padding: '3px 8px', borderRadius: '6px', background: 'rgba(0,0,0,0.8)', color: '#fff', fontSize: '12px', fontWeight: 700, fontFamily: '"DM Sans",sans-serif', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                {fmtTimePhone(scrubTime)}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)', fontFamily: '"DM Sans",sans-serif', fontVariantNumeric: 'tabular-nums' }}>
              {fmtTimePhone(scrubTime ?? currentTime)} / {fmtTimePhone(totalSeconds)}
            </span>
            <button aria-label="Fullscreen"
              onClick={once('fs', () => goFullscreen())} onTouchEnd={once('fs', () => goFullscreen())}
              style={{ width: '44px', height: '44px', padding: 0, background: 'none', border: 'none', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {isFullscreen
                  ? <path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3" />
                  : <path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3" />}
              </svg>
            </button>
          </div>
        </div>

        <style>{`
          @keyframes rbspin { to { transform: rotate(360deg); } }
          @keyframes rbpulse { 0%,100% { transform: scale(1); box-shadow: 0 8px 32px rgba(0,0,0,0.4); } 50% { transform: scale(1.06); box-shadow: 0 10px 40px rgba(0,0,0,0.5); } }
          @keyframes rbsheet { from { transform: translateY(100%); } to { transform: translateY(0); } }
          .video-js { background-color: transparent !important; }
          .video-js video, .video-js .vjs-tech { object-fit: contain; }
          .video-js .vjs-control-bar, .video-js .vjs-big-play-button, .video-js .vjs-loading-spinner,
          .video-js .vjs-error-display, .video-js .vjs-poster, .video-js .vjs-text-track-display { display: none !important; }
        `}</style>
      </div>

      {/* ── SPEED SHEET ───────────────────────────────────────────────────────
          Rises from the BOTTOM OF THE SCREEN. position:fixed, rendered as a
          SIBLING of the player box rather than inside it.
          WHY: the original menu was position:absolute with bottom:100% inside the
          TOP bar, so it drew above the top of the player — outside the container's
          overflow:hidden — and was clipped away. It looked like it opened and
          could never be tapped. A fixed-position sheet cannot be clipped by any
          ancestor, and full-width 54px rows are a far bigger target than a
          floating 120px menu over a ~190px-tall video. */}
      {showSpeedSheet && (
        <div
          onClick={once('sheetbg', () => setShowSpeedSheet(false))}
          onTouchEnd={once('sheetbg', () => setShowSpeedSheet(false))}
          style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: 'rgba(0,0,0,0.5)' }}>
          <div onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
            style={{
              background: colors.cardBg || '#141414',
              borderTopLeftRadius: '18px', borderTopRightRadius: '18px',
              padding: '10px 12px calc(20px + env(safe-area-inset-bottom))',
              borderTop: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)'}`,
              boxShadow: '0 -8px 40px rgba(0,0,0,0.45)',
              animation: 'rbsheet 0.22s cubic-bezier(0.4,0,0.2,1)',
            }}>
            <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: colors.muted, opacity: 0.5, margin: '4px auto 14px' }} />
            <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.muted, fontFamily: '"DM Sans",sans-serif', padding: '0 8px 8px' }}>
              Playback speed
            </div>
            {PLAYBACK_SPEEDSPhone.map((spd) => (
              <button key={spd}
                onClick={once('spd' + spd, () => { setPlaybackSpeed(spd); setShowSpeedSheet(false); })}
                onTouchEnd={once('spd' + spd, () => { setPlaybackSpeed(spd); setShowSpeedSheet(false); })}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', minHeight: '54px', padding: '0 16px',
                  background: playbackSpeed === spd ? `${colors.accent}22` : 'transparent',
                  border: 'none', borderRadius: '10px', cursor: 'pointer',
                  color: playbackSpeed === spd ? colors.accent : colors.text,
                  fontSize: '16px', fontWeight: playbackSpeed === spd ? 700 : 500,
                  fontFamily: '"DM Sans",sans-serif',
                  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
                }}>
                <span>{spd === 1 ? 'Normal' : `${spd}x`}</span>
                {playbackSpeed === spd && (
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* End of lesson (1.23): Up next / Unlock / Lesson complete, over the video. */}
      {ended && endCardEnabled && !nativeFs && <EndOfLessonCard colors={colors} upNext={upNext} upNextLocked={upNextLocked} info={endCard} onUpNext={onUpNext} onUnlock={onUnlock} onReplay={replay} />}
      {/* Subtitles sheet (1.21): built like the speed sheet. */}
      {showSubtitleSheet && (
        <div
          onClick={once('subbg', () => setShowSubtitleSheet(false))}
          onTouchEnd={once('subbg', () => setShowSubtitleSheet(false))}
          style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: 'rgba(0,0,0,0.5)' }}>
          <div onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()} role="dialog" aria-label="Subtitles"
            style={{
              background: colors.cardBg || '#141414',
              borderTopLeftRadius: '18px', borderTopRightRadius: '18px',
              padding: '10px 12px calc(20px + env(safe-area-inset-bottom))',
              borderTop: `1px solid ${isLightTheme(currentTier) ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)'}`,
              boxShadow: '0 -8px 40px rgba(0,0,0,0.45)',
            }}>
            <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: colors.muted, opacity: 0.5, margin: '4px auto 14px' }} />
            <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.muted, fontFamily: '"DM Sans",sans-serif', padding: '0 8px 8px' }}>
              Subtitles
            </div>
            {[{ key: 'off', label: 'Off' }, ...subtitleOptions].map((s) => (
              <button key={s.key} role="menuitemradio" aria-checked={activeSubtitleKey === s.key}
                onClick={once('sub' + s.key, () => { setSubtitleKey(s.key); setShowSubtitleSheet(false); })}
                onTouchEnd={once('sub' + s.key, () => { setSubtitleKey(s.key); setShowSubtitleSheet(false); })}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', minHeight: '54px', padding: '0 16px',
                  background: activeSubtitleKey === s.key ? `${colors.accent}22` : 'transparent',
                  border: 'none', borderRadius: '10px', cursor: 'pointer',
                  color: activeSubtitleKey === s.key ? colors.accent : colors.text,
                  fontSize: '16px', fontWeight: activeSubtitleKey === s.key ? 700 : 500,
                  fontFamily: '"DM Sans",sans-serif', textAlign: 'left',
                  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
                }}>
                <span>{s.label}</span>
                {activeSubtitleKey === s.key && (
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
                )}
              </button>
            ))}
            <div style={{ fontSize: '12px', color: colors.muted, fontFamily: '"DM Sans",sans-serif', padding: '10px 16px 0' }}>
              Only languages available for this lesson are listed.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════ The chooser ════════════════
export default function FairPlayPlayer({ device, ...props }) {
  if (device === 'tablet') return <MobilePlayerTablet {...props} />;
  if (device === 'phone') return <MobilePlayerPhone {...props} />;
  return <VideoPlayer {...props} />;
}
